import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import NovaSolicitacaoButton from '../components/NovaSolicitacaoButton';
import planoService from '../services/planoTrabalhoService';
import FileViewerModal from '../components/FileViewerModal';
import AuthenticatedFileButton from '../components/AuthenticatedFileButton';
import type { PlanoTrabalhoAnexo, PlanoTrabalhoHistorico, PlanoWorkflowPayload, PlanoTrabalhoParecerAnexo, PlanoTrabalhoAnaliseJuridica } from '../services/planoTrabalhoService';
import metaService from '../services/metaService';
import type { Meta } from '../services/metaService';
import itemService from '../services/itemService';
import type { Item } from '../services/itemService';
import emendaService, { isEmendaElegivelParaNovoPlano } from '../services/emendaService';
import { useBanks } from '../context/BanksContext';
import { getMinhasInstituicoesDetalhadas } from '../services/userService';
import type { InstituicaoDetalhada } from '../types/user.types';
import { Permission } from '../types/user.types';
import { formatCurrency, parseCurrency, roundMoney, toUpperText } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import PrestacaoPcConfigPanel from '../components/PrestacaoPcConfigPanel';
import dirigenteService from '../services/dirigenteService';
import type { Dirigente } from '../types/dirigente.types';
import { CARGO_OPTIONS } from '../types/dirigente.types';
import { BANKS } from '../data/bancos';
import orcamentoMetaService from '../services/orcamentoMetaService';
import type { OrcamentoMeta } from '../services/orcamentoMetaService';
import indicadorPlanoService from '../services/indicadorPlanoService';
import type { IndicadorPlano } from '../services/indicadorPlanoService';
import parcelaDesembolsoService from '../services/parcelaDesembolsoService';
import type { ParcelaDesembolso } from '../services/parcelaDesembolsoService';
import cronogramaExecucaoMesService from '../services/cronogramaExecucaoMesService';
import type { CronogramaExecucaoMes } from '../services/cronogramaExecucaoMesService';
import { BASENAME } from '../services/api';
import userAdminService from '../services/userAdminService';
import type { ProcuradorJuridicoDTO } from '../services/userAdminService';
import comissaoService from '../services/comissaoService';
import type { Comissao, ComissaoParecer, ComissaoParecerResumo } from '../services/comissaoService';
import diligenciaDocumentoService from '../services/diligenciaDocumentoService';
import type { DiligenciaDocumentoDTO } from '../services/diligenciaDocumentoService';
import { UI_THEME } from '../ui/theme';
import { createXlsxBlob } from '../utils/xlsx';
import { openAuthenticatedTab } from '../utils/openAuthenticatedTab';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, DocumentTextIcon, ExclamationCircleIcon, PaperClipIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { notifyError } from '../utils/notify';

const currencyFmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

// -- Types ------------------------------------------------------------------
type MetaForm = { titulo: string; descricao: string };
const emptyMeta = (): MetaForm => ({ titulo: '', descricao: '' });
type ItemForm = { descricao: string; valor: string; tipo?: string; periodoInicio?: string; periodoFim?: string; valorUnitario?: string };
const emptyItem = (): ItemForm => ({ descricao: '', valor: '', tipo: '', periodoInicio: '', periodoFim: '', valorUnitario: '' });
type Tab = 'metas' | 'cronograma' | 'aplicacao' | 'desembolso' | 'indicadores';
type PlanoSection = 'geral' | 'estrutura';
const resolveInitialSection = (value: string | null): PlanoSection => {
  if (value === 'estrutura') return value;
  return 'geral';
};

interface PlanoForm {
  titulo: string;
  descricao: string;
  emendaId: string;
  valor: string;
  status: string;
  banco?: string;
  agencia?: string;
  contaBancaria?: string; // stored as formatted string groups (for display)
  periodoInicio?: string;
  periodoFim?: string;
  modalidade?: string;
  dirigenteId?: string;
  objetoParceria?: string;
  descricaoRealidade?: string;
  formaExecucao?: string;
}

const emptyPlanoForm = (): PlanoForm => ({
  titulo: '',
  descricao: '',
  emendaId: '',
  valor: 'R$ 0,00',
  status: 'RASCUNHO',
  banco: '',
  agencia: '',
  contaBancaria: '',
  periodoInicio: '',
  periodoFim: '',
  modalidade: 'TERMO_FOMENTO',
  dirigenteId: '',
  objetoParceria: '',
  descricaoRealidade: '',
  formaExecucao: '',
});

const getDirigenteCargoPriority = (cargo?: string) => {
  const normalized = String(cargo || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (normalized === 'presidente') return 0;
  if (normalized === 'vice-presidente') return 1;
  if (normalized === 'tesoureiro') return 2;
  if (normalized === 'secretario') return 3;
  if (normalized === 'conselheiro fiscal') return 4;
  return 5;
};

const prioritizeDirigentes = <T extends Pick<Dirigente, 'cargo' | 'nomeCompleto'>>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const cargoDiff = getDirigenteCargoPriority(a.cargo) - getDirigenteCargoPriority(b.cargo);
    if (cargoDiff !== 0) return cargoDiff;
    return String(a.nomeCompleto || '').localeCompare(String(b.nomeCompleto || ''), 'pt-BR');
  });

const getPreferredDirigente = <T extends Pick<Dirigente, 'cargo' | 'nomeCompleto' | 'id'>>(items: T[], currentId?: string) => {
  const ordered = prioritizeDirigentes(items);
  const presidente = ordered.find((dirigente) => getDirigenteCargoPriority(dirigente.cargo) === 0);
  if (presidente) return presidente;
  if (currentId) {
    const current = ordered.find((dirigente) => dirigente.id === currentId);
    if (current) return current;
  }
  return ordered[0];
};

const toMoneyNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/[R$.,]/.test(trimmed)) {
      return parseCurrency(trimmed);
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : 0;
  }
  return 0;
};

const STATUS_LABEL: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  AGUARDANDO_ASSINATURA: 'Aguardando assinatura',
  EM_ANALISE: 'Em Análise',
  APROVADO: 'Aprovado',
  DEVOLVIDO_CORRECAO: 'Devolvido',
  REPROVADO: 'Reprovado',
  CONCLUIDO: 'Convênios concluído',
  PENDENTE_RETIFICACAO: 'Pendente de Retificação',
};
const STATUS_COLOR: Record<string, string> = {
  CONCLUIDO: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  APROVADO: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  REPROVADO: 'bg-red-100 text-red-700 border-red-200',
  EM_ANALISE: 'bg-blue-100 text-blue-700 border-blue-200',
  DEVOLVIDO_CORRECAO: 'bg-amber-100 text-amber-700 border-amber-200',
  RASCUNHO: 'bg-gray-100 text-gray-600 border-gray-200',
  AGUARDANDO_ASSINATURA: 'bg-blue-100 text-blue-700 border-blue-200',
  PENDENTE_RETIFICACAO: 'bg-orange-100 text-orange-700 border-orange-200',
};

const ETAPA_LABEL: Record<string, string> = {
  ORCAMENTO: 'Orçamento',
  CONVENIOS_ANALISE: 'Convênios - análise inicial',
  GESTOR_PARCERIA: 'Gestor da parceria',
  COMISSAO_SELECAO: 'Comissão de seleção',
  CONVENIOS_MINUTA: 'Convênios - minuta do termo de fomento',
  PROCURADORIA_JURIDICA: 'Procuradoria Geral Municipal',
  GESTOR_JURIDICO_REVISAO: 'Revisão do Gestor Jurídico',
  CONVENIOS_FORMALIZACAO: 'Convênios - formalização do termo de fomento',
  CONCLUIDO: 'Concluído',
};

const ETAPA_ANEXO_OBRIGATORIO: Record<string, { tipo: string; label: string } | undefined> = {
  CONVENIOS_ANALISE: { tipo: 'PLANO_TRABALHO_ASSINADO_PREFEITO', label: 'Plano de trabalho assinado pela instituição e Prefeito' },
  COMISSAO_SELECAO: { tipo: 'ATA_COMISSAO', label: 'Ata da comissão' },
  CONVENIOS_MINUTA: { tipo: 'MINUTA_TERMO_FOMENTO_ASSINADA', label: 'Minuta do termo de fomento' },
  CONVENIOS_FORMALIZACAO: { tipo: 'TERMO_FOMENTO', label: 'Termo de fomento assinado' },
};

const ETAPA_TIPO_PADRAO: Record<string, string> = {
  PROCURADORIA_JURIDICA: 'PARECER_JURIDICO',
  GESTOR_JURIDICO_REVISAO: 'PARECER_JURIDICO',
};

const ETAPA_ANEXO_SECUNDARIO: Record<string, { tipo: string; label: string } | undefined> = {
  CONVENIOS_MINUTA: { tipo: 'PARECER_ANALISE_CONVENIOS', label: 'Parecer de Análise - Departamento de Convênios' },
};

const MODALIDADE_LABEL: Record<string, string> = {
  TERMO_COLABORACAO: 'Termo de Colaboração',
  TERMO_FOMENTO: 'Termo de Fomento',
};

const SUMMARY_CARD_STYLES = {
  blue: 'border-blue-100 bg-blue-50 text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200',
  violet: 'border-violet-100 bg-violet-50 text-violet-900 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-200',
  emerald: 'border-emerald-100 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200',
  slate: 'border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100',
} as const;

const GUIDANCE_CARD_STYLES = {
  blue: 'border-blue-200 bg-blue-50 text-blue-900',
  amber: 'border-amber-200 bg-amber-50 text-amber-900',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
} as const;

const TAB_OPTIONS: { key: Tab; label: string; }[] = [
  { key: 'metas', label: 'Metas' },
  { key: 'indicadores', label: 'Indicadores' },
  { key: 'cronograma', label: 'Cronograma de Execução' },
  { key: 'aplicacao', label: 'Plano de Aplicação' },
  { key: 'desembolso', label: 'Cronograma de Desembolso' },
];

const PLANO_SECTION_OPTIONS: Array<{ key: PlanoSection; step: string; label: string; description: string }> = [
  { key: 'geral', step: '01', label: 'Dados gerais do plano de trabalho', description: 'Preencha identificação, justificativa da parceria, conta bancária e confira o workflow.' },
  { key: 'estrutura', step: '02', label: 'Estrutura do plano', description: 'Cadastre metas, itens, indicadores e orçamentos como demonstrativos; as parcelas definem o desembolso do termo.' },
];

const DetailSection: React.FC<{
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ eyebrow, title, description, children }) => (
  <section className="space-y-4">
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">{eyebrow}</p>
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{title}</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">{description}</p>
        </div>
      </div>
    </div>
    {children}
  </section>
);

const GuidanceCard: React.FC<{
  title: string;
  tone?: keyof typeof GUIDANCE_CARD_STYLES;
  children: React.ReactNode;
}> = ({ title, tone = 'blue', children }) => (
  <div className={`rounded-2xl border px-4 py-3 shadow-sm ${GUIDANCE_CARD_STYLES[tone]}`}>
    <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-75">{title}</p>
    <div className="mt-2 text-sm leading-6">{children}</div>
  </div>
);

const formatTimelineDateTime = (value?: string) =>
  value ? new Date(value).toLocaleString('pt-BR') : 'Sem data';

const planoHistoricoLabel = (acao?: string) => {
  switch (acao) {
    case 'CREATE':
      return 'Criação';
    case 'UPDATE':
      return 'Atualização';
    case 'ENVIAR_PARA_APROVACAO':
      return 'Envio para aprovação';
    case 'APROVAR':
      return 'Aprovação';
    case 'REPROVAR':
      return 'Reprovação';
    case 'DEVOLVER_CORRECAO':
      return 'Devolução para correção';
    case 'DELETE':
      return 'Exclusão';
    case 'ADMIN_EDITAR_FASE':
      return 'Edição de fase (Admin)';
    case 'SOLICITAR_RETIFICACAO':
      return 'Retificação solicitada';
    case 'ENVIAR_RETIFICACAO':
      return 'Retificação enviada';
    case 'JUNTADA_DOCUMENTO':
      return 'Juntada de documento';
    default:
      return acao || 'Atualização';
  }
};

const planoHistoricoTone = (acao?: string) => {
  switch (acao) {
    case 'APROVAR':
      return {
        dot: 'bg-emerald-500',
        badge: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      };
    case 'REPROVAR':
      return {
        dot: 'bg-red-500',
        badge: 'border-red-200 bg-red-50 text-red-700',
      };
    case 'DEVOLVER_CORRECAO':
      return {
        dot: 'bg-amber-500',
        badge: 'border-amber-200 bg-amber-50 text-amber-700',
      };
    case 'ENVIAR_PARA_APROVACAO':
      return {
        dot: 'bg-blue-500',
        badge: 'border-blue-200 bg-blue-50 text-blue-700',
      };
    case 'SOLICITAR_RETIFICACAO':
      return {
        dot: 'bg-orange-500',
        badge: 'border-orange-200 bg-orange-50 text-orange-700',
      };
    case 'ENVIAR_RETIFICACAO':
      return {
        dot: 'bg-blue-400',
        badge: 'border-blue-200 bg-blue-50 text-blue-700',
      };
    case 'JUNTADA_DOCUMENTO':
      return {
        dot: 'bg-violet-500',
        badge: 'border-violet-200 bg-violet-50 text-violet-700',
      };
    case 'ADMIN_EDITAR_FASE':
      return {
        dot: 'bg-orange-500',
        badge: 'border-orange-200 bg-orange-50 text-orange-700',
      };
    default:
      return {
        dot: 'bg-slate-400',
        badge: 'border-slate-200 bg-slate-50 text-slate-700',
      };
  }
};

// -- Component --------------------------------------------------------------
const PlanoFullPage: React.FC = () => {
  const { id } = useParams();           // exists when viewing an existing plan
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const workflowFocusRef = useRef<HTMLDivElement | null>(null);
  const isSaasTheme = UI_THEME === 'saas';

  // query-params used both for create and for "back" navigation
  const instituicaoIdParam = searchParams.get('instituicaoId') || '';
  const emendaIdParam = searchParams.get('emendaId') || '';
  const focusTarget = searchParams.get('focus') || '';
  const returnToParam = searchParams.get('returnTo') || '';

  const isCreateMode = !id;
  const backUrl = returnToParam.startsWith('/dashboard/') ? returnToParam : '/dashboard/planos';

  // -- Shared loading --------------------------------------------------------
  const [pageLoading, setPageLoading] = useState(true);
  const [plan, setPlan] = useState<any>(null);

  // -- Plan form (create + inline-edit header) -------------------------------
  const [planoForm, setPlanoForm] = useState<PlanoForm>(emptyPlanoForm());
  const [isEditingHeader, setIsEditingHeader] = useState(isCreateMode);
  const [savingPlan, setSavingPlan] = useState(false);
  const [planErrors, setPlanErrors] = useState<Record<string, string>>({});
  const [instituicaoNome, setInstituicaoNome] = useState('');
  const [emendas, setEmendas] = useState<any[]>([]);
  const [bankFilter, setBankFilter] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [bankCode, setBankCode] = useState('');
  const [highlightedBankIndex, setHighlightedBankIndex] = useState(-1);
  const [instituicoes, setInstituicoes] = useState<InstituicaoDetalhada[]>([]);
  const [selectedInstituicaoId, setSelectedInstituicaoId] = useState(instituicaoIdParam);
  const contaInputRef = useRef<HTMLInputElement | null>(null);
  const periodoInicioInputRef = useRef<HTMLInputElement | null>(null);
  const headerNavigationTargetRef = useRef<'dados' | 'conta' | null>(null);

  const { user, hasPermission, isOperadorSecretaria } = useAuth();
  const isPlanoReadOnlyRole = user?.role === 'VEREADOR' || user?.role === 'CAMARA';

  useEffect(() => {
    if (isPlanoReadOnlyRole && isCreateMode) {
      navigate('/dashboard/gestao/planos-trabalho', { replace: true });
    }
  }, [isPlanoReadOnlyRole, isCreateMode, navigate]);
  const [dirigentes, setDirigentes] = useState<any[]>([]);

  // Keep the account easy to type; format only when leaving the field.
  const handleContaChange = (value: string) => {
    const digits = String(value || '').replace(/\D/g, '').slice(-20);
    setPlanoForm(f => ({ ...f, contaBancaria: digits }));
  };

  const handleContaFocus = () => {
    setPlanoForm(f => ({
      ...f,
      contaBancaria: String(f.contaBancaria || '').replace(/\D/g, '').slice(-20),
    }));
  };

  const handleContaBlur = () => {
    setPlanoForm(f => {
      const digits = String(f.contaBancaria || '').replace(/\D/g, '').slice(-20);
      return {
        ...f,
        contaBancaria: digits ? formatContaDisplay(digits) : '',
      };
    });
  };

  const normalizeBankSearch = (value?: string | null) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  // -- Tabs (only in view mode) ----------------------------------------------
  const [activeTab, setActiveTab] = useState<Tab>('metas');
  const [activeSection, setActiveSection] = useState<PlanoSection>(resolveInitialSection(searchParams.get('section')));

  // -- Metas -----------------------------------------------------------------
  const [metas, setMetas] = useState<Meta[]>([]);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [showNewMeta, setShowNewMeta] = useState(false);
  const [newMeta, setNewMeta] = useState<MetaForm>(emptyMeta());
  const [savingMeta, setSavingMeta] = useState(false);
  const [editingMetaId, setEditingMetaId] = useState<string | null>(null);
  const [editMetaForm, setEditMetaForm] = useState<MetaForm>(emptyMeta());

  // -- Items -----------------------------------------------------------------
  const [itemsByMeta, setItemsByMeta] = useState<Record<string, Item[]>>({});
  const [expandedMetas, setExpandedMetas] = useState<Set<string>>(new Set());
  const [itemError, setItemError] = useState<string | null>(null);
  const [showNewItemFor, setShowNewItemFor] = useState<string | null>(null);
  const [newItemByMeta, setNewItemByMeta] = useState<Record<string, ItemForm>>({});
  const [savingItemMeta, setSavingItemMeta] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editItemForm, setEditItemForm] = useState<ItemForm>(emptyItem());

  // -- Orçamentos por meta ---------------------------------------------------
  const [orcamentosByMeta, setOrcamentosByMeta] = useState<Record<string, OrcamentoMeta[]>>({});
  const [showNewOrcamentoFor, setShowNewOrcamentoFor] = useState<string | null>(null);
  const [orcamentoValorByMeta, setOrcamentoValorByMeta] = useState<Record<string, string>>({});
  const [orcamentoFileByMeta, setOrcamentoFileByMeta] = useState<Record<string, File | null>>({});
  const [orcamentoPreviewByMeta, setOrcamentoPreviewByMeta] = useState<Record<string, string | null>>({});
  const [orcamentoError, setOrcamentoError] = useState<string | null>(null);
  const [savingOrcamentoMeta, setSavingOrcamentoMeta] = useState<string | null>(null);

  // -- Indicadores do plano -------------------------------------------------
  const [indicadores, setIndicadores] = useState<IndicadorPlano[]>([]);
  const [indicadoresLoaded, setIndicadoresLoaded] = useState(false);
  const [showNewIndicador, setShowNewIndicador] = useState(false);
  const [newIndicador, setNewIndicador] = useState({ unidade: '', quantidade: '', descricao: '' });
  const [editingIndicadorId, setEditingIndicadorId] = useState<string | null>(null);
  const [editIndicadorForm, setEditIndicadorForm] = useState({ unidade: '', quantidade: '', descricao: '' });
  const [indicadorError, setIndicadorError] = useState<string | null>(null);
  const [savingIndicador, setSavingIndicador] = useState(false);

  // -- New state and logic for approval workflow ------------------------------
  const isOperador = user?.role === 'OPERADOR';
  // A decisao na etapa GESTOR_PARCERIA e gateada por "sou o gestorId deste plano" (ou ADMIN),
  // nao por role.
  const isGestorOrAdmin = user?.role === 'ADMIN' || (user?.id != null && String(user.id) === String(plan?.gestorId ?? ''));
  const isConvenios = user?.role === 'CONVENIOS' || user?.role === 'ADMIN';
  const [pcConfigurado, setPcConfigurado] = useState(false);
  const canAssignProcuradorJuridico = hasPermission(Permission.ASSIGN_PROCURADOR);
  // Gestor da parceria via CPF
  const [gestores, setGestores] = useState<Array<{ id: number; nomeCompleto: string; documento?: string; email?: string; cargoFuncao?: string }>>([]);
  const [gestoresElegiveis, setGestoresElegiveis] = useState<Array<{ id: number; nomeCompleto: string; cargoFuncao?: string; secretaria?: string; documento?: string }>>([]);
  const [gestorCpf, setGestorCpf] = useState('');
  const [savingGestor, setSavingGestor] = useState(false);
  const [procuradoresJuridicos, setProcuradoresJuridicos] = useState<ProcuradorJuridicoDTO[]>([]);
  const [procuradorJuridicoId, setProcuradorJuridicoId] = useState('');
  const [savingProcuradorJuridico, setSavingProcuradorJuridico] = useState(false);
  const [comissoes, setComissoes] = useState<Comissao[]>([]);
  const [comissaoPickerId, setComissaoPickerId] = useState('');
  const [savingComissao, setSavingComissao] = useState(false);
  // Diligência por plano de trabalho
  const [diligenciaPlanoOpen, setDiligenciaPlanoOpen] = useState(false);
  const [diligenciaPlanoMensagem, setDiligenciaPlanoMensagem] = useState('');
  const [diligenciaPlanoLoading, setDiligenciaPlanoLoading] = useState(false);
  const [diligenciaPlanoEnviado, setDiligenciaPlanoEnviado] = useState(false);
  const [diligenciasPlano, setDiligenciasPlano] = useState<DiligenciaDocumentoDTO[]>([]);
  const [diligenciaPlanoErro, setDiligenciaPlanoErro] = useState<string | null>(null);
  // Banks from global context
  const { banks: ctxBanks, loading: banksLoading, findByCode, refresh: refreshBanks } = useBanks();
  // use a local alias for convenience (always an array)
  const banks = ctxBanks || [];
  const availableBanks = banks.length > 0 ? banks : BANKS;
  const [sendingForApproval, setSendingForApproval] = useState(false);
  const [enviandoRetificacao, setEnviandoRetificacao] = useState(false);
  const [showRetificacaoConveniosModal, setShowRetificacaoConveniosModal] = useState(false);
  const [retificacaoConveniosMotivo, setRetificacaoConveniosMotivo] = useState('');
  const [retificacaoConveniosEtapa, setRetificacaoConveniosEtapa] = useState('');
  const [retificacaoConveniosRetorno, setRetificacaoConveniosRetorno] = useState('');
  const [solicitandoRetificacaoConvenios, setSolicitandoRetificacaoConvenios] = useState(false);
  const [conveniosIniciarRetOpen, setConveniosIniciarRetOpen] = useState(false);
  const [conveniosIniciarRetMotivo, setConveniosIniciarRetMotivo] = useState('');
  const [conveniosIniciarRetLoading, setConveniosIniciarRetLoading] = useState(false);
  const [showJuntadaModal, setShowJuntadaModal] = useState(false);
  const [emendaPanelOpen, setEmendaPanelOpen] = useState(false);
  const [juntadaFaseLocked, setJuntadaFaseLocked] = useState(false);
  const [juntadaTipo, setJuntadaTipo] = useState('');
  const [juntadaEtapa, setJuntadaEtapa] = useState('');
  const [juntadaFile, setJuntadaFile] = useState<File | null>(null);
  const [juntadaJustificativa, setJuntadaJustificativa] = useState('');
  const [juntadaAnexoExistente, setJuntadaAnexoExistente] = useState<typeof anexos[0] | null>(null);
  const [enviandoJuntada, setEnviandoJuntada] = useState(false);
  const [toast, setToast] = useState<{message: string; type?: 'success'|'error'|'info'} | null>(null);
  const [confirmSending, setConfirmSending] = useState(false);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowAction, setWorkflowAction] = useState<'aprovar' | 'reprovar' | 'devolver' | 'confirmarReprovacao' | null>(null);
  const [workflowMotivo, setWorkflowMotivo] = useState('');
  const [workflowParecer, setWorkflowParecer] = useState('');
  const [showAdminFaseModal, setShowAdminFaseModal] = useState(false);
  const [adminFaseStatus, setAdminFaseStatus] = useState('');
  const [adminFaseEtapa, setAdminFaseEtapa] = useState('');
  const [adminFaseMotivo, setAdminFaseMotivo] = useState('');
  const [adminFaseLoading, setAdminFaseLoading] = useState(false);
  const [anexos, setAnexos] = useState<PlanoTrabalhoAnexo[]>([]);
  const [historicoWorkflow, setHistoricoWorkflow] = useState<PlanoTrabalhoHistorico[]>([]);
  const [anexosLoading, setAnexosLoading] = useState(false);
  const [expandedParecerTecnico, setExpandedParecerTecnico] = useState(false);
  const [timelineDrawerOpen, setTimelineDrawerOpen] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  // Gaveta lateral (slide-over) com os documentos da etapa atual.
  const [docsDrawerOpen, setDocsDrawerOpen] = useState(false);
  // Controla a auto-abertura da gaveta quando há documento obrigatório pendente
  // (uma vez por etapa, respeitando o fechamento manual do usuário).
  const [docsAutoOpened, setDocsAutoOpened] = useState(false);
  const [parecerTecnicoDraft, setParecerTecnicoDraft] = useState('');
  const [savingParecerTecnico, setSavingParecerTecnico] = useState(false);
  const [uploadingAnexo, setUploadingAnexo] = useState(false);
  const [selectedAnexoFile, setSelectedAnexoFile] = useState<File | null>(null);
  const [uploadingAnexoSecundario, setUploadingAnexoSecundario] = useState(false);
  const [selectedAnexoSecundarioFile, setSelectedAnexoSecundarioFile] = useState<File | null>(null);
  const [uploadingRetroAnexo, setUploadingRetroAnexo] = useState(false);
  const [selectedRetroAnexoFile, setSelectedRetroAnexoFile] = useState<File | null>(null);
  const [uploadingRetroParecerAnexo, setUploadingRetroParecerAnexo] = useState(false);
  const [selectedRetroParecerAnexoFile, setSelectedRetroParecerAnexoFile] = useState<File | null>(null);
  const [parecerAnexoInfo, setParecerAnexoInfo] = useState<PlanoTrabalhoParecerAnexo | null>(null);
  const [analiseJuridica, setAnaliseJuridica] = useState<PlanoTrabalhoAnaliseJuridica | null>(null);
  const [gestorParecerAnexo, setGestorParecerAnexo] = useState<PlanoTrabalhoParecerAnexo | null>(null);
  const [gestorParecerUploading, setGestorParecerUploading] = useState(false);
  const [gestorParecerFile, setGestorParecerFile] = useState<File | null>(null);
  const [abrindoGestorParecer, setAbrindoGestorParecer] = useState(false);
  const [abrindoParecerAnexo, setAbrindoParecerAnexo] = useState(false);
  const [parecerViewerUrl, setParecerViewerUrl] = useState<string | null>(null);
  const [anexoViewer, setAnexoViewer] = useState<PlanoTrabalhoAnexo | null>(null);
  const [anexoSecundarioDescricao, setAnexoSecundarioDescricao] = useState('');
  const [anexoDescricao, setAnexoDescricao] = useState('');
  const [comprovanteFile, setComprovanteFile] = useState<File | null>(null);
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  const [contrapartidaFile, setContrapartidaFile] = useState<File | null>(null);
  const [uploadingContrapartida, setUploadingContrapartida] = useState(false);
  const [planoAssinadoFile, setPlanoAssinadoFile] = useState<File | null>(null);
  const [uploadingPlanoAssinado, setUploadingPlanoAssinado] = useState(false);
  const [iniciandoAnalise, setIniciandoAnalise] = useState(false);
  const [registrandoAprovacaoPrefeito, setRegistrandoAprovacaoPrefeito] = useState(false);

  const [aplicacao, setAplicacao] = useState<Array<any>>([]);
  const [aplicacaoLoading, setAplicacaoLoading] = useState(false);
  const [desembolso, setDesembolso] = useState<{ months: Array<any>; metaTotals: Array<any>; totalPlano: number } | null>(null);
  const [desembolsoLoading, setDesembolsoLoading] = useState(false);

  // -- Parcelas de desembolso --------------------------------------------
  const [parcelas, setParcelas] = useState<ParcelaDesembolso[]>([]);
  const [parcelasLoaded, setParcelasLoaded] = useState(false);
  const [newParcela, setNewParcela] = useState({ mes: '', valor: '' });
  const [editingParcelaId, setEditingParcelaId] = useState<string | null>(null);
  const [editParcelaForm, setEditParcelaForm] = useState({ mes: '', valor: '' });
  const [parcelaError, setParcelaError] = useState<string | null>(null);
  const [savingParcela, setSavingParcela] = useState(false);
  const [showNewParcela, setShowNewParcela] = useState(false);
  const [cronogramaExecucaoMeses, setCronogramaExecucaoMeses] = useState<CronogramaExecucaoMes[]>([]);
  const [cronogramaExecucaoMesesLoaded, setCronogramaExecucaoMesesLoaded] = useState(false);
  const [cronogramaExecucaoDrafts, setCronogramaExecucaoDrafts] = useState<Record<string, string>>({});
  const [savingCronogramaMes, setSavingCronogramaMes] = useState<string | null>(null);
  const [cronogramaExecucaoError, setCronogramaExecucaoError] = useState<string | null>(null);

  // -- Comissão de Seleção — pareceres ---------------------------------------
  const [pareceres, setPareceres] = useState<ComissaoParecer[]>([]);
  const [parecerResumo, setParecerResumo] = useState<ComissaoParecerResumo | null>(null);
  const [pareceresLoading, setPareceresLoading] = useState(false);
  const [parecerForm, setParecerForm] = useState({ resultado: '', justificativa: '' });
  const [submittingParecer, setSubmittingParecer] = useState(false);

  // -- Progress checklist -------------------------------------------------
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [checklistCollapsed, setChecklistCollapsed] = useState(false);

  const onlyDigits = (value: string) => value.replace(/\D/g, '');
  const formatCPF = (value: string) => {
    const digits = onlyDigits(value).slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };


  useEffect(() => { loadAnexos(); }, [id]);
  useEffect(() => { loadHistoricoWorkflow(); }, [id]);
  useEffect(() => {
    if (!id || !plan || plan.etapaAtual !== 'PROCURADORIA_JURIDICA') return;
    planoService.getAnaliseJuridica(id).then(setAnaliseJuridica).catch(() => setAnaliseJuridica(null));
  }, [id, plan?.etapaAtual]);
  // Mantém o rascunho do parecer sincronizado com o que está salvo no plano.
  useEffect(() => { setParecerTecnicoDraft(plan?.parecerTecnico || ''); }, [plan?.parecerTecnico]);
  // Fecha a gaveta de documentos ao pressionar Escape.
  useEffect(() => {
    if (!docsDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDocsDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [docsDrawerOpen]);
  useEffect(() => {
    if (id && (plan?.etapaAtual === 'COMISSAO_SELECAO' || plan?.etapaAtual === 'ORCAMENTO')) {
      loadPareceres();
    }
  }, [id, plan?.etapaAtual]);

  // Load responsible users for assignment pickers.
  useEffect(() => {
    if (!isConvenios && !canAssignProcuradorJuridico) return;

    const loadResponsaveis = async () => {
      try {
        // Usa a rota /plano-trabalho/gestores-elegiveis (liberada para Convenios/Admin) em vez de
        // /admin/users, que exige perfil ADMIN e barrava o setor de Convenios com 403.
        const [users, procuradores] = await Promise.all([
          isConvenios ? planoService.listarGestoresElegiveis() : Promise.resolve([] as Awaited<ReturnType<typeof planoService.listarGestoresElegiveis>>),
          canAssignProcuradorJuridico ? userAdminService.listProcuradoresJuridicos() : Promise.resolve([] as ProcuradorJuridicoDTO[]),
        ]);

        if (isConvenios) {
          setGestores(users);
        }
        if (canAssignProcuradorJuridico) {
          setProcuradoresJuridicos(procuradores);
        }
      } catch {
        setGestores([]);
        setProcuradoresJuridicos([]);
      }
    };

    loadResponsaveis();
  }, [isConvenios, canAssignProcuradorJuridico]);

  useEffect(() => {
    setProcuradorJuridicoId(plan?.procuradorJuridicoId ? String(plan.procuradorJuridicoId) : '');
  }, [plan?.procuradorJuridicoId]);

  useEffect(() => {
    if (!isConvenios) return;
    comissaoService.listar()
      .then(data => setComissoes(data || []))
      .catch(() => setComissoes([]));
  }, [isConvenios]);

  useEffect(() => {
    if (!id || !(isConvenios || isOperador)) return;
    diligenciaDocumentoService.listarPorPlano(id)
      .then(res => setDiligenciasPlano(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDiligenciasPlano([]));
  }, [isConvenios, isOperador, id]);

  // Emenda Direta: carrega gestores somente no passo em que a vinculação é exibida.
  // A tela da Indireta continua usando o fluxo e as fontes de dados existentes.
  useEffect(() => {
    const emendaVinculada = emendas.find((em: any) => em.id === plan?.emendaId);
    const emendaDireta = String(emendaVinculada?.tipoTransferencia || '').trim().toLowerCase() === 'direta';
    if (!id
      || plan?.etapaAtual !== 'GESTOR_PARCERIA'
      || !emendaDireta
      || !(isConvenios || isOperadorSecretaria)) return;
    planoService.listarGestoresElegiveis()
      .then(setGestoresElegiveis)
      .catch(() => setGestoresElegiveis([]));
  }, [id, plan?.etapaAtual, plan?.emendaId, emendas, isConvenios, isOperadorSecretaria]);

  const enviarDiligenciaPlano = async () => {
    if (!diligenciaPlanoMensagem.trim() || !id || !plan) return;
    setDiligenciaPlanoLoading(true);
    setDiligenciaPlanoErro(null);
    try {
      const instId = (plan as any).instituicaoId;
      if (!instId) return;
      await diligenciaDocumentoService.enviar(instId, diligenciaPlanoMensagem, [], id);
      const res = await diligenciaDocumentoService.listarPorPlano(id);
      setDiligenciasPlano(Array.isArray(res.data) ? res.data : []);
      setDiligenciaPlanoEnviado(true);
    } catch (err: any) {
      setDiligenciaPlanoErro(err?.response?.data?.error || 'Erro ao enviar diligência. Tente novamente.');
    } finally {
      setDiligenciaPlanoLoading(false);
    }
  };

  const fecharDiligenciaPlano = () => {
    setDiligenciaPlanoOpen(false);
    setDiligenciaPlanoMensagem('');
    setDiligenciaPlanoEnviado(false);
    setDiligenciaPlanoErro(null);
  };

  async function loadPareceres() {
    if (!id) return;
    setPareceresLoading(true);
    try {
      const [data, resumo] = await Promise.all([
        comissaoService.listarPareceresDoPlano(id),
        comissaoService.resumoPareceresDoPlano(id),
      ]);
      setPareceres(data);
      setParecerResumo(resumo);
    } catch {
      setParecerResumo(null);
    } finally {
      setPareceresLoading(false);
    }
  }

  const isMembroDaComissao = Boolean(parecerResumo?.membroLogado);
  const isAdminUser = user?.role === 'ADMIN';
  const isPresidenteDaComissao = Boolean(parecerResumo?.membroLogado?.isPresidente);
  const gestorLocalizado = (() => {
    const cpf = onlyDigits(gestorCpf);
    if (cpf.length !== 11) return undefined;
    return gestores.find(u => onlyDigits(u.documento || '') === cpf);
  })();

  // Match do CPF digitado contra o pool de gestores elegíveis (perfil GESTOR ativos) — usado na
  // etapa do Gestor da Parceria (Emenda Direta), pois o operador/secretaria não tem a lista admin.
  const gestorLocalizadoDireta = (() => {
    const cpf = onlyDigits(gestorCpf);
    if (cpf.length !== 11) return undefined;
    return gestoresElegiveis.find(g => onlyDigits(g.documento || '') === cpf);
  })();

  const handleBaixarModeloAtaComissao = () => {
    const protocolo = plan?.numeroProtocolo || id || 'plano';
    const texto = [
      'MODELO DE ATA - COMISSAO DE SELECAO',
      '',
      `Plano de trabalho: ${plan?.titulo || 'Nao informado'}`,
      `Protocolo: ${protocolo}`,
      `Comissao: ${parecerResumo?.numeroDaComissao ?? plan?.comissaoNumero ?? 'Nao informada'}`,
      `Portaria: ${parecerResumo?.numeroPortaria ?? plan?.comissaoNumeroPortaria ?? 'Nao informada'}/${parecerResumo?.anoPortaria ?? plan?.comissaoAnoPortaria ?? 'Nao informado'}`,
      `Instituicao: ${displayInstituicaoNome}`,
      '',
      'Membro da comissao:',
      `Nome: ${parecerResumo?.membroLogado?.nomeCompleto || user?.name || ''}`,
      `Cargo/funcao: ${parecerResumo?.membroLogado?.cargoFuncao || ''}`,
      '',
      'Manifestacao do membro:',
      '[  ] APROVO',
      '[  ] NAO APROVO',
      '',
      'Justificativa:',
      '',
      '',
      'Data:',
      '',
      'Assinatura do membro:',
    ].join('\r\n');

    const blob = new Blob([texto], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `modelo-ata-comissao-${protocolo}.txt`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  async function handleEmitirParecer(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !parecerForm.resultado) return;
    setSubmittingParecer(true);
    try {
      await comissaoService.emitirParecer(id, {
        resultado: parecerForm.resultado as 'APROVADO' | 'REPROVADO',
        justificativa: parecerForm.justificativa,
      });
      setParecerForm({ resultado: '', justificativa: '' });
      setToast({ message: 'Parecer registrado com sucesso', type: 'success' });
      const [, planRes] = await Promise.all([loadPareceres(), planoService.getFull(id)]);
      if (planRes?.data) setPlan(planRes.data);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { erro?: string; error?: string } } })?.response?.data?.erro
        || (err as { response?: { data?: { erro?: string; error?: string } } })?.response?.data?.error
        || 'Erro ao registrar parecer';
      setToast({ message: msg, type: 'error' });
    } finally {
      setSubmittingParecer(false);
    }
  }

  const validateBeforeSend = async (): Promise<string | null> => {
    if (!id) {
      return 'Salve o plano antes de solicitar aprovação.';
    }

    if (isEditingHeader) {
      return 'Salve ou cancele a edição atual antes de solicitar aprovação.';
    }

    if (!['RASCUNHO', 'DEVOLVIDO_CORRECAO', 'REPROVADO', 'AGUARDANDO_ASSINATURA'].includes(statusAtual || '')) {
      return `O plano só pode ser enviado para aprovação quando estiver em rascunho, devolvido para correção ou reprovado. Status atual: ${STATUS_LABEL[statusAtual] || statusAtual}.`;
    }

    if (statusAtual === 'AGUARDANDO_ASSINATURA' && !anexos.some(a => a.tipoDocumento === 'PLANO_ASSINADO_PRESIDENTE')) {
      return 'Anexe o plano de trabalho assinado antes de solicitar aprovação.';
    }

    const pendencias: string[] = [];
    const tituloAtual = (plan?.titulo || planoForm.titulo || '').trim();
    const instituicaoAtual = (plan?.instituicaoId || selectedInstituicaoId || instituicaoIdParam || '').trim();
    const periodoInicioAtual = plan?.periodoInicio || planoForm.periodoInicio;
    const periodoFimAtual = plan?.periodoFim || planoForm.periodoFim;
    const modalidadeAtual = (plan?.modalidade || planoForm.modalidade || '').trim();
    const bancoAtual = (plan?.banco || bankCode || planoForm.banco || '').trim();
    const agenciaAtual = (plan?.agencia || planoForm.agencia || '').trim();
    const contaAtual = String(plan?.contaBancaria || planoForm.contaBancaria || '').replace(/\D/g, '').trim();
    const objetoAtual = (plan?.objetoParceria || planoForm.objetoParceria || '').trim();
    const descricaoRealidadeAtual = (plan?.descricaoRealidade || planoForm.descricaoRealidade || '').trim();
    const formaExecucaoAtual = (plan?.formaExecucao || planoForm.formaExecucao || '').trim();

    if (!tituloAtual) pendencias.push('título do plano');
    if (!instituicaoAtual) pendencias.push('instituição');
    if (!periodoInicioAtual) pendencias.push('período de início');
    if (!periodoFimAtual) pendencias.push('período de fim');
    if (periodoInicioAtual && periodoFimAtual && String(periodoInicioAtual) > String(periodoFimAtual)) {
      pendencias.push('período de execução válido');
    }
    if (!modalidadeAtual) pendencias.push('modalidade');
    // Emenda Direta: dados bancarios opcionais (ver isEmendaDiretaAtual).
    if (!isEmendaDiretaAtual()) {
      if (!bancoAtual) pendencias.push('banco');
      if (!agenciaAtual) pendencias.push('agência');
      if (!contaAtual) pendencias.push('conta bancária');
    }
    if (!objetoAtual) pendencias.push('objeto da parceria');
    if (!descricaoRealidadeAtual) pendencias.push('descrição da realidade');
    if (!formaExecucaoAtual) pendencias.push('forma de execução');

    if (pendencias.length > 0) {
      return `Para enviar para aprovação, falta preencher: ${pendencias.join(', ')}.`;
    }

    // 1) At least 1 meta
    if (!metas || metas.length === 0) return 'Para enviar para aprovação, o plano deve conter ao menos 1 meta.';

    // 2) Each meta must have >= 1 item — fetch when not loaded
    for (const m of metas) {
      const items = itemsByMeta[m.id!] ?? await itemService.listByMeta(m.id!);
      if (!items || items.length === 0) return `Para enviar para aprovação, a meta "${m.titulo || ''}" deve ter ao menos 1 item.`;
    }

    // 3) Total geral do plano >= valor da emenda
    // compute total from metas (fallback to plan.valor)
    const totalFromMetas = roundMoney(metas.reduce((s, mm) => s + getMetaTotal(mm), 0));
    const totalPlano = totalFromMetas > 0 ? totalFromMetas : (plan?.valor ?? parseCurrency(planoForm.valor));
    const emendaValor = plan?.emendaValor ?? plan?.emenda?.value ?? (plan?.valor ?? 0);
    if (emendaValor && Number(totalPlano) < Number(emendaValor)) {
      return `Para enviar para aprovação, o total do plano (${currencyFmt.format(Number(totalPlano))}) deve ser igual ou maior que o valor da emenda (${currencyFmt.format(Number(emendaValor))}).`;
    }

    const cronogramaExecucaoAtual = cronogramaExecucaoMesesLoaded
      ? cronogramaExecucaoMeses
      : await cronogramaExecucaoMesService.listByPlano(id).catch(() => [] as CronogramaExecucaoMes[]);

    if (!cronogramaExecucaoMesesLoaded) {
      setCronogramaExecucaoMeses(cronogramaExecucaoAtual);
      setCronogramaExecucaoMesesLoaded(true);
    }

    const possuiCronogramaExecucaoPreenchido = cronogramaExecucaoAtual.some((item) => Number(item.valor || 0) > 0);
    if (!possuiCronogramaExecucaoPreenchido) {
      return 'Para enviar para aprovação, informe ao menos um valor no cronograma de execução.';
    }

    return null;
  };

  const handleRequestSendForApproval = async () => {
    await handleSendForApproval();
  };

  const handleConcluirParaAssinatura = async () => {
    if (!id) return;
    const errMsg = await validateBeforeSend();
    if (errMsg) {
      setPlanErrors({ general: errMsg });
      setToast({ message: errMsg, type: 'error' });
      return;
    }
    try {
      setSendingForApproval(true);
      await planoService.concluirParaAssinatura(id);
      const res = await planoService.getFull(id);
      setPlan(res.data);
      setToast({ message: 'Plano concluído. Edição travada para assinatura.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao concluir plano para assinatura', type: 'error' });
    } finally {
      setSendingForApproval(false);
    }
  };

  const handleSendForApproval = async () => {
    if (!id) return;
    setPlanErrors({});
    try {
      setSendingForApproval(true);
      const errMsg = await validateBeforeSend();
      if (errMsg) {
        setPlanErrors({ general: errMsg });
        setToast({ message: errMsg, type: 'error' });
        return;
      }
      // use dedicated API to send for approval (server-side validation)
      await planoService.enviar(id);
      // refresh plan and metas from server
      const res = await planoService.getFull(id);
      setPlan(res.data);
      setMetas(await metaService.listByPlano(id));
      // feedback
      const protocolo = res.data?.numeroProtocolo;
      const toastMsg = protocolo
        ? `Plano enviado para aprovação. Protocolo: ${protocolo}`
        : 'Plano enviado para aprovação com sucesso.';
      setToast({ message: toastMsg, type: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Erro ao enviar para aprovação';
      setToast({ message: String(msg), type: 'error' });
    } finally {
      setSendingForApproval(false);
      setConfirmSending(false);
    }
  };

  const handleSolicitarRetificacaoConvenios = async () => {
    if (!id || !retificacaoConveniosMotivo.trim()) {
      setToast({ message: 'Descreva o que precisa ser retificado.', type: 'error' });
      return;
    }
    if (!retificacaoConveniosEtapa) {
      setToast({ message: 'Selecione a etapa de retificação.', type: 'error' });
      return;
    }
    try {
      setSolicitandoRetificacaoConvenios(true);
      const updated = await planoService.solicitarRetificacao(
        id,
        retificacaoConveniosMotivo.trim(),
        retificacaoConveniosEtapa,
        retificacaoConveniosRetorno || undefined,
      );
      setPlan(updated);
      setShowRetificacaoConveniosModal(false);
      setRetificacaoConveniosMotivo('');
      setRetificacaoConveniosEtapa('');
      setRetificacaoConveniosRetorno('');
      setToast({ message: 'Retificação solicitada. O operador foi notificado.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao solicitar retificação.', type: 'error' });
    } finally {
      setSolicitandoRetificacaoConvenios(false);
    }
  };

  const handleIniciarRetificacaoConvenios = async () => {
    if (!id || !conveniosIniciarRetMotivo.trim()) return;
    try {
      setConveniosIniciarRetLoading(true);
      const updated = await planoService.solicitarRetificacao(id, conveniosIniciarRetMotivo.trim(), etapaAtual || undefined);
      setPlan(updated);
      setConveniosIniciarRetOpen(false);
      setConveniosIniciarRetMotivo('');
      setToast({ message: 'Retificação iniciada. Você pode editar o plano de trabalho.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao iniciar retificação.', type: 'error' });
    } finally {
      setConveniosIniciarRetLoading(false);
    }
  };

  const handleJuntarDocumento = async () => {
    if (!id || !juntadaFile || !juntadaTipo || !juntadaEtapa || !juntadaJustificativa.trim()) return;
    try {
      setEnviandoJuntada(true);
      await planoService.juntarDocumento(id, juntadaTipo, juntadaEtapa, juntadaFile, juntadaJustificativa.trim());
      setToast({ message: 'Documento juntado com sucesso.', type: 'success' });
      setShowJuntadaModal(false);
      setJuntadaTipo('');
      setJuntadaEtapa('');
      setJuntadaFile(null);
      setJuntadaJustificativa('');
      setJuntadaFaseLocked(false);
      setJuntadaAnexoExistente(null);
      await loadHistoricoWorkflow();
      await loadAnexos();
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao juntar documento.', type: 'error' });
    } finally {
      setEnviandoJuntada(false);
    }
  };

  const handleEnviarRetificacao = async () => {
    if (!id) return;
    try {
      setEnviandoRetificacao(true);
      const res = await planoService.enviarRetificacao(id);
      setPlan(res);
      const etapaLabel = ETAPA_LABEL[res.etapaAtual || ''] || res.etapaAtual || 'etapa anterior';
      setToast({ message: `Retificação enviada. O plano voltou para ${etapaLabel}.`, type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao enviar retificação.', type: 'error' });
    } finally {
      setEnviandoRetificacao(false);
    }
  };

  const loadAnexos = async () => {
    if (!id) return;
    try {
      setAnexosLoading(true);
      const data = await planoService.listAnexos(id);
      setAnexos(data || []);
    } catch {
      setAnexos([]);
    } finally {
      setAnexosLoading(false);
    }
  };

  const loadHistoricoWorkflow = async () => {
    if (!id) return;
    try {
      const data = await planoService.getHistorico(id);
      setHistoricoWorkflow(data || []);
    } catch {
      setHistoricoWorkflow([]);
    }
  };

  const closeWorkflowModal = () => {
    setWorkflowAction(null);
    setWorkflowMotivo('');
    setWorkflowParecer('');
  };

  const refreshPlan = async () => {
    if (!id) return;
    const res = await planoService.getFull(id);
    setPlan(res.data);
    setGestorCpf('');
    setComissaoPickerId(res.data?.comissaoId || '');
  };

  const handleVincularGestor = async () => {
    if (!id) return;
    if (!gestorLocalizado) {
      setToast({ message: 'Digite um CPF válido de um gestor cadastrado', type: 'error' });
      return;
    }
    try {
      setSavingGestor(true);
      const updated = await planoService.vincularGestor(id, gestorLocalizado.id);
      setPlan((prev: any) => ({ ...prev, gestorId: updated.gestorId, gestorNome: updated.gestorNome }));
      setGestorCpf('');
      setToast({ message: 'Gestor da parceria vinculado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao vincular gestor', type: 'error' });
    } finally {
      setSavingGestor(false);
    }
  };

  // Vinculação do gestor na etapa do Gestor da Parceria (Emenda Direta), por CPF.
  const handleVincularGestorSelecionado = async (gestorId: number) => {
    if (!id || !gestorId) return;
    try {
      setSavingGestor(true);
      const updated = await planoService.vincularGestor(id, gestorId);
      setPlan((prev: any) => ({ ...prev, gestorId: updated.gestorId, gestorNome: updated.gestorNome }));
      setGestorCpf('');
      setToast({ message: 'Gestor da parceria vinculado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao vincular gestor', type: 'error' });
    } finally {
      setSavingGestor(false);
    }
  };

  const handleVincularProcuradorJuridico = async () => {
    if (!id) return;
    if (!procuradorJuridicoId) {
      setToast({ message: 'Selecione um procurador para vincular ao plano', type: 'error' });
      return;
    }
    try {
      setSavingProcuradorJuridico(true);
      const updated = await planoService.vincularProcuradorJuridico(id, Number(procuradorJuridicoId));
      setPlan((prev: any) => ({ ...prev, procuradorJuridicoId: updated.procuradorJuridicoId, procuradorJuridicoNome: updated.procuradorJuridicoNome }));
      setToast({ message: 'Procurador vinculado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao vincular procurador', type: 'error' });
    } finally {
      setSavingProcuradorJuridico(false);
    }
  };

  const handleDesvincularProcuradorJuridico = async () => {
    if (!id) return;
    try {
      setSavingProcuradorJuridico(true);
      const updated = await planoService.vincularProcuradorJuridico(id, null);
      setPlan((prev: any) => ({ ...prev, procuradorJuridicoId: updated.procuradorJuridicoId, procuradorJuridicoNome: updated.procuradorJuridicoNome }));
      setProcuradorJuridicoId('');
      setToast({ message: 'Procurador desvinculado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao desvincular procurador', type: 'error' });
    } finally {
      setSavingProcuradorJuridico(false);
    }
  };

  const handleVincularComissao = async () => {
    if (!id) return;
    try {
      setSavingComissao(true);
      const updated = await planoService.vincularComissao(id, comissaoPickerId || null);
      setPlan((prev: any) => ({
        ...prev,
        comissaoId: updated.comissaoId,
        comissaoNumero: updated.comissaoNumero,
        comissaoNumeroPortaria: updated.comissaoNumeroPortaria,
        comissaoAnoPortaria: updated.comissaoAnoPortaria,
      }));
      setToast({ message: 'Comissão de seleção vinculada com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao vincular comissão', type: 'error' });
    } finally {
      setSavingComissao(false);
    }
  };

  const handleWorkflowAction = async () => {
    if (!id || !workflowAction) return;
    try {
      setWorkflowLoading(true);
      const payload: PlanoWorkflowPayload = {
        motivo: workflowMotivo.trim() || undefined,
        parecerTecnico: etapaAtual === 'GESTOR_PARCERIA' ? workflowParecer.trim() || undefined : undefined,
        parecerJuridico: etapaAtual === 'PROCURADORIA_JURIDICA' ? workflowParecer.trim() || undefined : undefined,
      };

      if (workflowAction === 'aprovar') {
        await planoService.aprovar(id, payload);
      } else if (workflowAction === 'reprovar') {
        await planoService.reprovar(id, payload);
      } else if (workflowAction === 'devolver') {
        await planoService.devolver(id, payload);
      } else if (workflowAction === 'confirmarReprovacao') {
        await planoService.confirmarReprovacao(id);
      }

      await refreshPlan();
      await loadAnexos();
      await loadHistoricoWorkflow();
      await loadGestorParecerAnexo();
      closeWorkflowModal();
      setToast({ message: 'Workflow atualizado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao atualizar workflow', type: 'error' });
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleAdminEditarFase = async () => {
    if (!id || !adminFaseStatus) return;
    if (!adminFaseMotivo.trim()) {
      setToast({ message: 'Informe o motivo da alteração.', type: 'error' });
      return;
    }
    try {
      setAdminFaseLoading(true);
      await planoService.editarFaseAdmin(id, {
        novoStatus: adminFaseStatus,
        novaEtapa: adminFaseEtapa || null,
        motivo: adminFaseMotivo.trim(),
      });
      await refreshPlan();
      await loadAnexos();
      await loadHistoricoWorkflow();
      setShowAdminFaseModal(false);
      setAdminFaseStatus('');
      setAdminFaseEtapa('');
      setAdminFaseMotivo('');
      setToast({ message: 'Fase do plano atualizada com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao editar fase', type: 'error' });
    } finally {
      setAdminFaseLoading(false);
    }
  };

  const handleSalvarParecerTecnico = async () => {
    if (!id || !parecerTecnicoDraft.trim()) return;
    try {
      setSavingParecerTecnico(true);
      await planoService.salvarParecerTecnico(id, parecerTecnicoDraft.trim());
      const planRes = await planoService.getFull(id);
      if (planRes?.data) setPlan(planRes.data);
      setToast({ message: 'Parecer salvo. Agora imprima, assine e anexe o PDF abaixo.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao salvar o parecer técnico.', type: 'error' });
    } finally {
      setSavingParecerTecnico(false);
    }
  };

  const handleImprimirParecerGestor = () => {
    if (!id) return;
    openAuthenticatedTab(`${BASENAME}/plano/parecer-gestor/impressao/${id}?autoprint=1`);
  };

  const handleUploadAnexoWorkflow = async () => {
    if (!id || !plan?.etapaAtual || !selectedAnexoFile) return;
    const required = ETAPA_ANEXO_OBRIGATORIO[plan.etapaAtual];
    if (!required) return;

    try {
      setUploadingAnexo(true);
      if (required.tipo === 'ATA_COMISSAO') {
        await comissaoService.uploadAtaPresidente(id, selectedAnexoFile);
        setSelectedAnexoFile(null);
        setAnexoDescricao('');
        const [, , planRes] = await Promise.all([loadAnexos(), loadPareceres(), planoService.getFull(id)]);
        if (planRes?.data) setPlan(planRes.data);
      } else {
        await planoService.uploadAnexo(id, {
          file: selectedAnexoFile,
          etapa: plan.etapaAtual,
          tipoDocumento: required.tipo,
          descricao: anexoDescricao.trim() || undefined,
        });
        setSelectedAnexoFile(null);
        setAnexoDescricao('');
        const [, planRes] = await Promise.all([loadAnexos(), planoService.getFull(id)]);
        if (planRes?.data) setPlan(planRes.data);
      }
      setToast({ message: `${required.label} anexado com sucesso.`, type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao enviar anexo', type: 'error' });
    } finally {
      setUploadingAnexo(false);
    }
  };

  const handleUploadAnexoSecundario = async () => {
    if (!id || !plan?.etapaAtual || !selectedAnexoSecundarioFile) return;
    const required = ETAPA_ANEXO_SECUNDARIO[plan.etapaAtual];
    if (!required) return;
    try {
      setUploadingAnexoSecundario(true);
      await planoService.uploadAnexo(id, {
        file: selectedAnexoSecundarioFile,
        etapa: plan.etapaAtual,
        tipoDocumento: required.tipo,
        descricao: anexoSecundarioDescricao.trim() || undefined,
      });
      setSelectedAnexoSecundarioFile(null);
      setAnexoSecundarioDescricao('');
      const [, planRes] = await Promise.all([loadAnexos(), planoService.getFull(id)]);
      if (planRes?.data) setPlan(planRes.data);
      setToast({ message: `${required.label} anexado com sucesso.`, type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao enviar anexo', type: 'error' });
    } finally {
      setUploadingAnexoSecundario(false);
    }
  };

  const handleUploadRetroAnexo = async () => {
    if (!id || !selectedRetroAnexoFile) return;
    const required = ETAPA_ANEXO_OBRIGATORIO['CONVENIOS_ANALISE'];
    if (!required) return;
    try {
      setUploadingRetroAnexo(true);
      await planoService.uploadAnexo(id, {
        file: selectedRetroAnexoFile,
        etapa: 'CONVENIOS_ANALISE',
        tipoDocumento: required.tipo,
      });
      setSelectedRetroAnexoFile(null);
      await loadAnexos();
      setToast({ message: `${required.label} anexado com sucesso.`, type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao enviar anexo', type: 'error' });
    } finally {
      setUploadingRetroAnexo(false);
    }
  };

  const handleUploadRetroParecerAnexo = async () => {
    if (!id || !selectedRetroParecerAnexoFile) return;
    const required = ETAPA_ANEXO_SECUNDARIO['CONVENIOS_MINUTA'];
    if (!required) return;
    try {
      setUploadingRetroParecerAnexo(true);
      await planoService.uploadAnexo(id, {
        file: selectedRetroParecerAnexoFile,
        etapa: 'CONVENIOS_MINUTA',
        tipoDocumento: required.tipo,
      });
      setSelectedRetroParecerAnexoFile(null);
      await loadAnexos();
      setToast({ message: `${required.label} anexado com sucesso.`, type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao enviar anexo', type: 'error' });
    } finally {
      setUploadingRetroParecerAnexo(false);
    }
  };

  // Carrega o parecer jurídico assinado (PDF) da PGM para exibir inline ao
  // Convênios / Procurador (PGM) / Gestor Jurídico / Admin a partir da etapa
  // jurídica em diante (inclui a própria PGM e as etapas posteriores).
  useEffect(() => {
    const etapa = plan?.etapaAtual || '';
    const canSee = ['CONVENIOS', 'JURIDICO', 'GESTOR_JURIDICO', 'ADMIN'].includes(user?.role || '')
      && ['PROCURADORIA_JURIDICA', 'GESTOR_JURIDICO_REVISAO', 'CONVENIOS_FORMALIZACAO', 'CONCLUIDO'].includes(etapa);
    if (!id || !canSee) {
      setParecerAnexoInfo(null);
      return;
    }
    planoService.getParecerAnexo(id)
      .then(setParecerAnexoInfo)
      .catch(() => setParecerAnexoInfo(null));
  }, [id, plan?.etapaAtual, user?.role]);

  // Carrega o parecer (PDF) do Gestor da Parceria — seja o anexado pelo gestor
  // ou o gerado a partir do texto na aprovacao.
  const loadGestorParecerAnexo = async () => {
    if (!id) return;
    try {
      setGestorParecerAnexo(await planoService.getParecerAnexo(id, 'GESTOR_PARCERIA'));
    } catch {
      setGestorParecerAnexo(null);
    }
  };
  useEffect(() => { loadGestorParecerAnexo(); }, [id, plan?.etapaAtual]);

  const handleUploadGestorParecer = async (file: File | null) => {
    if (!id || !file) return;
    try {
      setGestorParecerUploading(true);
      const saved = await planoService.uploadParecerAnexo(id, file, 'GESTOR_PARCERIA');
      setGestorParecerAnexo(saved);
      setToast({ message: 'Parecer (PDF) anexado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Não foi possível anexar o parecer (PDF).', type: 'error' });
    } finally {
      setGestorParecerUploading(false);
    }
  };

  // Abre o PDF autenticado do parecer numa nova aba. Abre a aba de forma
  // síncrona (no clique) para não ser bloqueada por popup blocker e só então
  // aponta para a object URL do blob baixado com o token.
  const handleAbrirParecerAnexo = async () => {
    if (!id) return;
    try {
      setAbrindoParecerAnexo(true);
      const blob = await planoService.downloadParecerAnexo(id);
      setParecerViewerUrl(URL.createObjectURL(blob));
    } catch {
      setToast({ message: 'Não foi possível abrir o parecer jurídico.', type: 'error' });
    } finally {
      setAbrindoParecerAnexo(false);
    }
  };

  const fecharParecerViewer = () => setParecerViewerUrl(null);

  // Abre o parecer (PDF) do Gestor da Parceria via blob autenticado.
  const handleAbrirGestorParecer = async () => {
    if (!id) return;
    try {
      setAbrindoGestorParecer(true);
      const blob = await planoService.downloadParecerAnexo(id, 'GESTOR_PARCERIA');
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setToast({ message: 'Não foi possível abrir o parecer do gestor.', type: 'error' });
    } finally {
      setAbrindoGestorParecer(false);
    }
  };

  const handleRemoverGestorParecer = async () => {
    if (!id || !window.confirm('Remover o parecer (PDF) do gestor da parceria?')) return;
    try {
      await planoService.deleteParecerAnexo(id, 'GESTOR_PARCERIA');
      setGestorParecerAnexo(null);
      setToast({ message: 'Parecer removido.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Não foi possível remover o parecer.', type: 'error' });
    }
  };

  // Revoga a object URL do parecer ao fechar/trocar/desmontar (evita vazamento).
  useEffect(() => {
    if (!parecerViewerUrl) return;
    return () => URL.revokeObjectURL(parecerViewerUrl);
  }, [parecerViewerUrl]);

  const handleUploadComprovante = async () => {
    if (!id || !comprovanteFile) return;
    try {
      setUploadingComprovante(true);
      await planoService.uploadAnexo(id, {
        file: comprovanteFile,
        etapa: 'RASCUNHO',
        tipoDocumento: 'COMPROVANTE_CONTA_ZERADA',
        descricao: 'Comprovante de conta zerada',
      });
      setComprovanteFile(null);
      await loadAnexos();
      setToast({ message: 'Comprovante de conta zerada enviado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao enviar comprovante', type: 'error' });
    } finally {
      setUploadingComprovante(false);
    }
  };

  const handleUploadContrapartida = async () => {
    if (!id || !contrapartidaFile) return;
    try {
      setUploadingContrapartida(true);
      await planoService.uploadAnexo(id, {
        file: contrapartidaFile,
        etapa: 'RASCUNHO',
        tipoDocumento: 'CONTRAPARTIDA',
        descricao: 'Comprovante de contrapartida',
      });
      setContrapartidaFile(null);
      await loadAnexos();
      setToast({ message: 'Comprovante de contrapartida enviado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao enviar comprovante de contrapartida', type: 'error' });
    } finally {
      setUploadingContrapartida(false);
    }
  };

  const handleDeleteContrapartida = async (anexoId: string) => {
    if (!id) return;
    if (!window.confirm('Remover o comprovante de contrapartida? Você poderá enviar um novo em seguida.')) return;
    try {
      await planoService.deleteAnexo(id, anexoId);
      await loadAnexos();
      setToast({ message: 'Comprovante removido. Envie um novo arquivo.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao remover comprovante', type: 'error' });
    }
  };

  const handleIniciarAnalise = async () => {
    if (!id) return;
    try {
      setIniciandoAnalise(true);
      await planoService.iniciarAnalise(id);
      await refreshPlan();
      setToast({ message: 'Análise iniciada com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao iniciar análise', type: 'error' });
    } finally {
      setIniciandoAnalise(false);
    }
  };

  const handleRegistrarAprovacaoPrefeito = async () => {
    if (!id) return;
    try {
      setRegistrandoAprovacaoPrefeito(true);
      await planoService.registrarAprovacaoPrefeito(id);
      await refreshPlan();
      setToast({ message: 'Aprovação do Prefeito registrada com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao registrar aprovação do Prefeito', type: 'error' });
    } finally {
      setRegistrandoAprovacaoPrefeito(false);
    }
  };

  const handleUploadPlanoAssinado = async () => {
    if (!id || !planoAssinadoFile) return;
    try {
      setUploadingPlanoAssinado(true);
      await planoService.uploadAnexo(id, {
        file: planoAssinadoFile,
        etapa: 'ENVIO',
        tipoDocumento: 'PLANO_ASSINADO_PRESIDENTE',
        descricao: 'Plano de trabalho assinado pelo diretor da instituição',
      });
      setPlanoAssinadoFile(null);
      await loadAnexos();
      setToast({ message: 'Plano de trabalho assinado enviado com sucesso.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.response?.data?.error || 'Erro ao enviar plano assinado', type: 'error' });
    } finally {
      setUploadingPlanoAssinado(false);
    }
  };


  // -- Initial load ----------------------------------------------------------
  useEffect(() => {
    const loadSupportData = async (instId: string) => {
      const instituicaoDaLista = instituicoes.find(inst => inst.id === instId);
      try {
        const inst = await institutionService.getByInstitutionId(instId);
        setInstituicaoNome(
          inst?.razaoSocial ||
          inst?.nomeFantasia ||
          instituicaoDaLista?.razaoSocial ||
          instituicaoDaLista?.nomeFantasia ||
          '',
        );
      } catch {
        try {
          const instMinima = await institutionService.getMinimos(instId);
          setInstituicaoNome(
            instMinima?.razaoSocial ||
            instMinima?.nomeFantasia ||
            instituicaoDaLista?.razaoSocial ||
            instituicaoDaLista?.nomeFantasia ||
            '',
          );
        } catch {
          setInstituicaoNome(
            instituicaoDaLista?.razaoSocial ||
            instituicaoDaLista?.nomeFantasia ||
            '',
          );
        }
      }
      try {
        const [all, planosDaInstituicao] = await Promise.all([
          emendaService.listWithDetails(),
          isCreateMode ? planoService.listByInstituicao(instId) : Promise.resolve([]),
        ]);
        const emendaIdsComPlano = new Set(planosDaInstituicao.map(plano => plano.emendaId).filter(Boolean));
        const emendasDaInstituicao = all.filter((e: any) =>
          (e.institutionId === instId || e.institution?.id === instId)
          && (!isCreateMode || isEmendaElegivelParaNovoPlano(e))
          && (!isCreateMode || !emendaIdsComPlano.has(e.id)),
        );
        setEmendas(emendasDaInstituicao);
        const nomeViaEmenda =
          emendasDaInstituicao.find((e: any) => e.id === (plan?.emendaId || planoForm.emendaId))?.institutionName ||
          emendasDaInstituicao.find((e: any) => e.institutionName)?.institutionName ||
          '';
        if (nomeViaEmenda) {
          setInstituicaoNome((atual) => atual || nomeViaEmenda);
        }
      } catch { /* ignore */ }
      try {
        const allDir = prioritizeDirigentes(await dirigenteService.listar(instId, true) || []);
        setDirigentes(allDir);
        if (allDir.length > 0) {
          const preferredDir = getPreferredDirigente(allDir, plan?.dirigenteId || planoForm.dirigenteId);
          if (preferredDir?.id) {
            setPlanoForm(f => ({ ...f, dirigenteId: preferredDir.id }));
          }
        }
      } catch { setDirigentes([]); }
    };

    if (isCreateMode) {
      setPlanoForm({ ...emptyPlanoForm(), emendaId: emendaIdParam });
      setIsEditingHeader(true);
      const initAsync = async () => {
        try {
          const insts = await getMinhasInstituicoesDetalhadas();
          setInstituicoes(insts);
          if (instituicaoIdParam) {
            await loadSupportData(instituicaoIdParam);
          } else if (insts.length === 1) {
            setSelectedInstituicaoId(insts[0].id);
            await loadSupportData(insts[0].id);
          }
        } catch { /* ignore */ }
      };
      initAsync().finally(() => setPageLoading(false));
    } else {
      const loadExisting = async () => {
        try {
          setPageLoading(true);
          const res = await planoService.getFull(id!);
          const p = res.data;
          setPlan(p);
          // Instituições do usuário — necessárias para saber se ele é o dono do plano
          // (habilita a designação do gestor na Emenda Direta). No modo criação já é carregado.
          getMinhasInstituicoesDetalhadas().then(setInstituicoes).catch(() => {});
          setGestorCpf('');
          setComissaoPickerId(p.comissaoId || '');
          setPlanoForm({
            titulo: p.titulo || '',
            descricao: p.descricao || '',
            emendaId: p.emendaId || '',
            valor: p.valor != null ? formatCurrency(String(p.valor)) : 'R$ 0,00',
            status: p.status || 'RASCUNHO',
            banco: p.banco || '',
            agencia: p.agencia || '',
            contaBancaria: p.contaBancaria ? formatContaDisplay(String(p.contaBancaria)) : '',
            periodoInicio: p.periodoInicio ? p.periodoInicio.substring(0,7) : '',
            periodoFim: p.periodoFim ? p.periodoFim.substring(0,7) : '',
            modalidade: p.modalidade || 'TERMO_FOMENTO',
            dirigenteId: p.dirigenteId || '',
            objetoParceria: p.objetoParceria || '',
            descricaoRealidade: p.descricaoRealidade || '',
            formaExecucao: p.formaExecucao || '',
          });
          // try to resolve banco value: if backend stored code, find name and keep code
          if (p.banco) {
            const b = findByCode(p.banco);
            if (b) { setBankCode(b.code); setPlanoForm(f => ({ ...f, banco: b.name })); }
            else setBankCode('');
          }
          const instId = p.instituicaoId || instituicaoIdParam;
          if (instId) await loadSupportData(instId);
          setMetas(await metaService.listByPlano(id!));

        } catch (err) {
          console.error('Erro ao carregar plano', err);
        } finally {
          setPageLoading(false);
        }
      };
      loadExisting();
    }
    // populate local list from BanksContext
  }, [id]);

  // Emenda Direta: banco/agencia/conta bancaria nao sao obrigatorios.
  // Somente o valor explicito "Direta" habilita o fluxo simplificado.
  function isEmendaDiretaAtual(): boolean {
    const emendaVinc = emendas.find((em: any) => em.id === (plan?.emendaId || planoForm.emendaId));
    return !!emendaVinc && String(emendaVinc?.tipoTransferencia || '').trim().toLowerCase() === 'direta';
  }

  // -- Plan save (create or update header) -----------------------------------
  const validatePlan = (): boolean => {
    if (planoForm.status === 'RASCUNHO') {
      setPlanErrors({});
      return true;
    }

    const e: Record<string, string> = {};
    if (!planoForm.titulo.trim()) e.titulo = 'Título é obrigatório';
    if (planoForm.titulo.trim().length > 0 && planoForm.titulo.trim().length < 5)
      e.titulo = 'Título deve ter ao menos 5 caracteres';
    if (isCreateMode && !selectedInstituicaoId && !instituicaoIdParam)
      e.instituicao = 'Selecione uma instituição';
    if (!planoForm.periodoInicio) e.periodoInicio = 'Período de início é obrigatório';
    if (!planoForm.periodoFim) e.periodoFim = 'Período de fim é obrigatório';
    if (planoForm.periodoInicio && planoForm.periodoFim && planoForm.periodoInicio > planoForm.periodoFim)
      e.periodoFim = 'O período de fim deve ser igual ou posterior ao início';
    if (!planoForm.modalidade) e.modalidade = 'Modalidade é obrigatória';
    // Emenda Direta: dados bancarios opcionais (ver isEmendaDiretaAtual).
    if (!isEmendaDiretaAtual()) {
      if (!(planoForm.banco || '').trim()) e.banco = 'Banco é obrigatório';
      if (!(planoForm.agencia || '').trim()) e.agencia = 'Agência é obrigatória';
      if (!(planoForm.contaBancaria || '').replace(/\D/g, '').trim()) e.contaBancaria = 'Conta bancária é obrigatória';
    }
    if (!planoForm.objetoParceria.trim()) e.objetoParceria = 'Objeto da parceria é obrigatório';
    if (!planoForm.descricaoRealidade.trim()) e.descricaoRealidade = 'Descrição da realidade é obrigatória';
    if (!planoForm.formaExecucao.trim()) e.formaExecucao = 'Forma de execução é obrigatória';
    setPlanErrors(e);
    return Object.keys(e).length === 0;
  };

  const validationMessages = Object.entries(planErrors)
    .filter(([key]) => key !== 'general')
    .map(([, message]) => String(message));

  const handleSavePlan = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validatePlan()) return;
    try {
      setSavingPlan(true);
      const valorNum = parseCurrency(planoForm.valor);
      const resolvedInstituicaoId = plan?.instituicaoId || selectedInstituicaoId || instituicaoIdParam || undefined;
      // normalize account: remove non-digits, pad left to 20
      const rawConta = String(planoForm.contaBancaria || '').replace(/\D/g, '');
      const paddedConta = rawConta ? rawConta.padStart(20, '0') : undefined;

      const payload: any = {
         titulo: planoForm.titulo.trim(),
         descricao: planoForm.descricao.trim() || undefined,
         instituicaoId: resolvedInstituicaoId,
         emendaId: planoForm.emendaId.trim() ? planoForm.emendaId : undefined,
         valor: valorNum > 0 ? valorNum : undefined,
         status: planoForm.status,
         // Prefer sending bank code when available; also include explicit bancoCodigo and bancoNome fields
         banco: bankCode || planoForm.banco || undefined,
         bancoCodigo: bankCode || undefined,
         bancoNome: planoForm.banco || undefined,
         agencia: planoForm.agencia || undefined,
         contaBancaria: paddedConta,
         periodoInicio: planoForm.periodoInicio ? new Date(planoForm.periodoInicio).toISOString() : undefined,
         periodoFim: planoForm.periodoFim ? new Date(planoForm.periodoFim).toISOString() : undefined,
         modalidade: planoForm.modalidade || undefined,
         dirigenteId: planoForm.dirigenteId || undefined,
         objetoParceria: planoForm.objetoParceria?.trim() || undefined,
         descricaoRealidade: planoForm.descricaoRealidade?.trim() || undefined,
         formaExecucao: planoForm.formaExecucao?.trim() || undefined,
       };

      if (isCreateMode) {
        const created = await planoService.create(payload);
        navigate(`/dashboard/plano/full/${created.id}?instituicaoId=${resolvedInstituicaoId || ''}`, { replace: true });
      } else {
        const updated = await planoService.update(id!, payload);
        setPlan((prev: any) => ({ ...prev, ...updated }));
        setIsEditingHeader(false);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Erro ao salvar plano';
      setPlanErrors({ general: msg });
    } finally {
      setSavingPlan(false);
    }
  };

  const handleValueBlur = () => {
    const raw = parseCurrency(planoForm.valor);
    setPlanoForm(f => ({ ...f, valor: formatCurrency(String(raw)) }));
  };

  const formatMoneyInput = (value: string) => formatCurrency(parseCurrency(value));
  const sanitizeMoneyDraft = (value: string) => {
    const cleaned = String(value || '').replace(/[^\d,]/g, '');
    const commaIndex = cleaned.indexOf(',');
    if (commaIndex === -1) return cleaned;
    return `${cleaned.slice(0, commaIndex + 1)}${cleaned.slice(commaIndex + 1).replace(/,/g, '')}`;
  };
  const formatMoneyOnBlur = (value?: string) => {
    const draft = String(value || '').trim();
    if (!draft) return '';
    return formatCurrency(parseCurrency(draft));
  };

  const handleItemMoneyChange = (
    metaId: string,
    field: 'valorUnitario',
    value: string
  ) => {
    const draft = sanitizeMoneyDraft(value);
    setNewItemByMeta(prev => ({ ...prev, [metaId]: { ...emptyItem(), ...prev[metaId], [field]: draft } }));
  };

  const handleItemMoneyBlur = (
    metaId: string,
    field: 'valorUnitario'
  ) => {
    setNewItemByMeta(prev => ({
      ...prev,
      [metaId]: {
        ...emptyItem(),
        ...prev[metaId],
        [field]: formatMoneyOnBlur(prev[metaId]?.[field]),
      },
    }));
  };

  const handleEditItemMoneyChange = (
    field: 'valorUnitario',
    value: string
  ) => {
    setEditItemForm(prev => ({ ...prev, [field]: sanitizeMoneyDraft(value) }));
  };

  const handleEditItemMoneyBlur = (
    field: 'valorUnitario'
  ) => {
    setEditItemForm(prev => ({ ...prev, [field]: formatMoneyOnBlur(prev[field]) }));
  };

  // --- Bank account helpers ---
  const formatContaDisplay = (raw: string) => {
    const digits = String(raw || '').replace(/\D/g, '');
    const padded = digits.padStart(20, '0').slice(-20);
    const groups: string[] = [];
    for (let i = 0; i < 5; i++) groups.push(padded.slice(i * 4, i * 4 + 4));
    return groups.join(' ');
  };

  // Helpers for cronograma display
  const formatNumber = (v: number | string) => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(n);
  };

  const parseCalendarDate = (value?: string | null) => {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const dateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };

  const serializeItemDate = (value?: string | null) => {
    const raw = String(value || '').trim();
    if (!raw) return undefined;

    const dateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return `${year}-${month}-${day}T12:00:00Z`;
    }

    return raw;
  };

  const formatPeriodo = (inicio?: string | null, fim?: string | null) => {
    if (!inicio && !fim) return '—';
    try {
      const s = parseCalendarDate(inicio);
      const e = parseCalendarDate(fim);
      if (s && e) {
        return `${s.toLocaleDateString('pt-BR')} a ${e.toLocaleDateString('pt-BR')}`;
      }
      if (s) return `${s.toLocaleDateString('pt-BR')}`;
      if (e) return `${e.toLocaleDateString('pt-BR')}`;
    } catch {
      return '—';
    }
    return '—';
  };

  const formatMonthLabel = (month: string) => {
    if (!month) return '—';
    const date = new Date(`${month}-01T00:00:00`);
    if (Number.isNaN(date.getTime())) return month;
    return date.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' });
  };

  const parseMonthSourceDate = (value?: string | null) => {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const ymMatch = raw.match(/^(\d{4})-(\d{2})$/);
    if (ymMatch) {
      return new Date(`${raw}-01T00:00:00`);
    }

    const isoDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDateMatch) {
      return new Date(`${isoDateMatch[1]}-${isoDateMatch[2]}-01T00:00:00`);
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), 1);
  };

  const buildMonthRange = (inicio?: string | null, fim?: string | null): string[] => {
    if (!inicio || !fim) return [];
    const start = parseMonthSourceDate(inicio);
    const end = parseMonthSourceDate(fim);
    if (!start || !end) return [];
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];

    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const limit = new Date(end.getFullYear(), end.getMonth(), 1);
    const months: string[] = [];

    while (cursor <= limit) {
      const year = cursor.getFullYear();
      const month = String(cursor.getMonth() + 1).padStart(2, '0');
      months.push(`${year}-${month}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return months;
  };

  const cronogramaExecucaoPlanejado = useMemo(() => {
    const monthMap = new Map<string, Set<string>>();

    metas.forEach((meta) => {
      const metaLabel = String(meta.titulo || meta.descricao || 'Meta sem titulo').trim();
      (itemsByMeta[meta.id || ''] || []).forEach((item) => {
        const months = buildMonthRange(item.periodoInicio, item.periodoFim);
        months.forEach((month) => {
          if (!monthMap.has(month)) monthMap.set(month, new Set<string>());
          monthMap.get(month)?.add(metaLabel);
        });
      });
    });

    return Array.from(monthMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mes, metasDoMes]) => ({
        mes,
        metas: Array.from(metasDoMes).sort((a, b) => a.localeCompare(b, 'pt-BR')),
      }));
  }, [itemsByMeta, metas]);


  useEffect(() => {
    const preloadItemsForStructure = async () => {
      if (activeSection !== 'estrutura') return;
      if (!metas || metas.length === 0) return;

      const missingMetas = metas.filter(meta => meta.id && itemsByMeta[meta.id] === undefined);
      if (missingMetas.length === 0) return;

      const loadedEntries = await Promise.all(
        missingMetas.map(async (meta) => {
          try {
            const items = await itemService.listByMeta(meta.id!);
            return [meta.id!, items] as const;
          } catch {
            return [meta.id!, []] as const;
          }
        })
      );

      setItemsByMeta(prev => ({
        ...prev,
        ...Object.fromEntries(loadedEntries),
      }));
    };

    preloadItemsForStructure();
  }, [activeSection, metas, itemsByMeta]);


     // -- Tabs: metas ? prestacoes ----------------------------------------------
     const toggleTab = (tab: Tab) => {
     if (activeTab === tab) return;
     setActiveTab(tab);
     // reset expanded metas when switching away from metas tab
     if (tab !== 'metas') setExpandedMetas(new Set());
     };

     // Select a tab and perform extra actions (scroll to cronograma when requested)
     const handleSelectTab = (tab: Tab) => {
     // always set activeTab (allows re-triggering effects even if same)
     setActiveTab(tab);
     if (tab !== 'metas') setExpandedMetas(new Set());
     if (tab === 'cronograma') {
       // scroll the cronograma section into view after React updates the DOM
       setTimeout(() => {
         const el = document.getElementById('cronograma-section');
         if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
       }, 80);
     }
     };

  // -- Metas -----------------------------------------------------------------
  const reloadMetas = async () => {
    if (!id) return;
    setMetas(await metaService.listByPlano(id));
  };

  const [isReorderingMeta, setIsReorderingMeta] = useState(false);

  const handleMoveMeta = async (metaId: string, direction: 'up' | 'down') => {
    if (isReorderingMeta) return;
    const idx = metas.findIndex(m => m.id === metaId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= metas.length) return;
    const reordered = [...metas];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    const updated = reordered.map((m, i) => ({ ...m, ordem: i }));
    setMetas(updated);
    setIsReorderingMeta(true);
    try {
      await metaService.reorder(updated.map(m => ({ id: m.id!, ordem: m.ordem! })));
    } catch {
      await reloadMetas();
      setToast({ message: 'Erro ao salvar nova ordem das metas.', type: 'error' });
    } finally {
      setIsReorderingMeta(false);
    }
  };

  const handleCreateMeta = async (e: React.FormEvent) => {
    e.preventDefault();
    setMetaError(null);
    if (!id) return setMetaError('Não foi possível identificar o plano de trabalho. Recarregue a página.');
    if (!newMeta.descricao || !newMeta.descricao.trim()) return setMetaError('Descrição curta da meta é obrigatória');
    try {
      setSavingMeta(true);
      await metaService.create({
        planoTrabalhoId: id!,
        titulo: newMeta.titulo.trim() || undefined,
        descricao: newMeta.descricao.trim(),
        valor: 0,
      });
      setNewMeta(emptyMeta());
      setShowNewMeta(false);
      await reloadMetas();
    } catch (err: any) {
      setMetaError(err?.response?.data?.error || 'Erro ao criar meta');
    } finally { setSavingMeta(false); }
  };

  const handleSaveMeta = async () => {
    if (!editingMetaId) return;
    if (!id) return setMetaError('Não foi possível identificar o plano de trabalho. Recarregue a página.');
    if (!editMetaForm.titulo.trim()) return setMetaError('Título é obrigatório');
    try {
      setSavingMeta(true);
      await metaService.update(editingMetaId, {
        planoTrabalhoId: id!,
        titulo: editMetaForm.titulo,
        descricao: editMetaForm.descricao,
        valor: 0,
      });
      setEditingMetaId(null);
      await reloadMetas();
    } catch (err: any) {
      setMetaError(err?.response?.data?.error || 'Erro ao salvar meta');
    } finally { setSavingMeta(false); }
  };

  const handleDeleteMeta = async (metaId: string) => {
    if (!confirm('Excluir esta meta e todos os seus itens?')) return;
    try {
      await metaService.remove(metaId);
      setItemsByMeta(prev => { const n = { ...prev }; delete n[metaId]; return n; });
      setExpandedMetas(prev => { const n = new Set(prev); n.delete(metaId); return n; });
      await reloadMetas();
    } catch { setMetaError('Erro ao excluir meta'); }
  };

  // -- Item helpers ----------------------------------------------------------
  const toggleMeta = async (metaId: string) => {
    const next = new Set(expandedMetas);
    if (next.has(metaId)) { next.delete(metaId); }
    else {
      next.add(metaId);
      if (!itemsByMeta[metaId]) {
        try {
          const items = await itemService.listByMeta(metaId);
          setItemsByMeta(prev => ({ ...prev, [metaId]: items }));
        } catch { setItemError('Erro ao carregar itens'); }
      }
      if (!orcamentosByMeta[metaId]) {
        try {
          const orc = await orcamentoMetaService.listByMeta(metaId);
          setOrcamentosByMeta(prev => ({ ...prev, [metaId]: orc }));
        } catch { /* silencioso */ }
      }
    }
    setExpandedMetas(next);
  };

  const handleAddOrcamento = async (metaId: string) => {
    setOrcamentoError(null);
    const file = orcamentoFileByMeta[metaId];
    if (!file) return setOrcamentoError('Selecione um arquivo para o orçamento.');
    const valorRaw = parseCurrency(orcamentoValorByMeta[metaId] || '');
    if (valorRaw <= 0) return setOrcamentoError('Informe o valor do orçamento.');

    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!['.pdf', '.jpg', '.jpeg', '.png'].includes(ext)) {
      return setOrcamentoError('Formato inválido. Use PDF, JPG ou PNG.');
    }
    if (file.size > 10 * 1024 * 1024) {
      return setOrcamentoError(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). O tamanho máximo permitido é 10MB.`);
    }

    try {
      setSavingOrcamentoMeta(metaId);
      const novo = await orcamentoMetaService.upload(metaId, { file, valor: valorRaw });
      setOrcamentosByMeta(prev => ({ ...prev, [metaId]: [...(prev[metaId] || []), novo] }));
      // limpar form
      const previewUrl = orcamentoPreviewByMeta[metaId];
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setOrcamentoFileByMeta(prev => ({ ...prev, [metaId]: null }));
      setOrcamentoPreviewByMeta(prev => ({ ...prev, [metaId]: null }));
      setOrcamentoValorByMeta(prev => ({ ...prev, [metaId]: '' }));
      setShowNewOrcamentoFor(null);
    } catch (err: any) {
      setOrcamentoError(err?.response?.data?.error || 'Erro ao salvar orçamento.');
    } finally {
      setSavingOrcamentoMeta(null);
    }
  };

  const handleDeleteOrcamento = async (metaId: string, orcamentoId: string) => {
    if (!window.confirm('Remover este orçamento?')) return;
    try {
      await orcamentoMetaService.delete(orcamentoId);
      setOrcamentosByMeta(prev => ({ ...prev, [metaId]: (prev[metaId] || []).filter(o => o.id !== orcamentoId) }));
    } catch { /* silencioso */ }
  };

  const handleOrcamentoFileChange = (metaId: string, file: File | null) => {
    const prev = orcamentoPreviewByMeta[metaId];
    if (prev) URL.revokeObjectURL(prev);
    setOrcamentoFileByMeta(p => ({ ...p, [metaId]: file }));
    setOrcamentoPreviewByMeta(p => ({ ...p, [metaId]: file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null }));
  };

  const reloadItems = async (metaId: string) => {
    const items = await itemService.listByMeta(metaId);
    setItemsByMeta(prev => ({ ...prev, [metaId]: items }));
  };

  const handleCreateItem = async (metaId: string) => {
    const form = newItemByMeta[metaId];
    if (!form?.tipo || !form.tipo.trim()) return setItemError('Tipo do item é obrigatório');
    if (!form?.descricao?.trim()) return setItemError('Descrição do item é obrigatória');
    if (!form?.periodoInicio || !form?.periodoFim) return setItemError('Data início e data fim são obrigatórias');
    const valorUnitario = parseCurrency(form.valorUnitario || '');
    if (!(valorUnitario > 0)) return setItemError('Valor unitário deve ser maior que zero');
    if (form.periodoInicio > form.periodoFim) return setItemError('Data início não pode ser posterior à data fim');
    setItemError(null);
    try {
      setSavingItemMeta(metaId);
        await itemService.create({
          metaId,
          descricao: form.descricao.trim(),
          tipo: form.tipo || undefined,
          periodoInicio: serializeItemDate(form.periodoInicio),
          periodoFim: serializeItemDate(form.periodoFim),
          valorUnitario,
          valor: valorUnitario
        });
      setNewItemByMeta(prev => ({ ...prev, [metaId]: emptyItem() }));
      setShowNewItemFor(null);
      await reloadItems(metaId);
      await reloadMetas();
    } catch (err: any) {
      setItemError(err?.response?.data?.error || 'Erro ao criar item');
    } finally { setSavingItemMeta(null); }
  };

  const handleSaveItem = async (metaId: string) => {
    if (!editingItemId) return;
    if (!editItemForm.tipo?.trim()) return setItemError('Tipo do item é obrigatório');
    if (!editItemForm.descricao?.trim()) return setItemError('Descrição do item é obrigatória');
    if (!editItemForm.periodoInicio || !editItemForm.periodoFim) return setItemError('Data início e data fim são obrigatórias');
    const valorUnitario = parseCurrency(editItemForm.valorUnitario || '');
    if (!(valorUnitario > 0)) return setItemError('Valor unitário deve ser maior que zero');
    if (editItemForm.periodoInicio > editItemForm.periodoFim) return setItemError('Data início não pode ser posterior à data fim');
    try {
        await itemService.update(editingItemId, {
          metaId,
          descricao: editItemForm.descricao.trim(),
          tipo: editItemForm.tipo || undefined,
          periodoInicio: serializeItemDate(editItemForm.periodoInicio),
          periodoFim: serializeItemDate(editItemForm.periodoFim),
          valorUnitario,
          valor: valorUnitario
        });
      setEditingItemId(null);
      await reloadItems(metaId);
      await reloadMetas();
    } catch (err: any) { setItemError(err?.response?.data?.error || 'Erro ao salvar item'); }
  };

  const handleDeleteItem = async (metaId: string, itemId: string) => {
    if (!confirm('Excluir este item?')) return;
    try { await itemService.remove(itemId); await reloadItems(metaId); await reloadMetas(); }
    catch { setItemError('Erro ao excluir item'); }
  };

  // -- Derived ---------------------------------------------------------------
  const getMetaTotal = (meta: Meta) => {
    const items = meta.id ? itemsByMeta[meta.id] : undefined;
    if (!items) return 0;
    return roundMoney(items.reduce((sum, item) => sum + Number(item.valor || 0), 0));
  };

  const totalMetas = metas.reduce((s, m) => s + getMetaTotal(m), 0);
  const statusColor = STATUS_COLOR[(plan?.status) || planoForm.status] || STATUS_COLOR.RASCUNHO;
  const planTitle = plan?.titulo || 'Plano de Trabalho';
  const rawPlanDescricao = plan?.descricao || planoForm.descricao || '';
  const planDescricao = (() => {
    if (!rawPlanDescricao || !planTitle) return rawPlanDescricao;

    const escapedTitle = planTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return rawPlanDescricao
      .replace(new RegExp(`\\s*-?\\s*${escapedTitle}`, 'gi'), '')
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+([,.;:])/g, '$1')
      .trim();
  })();
  const linkedEmenda =
    emendas.find(em => em.id === (plan?.emendaId || planoForm.emendaId)) ||
    null;
  const emendasDisponiveis = useMemo(() => {
    const currentEmendaId = plan?.emendaId || planoForm.emendaId;
    if (!currentEmendaId) return emendas;
    if (emendas.some((em: any) => em.id === currentEmendaId)) return emendas;
    return [
      {
        id: currentEmendaId,
        officialCode: plan?.emendaCodigo || currentEmendaId,
        value: plan?.emendaValor ?? null,
        description: plan?.descricao || '',
      },
      ...emendas,
    ];
  }, [emendas, plan?.emendaId, plan?.emendaCodigo, plan?.emendaValor, plan?.descricao, planoForm.emendaId]);
  const planCode =
    plan?.emendaCodigo ||
    plan?.emenda?.officialCode ||
    plan?.emenda?.codigo ||
    linkedEmenda?.officialCode ||
    linkedEmenda?.codigo ||
    linkedEmenda?.id ||
    plan?.emendaId ||
    planoForm.emendaId ||
    'Sem emenda vinculada';
  const planPeriodo = formatPeriodo(plan?.periodoInicio, plan?.periodoFim);
  const planModalidade = MODALIDADE_LABEL[plan?.modalidade || ''] || plan?.modalidade || 'Não informada';
  const planBanco = plan?.banco || planoForm.banco || 'Não informado';
  const planAgencia = plan?.agencia || planoForm.agencia || 'Não informada';
  const planConta = plan?.contaBancaria
    ? formatContaDisplay(String(plan.contaBancaria))
    : (planoForm.contaBancaria || 'Não informada');
  const currentBankQuery = bankFilter || planoForm.banco || '';
  const filteredBanks = availableBanks.filter(b => {
    const normalizedQuery = normalizeBankSearch(currentBankQuery);
    const normalizedName = normalizeBankSearch(b.name);
    return !normalizedQuery || normalizedName.includes(normalizedQuery) || String(b.code).includes(normalizedQuery);
  }).slice(0, 200);
  const displayInstituicaoNome =
    plan?.instituicao?.razaoSocial ||
    plan?.instituicao?.nomeFantasia ||
    plan?.emenda?.institution?.razaoSocial ||
    plan?.emenda?.institution?.nomeFantasia ||
    linkedEmenda?.institutionName ||
    instituicoes.find(inst => inst.id === (plan?.instituicaoId || selectedInstituicaoId || instituicaoIdParam))?.razaoSocial ||
    instituicoes.find(inst => inst.id === (plan?.instituicaoId || selectedInstituicaoId || instituicaoIdParam))?.nomeFantasia ||
    instituicaoNome ||
    'Não informada';
  const highlightedPlanDescricao = (() => {
    if (!planDescricao) return null;
    if (!displayInstituicaoNome || displayInstituicaoNome === 'Não informada') return planDescricao;

    const escapedName = displayInstituicaoNome.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = planDescricao.split(new RegExp(`(${escapedName})`, 'gi'));

    if (parts.length === 1) return planDescricao;

    return parts.map((part, index) =>
      part.toLowerCase() === displayInstituicaoNome.toLowerCase() ? (
        <strong key={`${part}-${index}`} className="font-semibold text-slate-800">
          {part}
        </strong>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      ),
    );
  })();
  const dirigentesPrioritarios = useMemo(() => prioritizeDirigentes(dirigentes), [dirigentes]);
  const dirigentePreferencial = useMemo(
    () => getPreferredDirigente(dirigentesPrioritarios, plan?.dirigenteId || planoForm.dirigenteId),
    [dirigentesPrioritarios, plan?.dirigenteId, planoForm.dirigenteId],
  );
  const dirigenteNome =
    dirigentePreferencial?.nomeCompleto ||
    'Não informado';
  const etapaAtual = plan?.etapaAtual || '';
  const statusAtual = plan?.status || planoForm.status;
  // WORKFLOW_DECISION_PLANO nunca é checada no backend (permissão morta — ver
  // task-157) e existe só como heurística de UI para decidir se mostra os
  // botões de decisão genéricos. A autorização real de cada transição vive em
  // @RolesAllowed por endpoint em PlanoTrabalhoResource (aprovar/devolver/
  // reprovar/etc.) MAIS validarPermissaoEtapa no service (checa vínculo, não
  // só papel) — então esconder o botão aqui nunca abre um buraco.
  //
  // Na etapa PROCURADORIA_JURIDICA, validarPermissaoEtapa (PlanoTrabalhoService
  // L260-263) autoriza o backend a aceitar POST /aprovar e /reprovar do
  // procurador JURIDICO designado (plan.procuradorJuridicoId), não só ADMIN —
  // e o próprio aprovar() exige que a análise jurídica estruturada já tenha
  // encaminhamentoFinal preenchido antes de aceitar (PlanoTrabalhoService
  // L1343-1349). A UI de "Aprovar"/"Reprovar" da etapa (mais abaixo, dentro
  // do bloco etapaAtual === 'PROCURADORIA_JURIDICA') já foi construída com
  // esse fluxo em mente — o botão "Aprovar" já nasce disabled até a análise
  // estar completa. Isolamos esse caso aqui para não alterar o comportamento
  // de nenhuma outra etapa/papel.
  const isProcuradorJuridicoDesignado =
    user?.role === 'JURIDICO' &&
    plan?.procuradorJuridicoId != null &&
    String(user?.id) === String(plan.procuradorJuridicoId);
  const canUseWorkflowDecisionButtons =
    (hasPermission(Permission.WORKFLOW_DECISION_PLANO) && (etapaAtual !== 'PROCURADORIA_JURIDICA' || user?.role === 'ADMIN')) ||
    (etapaAtual === 'PROCURADORIA_JURIDICA' && isProcuradorJuridicoDesignado);
  const canManageComissao = isConvenios && ['CONVENIOS_ANALISE', 'ORCAMENTO', 'COMISSAO_SELECAO'].includes(etapaAtual || '');
  const comissaoAtualLabel = plan?.comissaoNumero
    ? `Comissão ${plan.comissaoNumero} - Portaria ${plan.comissaoNumeroPortaria || '—'}/${plan.comissaoAnoPortaria || '—'}`
    : 'Não informada';
  const contagemPareceresComissao = etapaAtual === 'COMISSAO_SELECAO'
    ? `${parecerResumo?.pareceresConcluidos || 0}/${parecerResumo?.totalMembros || 0} pareceres`
    : null;
  const etapaLabel =
    statusAtual === 'RASCUNHO'
      ? 'Criação pela instituição'
      : statusAtual === 'AGUARDANDO_ASSINATURA'
        ? 'Assinatura do plano pela instituição'
      : statusAtual === 'PENDENTE_RETIFICACAO'
        ? 'Retificação solicitada pela Procuradoria'
      : statusAtual === 'DEVOLVIDO_CORRECAO'
        ? (etapaAtual === 'ORCAMENTO' ? 'Correção pelo Orçamento' : 'Correção pela instituição')
      : (statusAtual === 'REPROVADO' && etapaAtual === 'ORCAMENTO')
        ? 'Reprovado — aguardando Orçamento'
        : ETAPA_LABEL[etapaAtual] || 'Criação pela instituição';
  const etapaLabelDetalhada = contagemPareceresComissao
    ? `${etapaLabel} · ${contagemPareceresComissao}`
    : etapaLabel;
  const workflowDescricao =
    statusAtual === 'RASCUNHO'
      ? 'O plano ainda está sendo montado pela instituição e pode ser ajustado antes do envio.'
      : statusAtual === 'AGUARDANDO_ASSINATURA'
        ? "A edição do plano está travada. Baixe o plano sem marca d'água, assine e anexe o PDF para solicitar aprovação."
      : statusAtual === 'PENDENTE_RETIFICACAO'
        ? `Retificação solicitada${etapaAtual ? ` — ao retificar, o plano voltará para ${ETAPA_LABEL[etapaAtual] || etapaAtual}` : ''}${plan?.motivoPendencia ? `: ${plan.motivoPendencia}` : '.'}`
      : statusAtual === 'DEVOLVIDO_CORRECAO'
        ? etapaAtual === 'ORCAMENTO'
          ? `O plano retornou para a etapa de Orçamento${plan?.motivoPendencia ? `: ${plan.motivoPendencia}` : '.'}`
          : `O plano voltou para ajustes da instituição${plan?.motivoPendencia ? `: ${plan.motivoPendencia}` : '.'}`
        : statusAtual === 'EM_ANALISE'
          ? etapaAtual === 'CONVENIOS_MINUTA'
            ? 'Anexe a minuta do termo de fomento assinada e o parecer de análise do Departamento de Convênios para avançar para a PGM.'
            : 'O plano está seguindo o fluxo sequencial de análise e aprovação.'
          : statusAtual === 'APROVADO'
            ? 'A etapa atual foi aprovada e o plano avançou para a próxima análise do fluxo.'
          : statusAtual === 'CONCLUIDO'
            ? 'O fluxo de convênios foi concluído e o plano está formalizado.'
            : statusAtual === 'REPROVADO'
              ? etapaAtual === 'ORCAMENTO'
                ? `O plano foi reprovado e aguarda a confirmação do Orçamento para devolução da emenda${plan?.motivoPendencia ? `: ${plan.motivoPendencia}` : '.'}`
                : `O plano foi reprovado${plan?.motivoPendencia ? `: ${plan.motivoPendencia}` : '.'}`
              : 'Acompanhe por aqui a etapa atual, exigências e próximos passos do plano.';
  const requiredAnexo = ETAPA_ANEXO_OBRIGATORIO[etapaAtual];
  const hasRequiredAnexo = requiredAnexo ? anexos.some(a => a.etapa === etapaAtual && a.tipoDocumento === requiredAnexo.tipo) : false;
  // Retroactive upload: users with UPLOAD_RETRO_ANEXO permission can still upload the CONVENIOS_ANALISE required doc for plans that have already advanced
  const ETAPAS_APOS_CONVENIOS_ANALISE = ['GESTOR_PARCERIA', 'CONVENIOS_MINUTA', 'PROCURADORIA_JURIDICA', 'GESTOR_JURIDICO_REVISAO', 'CONVENIOS_FORMALIZACAO', 'CONCLUIDO'];
  const needsRetroAnexo = hasPermission(Permission.UPLOAD_RETRO_ANEXO) && ETAPAS_APOS_CONVENIOS_ANALISE.includes(etapaAtual)
    && !['REPROVADO', 'FECHADO'].includes(statusAtual || '')
    && !anexos.some(a => a.etapa === 'CONVENIOS_ANALISE' && a.tipoDocumento === 'PLANO_TRABALHO_ASSINADO_PREFEITO');
  const ETAPAS_APOS_CONVENIOS_MINUTA = ['PROCURADORIA_JURIDICA', 'GESTOR_JURIDICO_REVISAO', 'CONVENIOS_FORMALIZACAO', 'CONCLUIDO'];
  const needsRetroParecerAnexo = hasPermission(Permission.UPLOAD_RETRO_ANEXO) && ETAPAS_APOS_CONVENIOS_MINUTA.includes(etapaAtual)
    && !['REPROVADO', 'FECHADO'].includes(statusAtual || '')
    && !anexos.some(a => a.etapa === 'CONVENIOS_MINUTA' && a.tipoDocumento === 'PARECER_ANALISE_CONVENIOS');
  const requiredAnexoSecundario = ETAPA_ANEXO_SECUNDARIO[etapaAtual];
  const hasRequiredAnexoSecundario = requiredAnexoSecundario ? anexos.some(a => a.etapa === etapaAtual && a.tipoDocumento === requiredAnexoSecundario.tipo) : false;
  const anexosEtapaAtual = anexos.filter(a => a.etapa === etapaAtual);
  const isWorkflowStage = statusAtual === 'EM_ANALISE' || statusAtual === 'APROVADO'
    || (statusAtual === 'DEVOLVIDO_CORRECAO' && etapaAtual === 'ORCAMENTO')
    || (statusAtual === 'REPROVADO' && etapaAtual === 'ORCAMENTO');
  const ETAPAS_PERMITEM_REPROVACAO = ['CONVENIOS_ANALISE', 'GESTOR_PARCERIA', 'PROCURADORIA_JURIDICA'];
  const canReprovarEtapa = Boolean(etapaAtual) && ETAPAS_PERMITEM_REPROVACAO.includes(etapaAtual);
  const canEditAsConvenios = (user?.role === 'CONVENIOS' || user?.role === 'ADMIN')
    && (statusAtual === 'PENDENTE_RETIFICACAO' || !isWorkflowStage)
    && !['AGUARDANDO_ASSINATURA', 'CONCLUIDO', 'FECHADO'].includes(plan?.status || '');
  const hasDiligenciaAbertaPlano = diligenciasPlano.some(d => d.status !== 'RESOLVIDA');
  const canEdit = canEditAsConvenios || (!isPlanoReadOnlyRole
    && (!isWorkflowStage || (isOperador && hasDiligenciaAbertaPlano))
    && !['AGUARDANDO_ASSINATURA', 'CONCLUIDO', 'FECHADO'].includes(plan?.status || ''));
  // Emenda Direta: o Operador/Secretaria dono do plano informa o gestor da parceria nesta etapa
  // (o setor de Convênios está fora do fluxo). O backend valida o vínculo com a instituição.
  const isDonoDoPlano = Boolean(plan?.instituicaoId) && instituicoes.some(i => i.id === plan?.instituicaoId);
  // Secretaria pessoa jurídica (perfil SECRETARIA + CNPJ) é quem gerencia as Emendas Diretas.
  // Só na Direta e na etapa do Gestor: Convênios/Admin OU a secretaria dona do plano.
  // O fluxo Indireta não é afetado (condição exige isEmendaDiretaAtual()).
  const canVincularGestorDireta = isEmendaDiretaAtual()
    && etapaAtual === 'GESTOR_PARCERIA'
    && (isConvenios || (isOperadorSecretaria && isDonoDoPlano));
  const canConcluirParaAssinatura = Boolean(id)
    && ['RASCUNHO', 'DEVOLVIDO_CORRECAO', 'REPROVADO'].includes(statusAtual || '')
    && !(statusAtual === 'DEVOLVIDO_CORRECAO' && etapaAtual === 'ORCAMENTO')
    && !(statusAtual === 'REPROVADO' && etapaAtual === 'ORCAMENTO');
  const canSendForApproval = Boolean(id) && statusAtual === 'AGUARDANDO_ASSINATURA';
  const planEmendaValor = toMoneyNumber(
    plan?.emendaValor ??
    plan?.emenda?.value ??
    linkedEmenda?.value ??
    0
  );
  const totalCronograma = useMemo(() => {
    const savedValuesByMonth = new Map(cronogramaExecucaoMeses.map((item) => [item.mes, Number(item.valor || 0)]));
    return cronogramaExecucaoPlanejado.reduce((sum, row) => sum + (savedValuesByMonth.get(row.mes) || 0), 0);
  }, [cronogramaExecucaoMeses, cronogramaExecucaoPlanejado]);
  const targetCronogramaValue = totalMetas > 0 ? totalMetas : Number(plan?.valor ?? 0);
  const cronogramaDiff = totalCronograma - targetCronogramaValue;
  const contrapartidaBase = totalMetas;
  const contrapartidaReferencia = planEmendaValor > 0 ? planEmendaValor : Number(plan?.valor ?? 0);
  const needsContrapartida = contrapartidaReferencia > 0 && contrapartidaBase > contrapartidaReferencia;
  const diferencaContrapartida = needsContrapartida ? contrapartidaBase - contrapartidaReferencia : 0;

  // -- Progress checklist (reactive from state) ---------------------------
  type ChecklistItem = {
    key: string;
    label: string;
    done: boolean;
    loading?: boolean;
    onNavigate?: () => void;
  };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const checklist = useMemo((): ChecklistItem[] => {
    if (!plan) return [];

    const firstFilledValue = (...values: Array<string | undefined | null>) =>
      values.find(value => typeof value === 'string' && value.trim())?.trim() || '';
    const firstTruthyValue = <T,>(...values: Array<T | undefined | null>) =>
      values.find(value => Boolean(value));

    const tituloAtual = firstFilledValue(plan.titulo, planoForm.titulo, planTitle);
    const periodoInicioAtual = firstTruthyValue(plan.periodoInicio, planoForm.periodoInicio);
    const periodoFimAtual = firstTruthyValue(plan.periodoFim, planoForm.periodoFim);
    const modalidadeAtual = firstFilledValue(plan.modalidade, planoForm.modalidade);
    const objetoAtual = firstFilledValue(plan.objetoParceria, planoForm.objetoParceria);
    const descricaoRealidadeAtual = firstFilledValue(plan.descricaoRealidade, planoForm.descricaoRealidade);
    const formaExecucaoAtual = firstFilledValue(plan.formaExecucao, planoForm.formaExecucao);
    const bancoAtual = firstFilledValue(plan.banco, bankCode, planoForm.banco, planBanco !== 'Não informado' ? planBanco : '');
    const agenciaAtual = firstFilledValue(plan.agencia, planoForm.agencia, planAgencia !== 'Não informada' ? planAgencia : '');
    const contaAtual = String(
      firstFilledValue(
        plan.contaBancaria,
        planoForm.contaBancaria,
        planConta !== 'Não informada' ? planConta : '',
      ),
    ).replace(/\D/g, '').trim();

    // 1. Dados cadastrais
    const hasDados = Boolean(
      tituloAtual &&
      periodoInicioAtual && periodoFimAtual &&
      modalidadeAtual &&
      objetoAtual &&
      descricaoRealidadeAtual &&
      formaExecucaoAtual
    );
    // 2. Conta bancária + comprovante
    const hasContaBancaria = Boolean(bancoAtual && agenciaAtual && contaAtual);
    const hasComprovante = anexos.some(a => a.tipoDocumento === 'COMPROVANTE_CONTA_ZERADA');

    // 3. Metas
    const hasMeta = metas.length >= 1;

    // 4. Items per meta
    const allMetasHaveItems = hasMeta && metas.every(m => (itemsByMeta[m.id!]?.length ?? 0) >= 1);
    const itemsLoading = hasMeta && !progressLoaded && metas.some(m => itemsByMeta[m.id!] === undefined);

    // 5. Indicadores
    const hasIndicador = indicadores.length >= 1;

    // 6. Orcamentos per meta
    const allMetasHaveOrcamento = hasMeta && metas.every(m => (orcamentosByMeta[m.id!]?.length ?? 0) >= 1);
    const orcamentosLoading = hasMeta && !progressLoaded && metas.some(m => orcamentosByMeta[m.id!] === undefined);

    // 7. Parcelas summing to emenda value
    const parcelasSum = parcelas.reduce((s, p) => s + toMoneyNumber(p.valor), 0);
    const parcelasOk = parcelasLoaded && parcelas.length > 0 && Math.abs(parcelasSum - planEmendaValor) < 0.01;

    // 8. Contrapartida (when totalMetas > emendaValor)
    const needsContrapartida = planEmendaValor > 0 && totalMetas > 0 && totalMetas > planEmendaValor;
    const diferencaContrapartida = needsContrapartida ? totalMetas - planEmendaValor : 0;
    const hasContrapartida = anexos.some(a => a.tipoDocumento === 'CONTRAPARTIDA');

    const items: ChecklistItem[] = [
      {
        key: 'dados',
        label: 'Dados cadastrais completos (título, período, objeto, descrição da realidade, forma de execução)',
        done: hasDados,
        onNavigate: () => {
          headerNavigationTargetRef.current = 'dados';
          setIsEditingHeader(true);
        },
      },
      {
        key: 'conta',
        // Emenda Direta: conta bancaria opcional — item nao bloqueia o envio.
        label: isEmendaDiretaAtual()
          ? 'Conta bancária (opcional na Emenda Direta)'
          : 'Conta bancária cadastrada e comprovante de conta zerada anexado',
        done: isEmendaDiretaAtual() ? true : (hasContaBancaria && hasComprovante),
        onNavigate: () => {
          headerNavigationTargetRef.current = 'conta';
          setIsEditingHeader(true);
        },
      },
      {
        key: 'metas',
        label: 'Ao menos uma meta cadastrada',
        done: hasMeta,
        onNavigate: () => { setActiveSection('estrutura'); setActiveTab('metas'); },
      },
      {
        key: 'items',
        label: 'Cada meta com ao menos um item',
        done: allMetasHaveItems,
        loading: itemsLoading,
        onNavigate: () => { setActiveSection('estrutura'); setActiveTab('metas'); },
      },
      {
        key: 'indicadores',
        label: 'Ao menos um indicador cadastrado',
        done: hasIndicador,
        loading: !indicadoresLoaded && !progressLoaded,
        onNavigate: () => { setActiveSection('estrutura'); setActiveTab('indicadores'); },
      },
      {
        key: 'orcamento',
        label: 'Orçamento de cada meta (mínimo 1)',
        done: allMetasHaveOrcamento,
        loading: orcamentosLoading,
        onNavigate: () => { setActiveSection('estrutura'); setActiveTab('metas'); },
      },
      {
        key: 'desembolso',
        label: `Parcelas de desembolso somando ${planEmendaValor > 0 ? currencyFmt.format(planEmendaValor) : 'o valor da emenda'}`,
        done: parcelasOk,
        loading: !parcelasLoaded && !progressLoaded,
        onNavigate: () => { setActiveSection('estrutura'); setActiveTab('desembolso'); },
      },
      {
        key: 'cronograma',
        label: 'Cronograma de execução preenchido (ao menos um mês com valor)',
        done: cronogramaExecucaoMeses.some(m => Number(m.valor || 0) > 0),
        loading: !cronogramaExecucaoMesesLoaded && !progressLoaded,
        onNavigate: () => { setActiveSection('estrutura'); setActiveTab('cronograma'); },
      },
      ...(needsContrapartida ? [{
        key: 'contrapartida',
        label: `Contrapartida de ${currencyFmt.format(diferencaContrapartida)} anexada (valor do plano excede a emenda)`,
        done: hasContrapartida,
        onNavigate: () => { setActiveSection('estrutura'); },
      }] : []),
      ...(statusAtual === 'AGUARDANDO_ASSINATURA' ? [{
        key: 'plano-assinado',
        label: 'Plano de trabalho assinado pelo diretor da instituição anexado',
        done: anexos.some(a => a.tipoDocumento === 'PLANO_ASSINADO_PRESIDENTE'),
        onNavigate: () => { setActiveSection('geral'); },
      }] : []),
    ];
    return items;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, planoForm, bankCode, metas, itemsByMeta, indicadores, indicadoresLoaded, parcelas, parcelasLoaded, orcamentosByMeta, anexos, planEmendaValor, progressLoaded, cronogramaExecucaoMeses, cronogramaExecucaoMesesLoaded]);

  const pendingChecklistItems = checklist.filter(c => !c.done && !c.loading);
  const hasPending = pendingChecklistItems.length > 0;
  const doneCount = checklist.filter(c => c.done).length;
  const currentComissao = comissoes.find(c => c.id === plan?.comissaoId);
  const currentComissaoMembros = currentComissao?.membros?.length ?? null;
  type WorkflowHint = { key: string; label: string; sublabel?: string; tone?: 'amber' | 'blue' | 'emerald'; onNavigate?: () => void };
  const currentWorkflowKey =
    statusAtual === 'CONCLUIDO'
      ? 'CONCLUIDO'
      : statusAtual === 'RASCUNHO'
        ? 'INSTITUICAO'
        : statusAtual === 'AGUARDANDO_ASSINATURA'
          ? 'ASSINATURA'
          : etapaAtual === 'ORCAMENTO'
            ? 'ORCAMENTO'
            : statusAtual === 'DEVOLVIDO_CORRECAO'
              ? 'INSTITUICAO'
              : (etapaAtual || 'INSTITUICAO');
  const isDevolvido_ORCAMENTO = statusAtual === 'DEVOLVIDO_CORRECAO' && etapaAtual === 'ORCAMENTO';
  const isReprovado_ORCAMENTO = statusAtual === 'REPROVADO' && etapaAtual === 'ORCAMENTO';
  const workflowTimeline = isEmendaDiretaAtual()
    // Emenda Direta: esteira simplificada — Instituição → Assinatura → Gestor da Parceria → Concluído.
    ? [
        { key: 'INSTITUICAO', label: 'Instituição' },
        { key: 'ASSINATURA', label: 'Assinatura do plano' },
        { key: 'GESTOR_PARCERIA', label: ETAPA_LABEL.GESTOR_PARCERIA },
        { key: 'CONCLUIDO', label: 'Concluído' },
      ]
    : [
    { key: 'INSTITUICAO', label: 'Instituição' },
    { key: 'ASSINATURA', label: 'Assinatura do plano' },
    { key: 'CONVENIOS_ANALISE', label: ETAPA_LABEL.CONVENIOS_ANALISE },
    { key: 'GESTOR_PARCERIA', label: ETAPA_LABEL.GESTOR_PARCERIA },
    // Quando devolvido/reprovado com passagem pela comissão: COMISSAO_SELECAO antes de ORCAMENTO
    ...(isDevolvido_ORCAMENTO || isReprovado_ORCAMENTO
      ? [
          { key: 'COMISSAO_SELECAO', label: ETAPA_LABEL.COMISSAO_SELECAO },
          { key: 'ORCAMENTO', label: ETAPA_LABEL.ORCAMENTO },
        ]
      : [
          ...(currentWorkflowKey === 'ORCAMENTO' ? [{ key: 'ORCAMENTO', label: ETAPA_LABEL.ORCAMENTO }] : []),
          { key: 'COMISSAO_SELECAO', label: ETAPA_LABEL.COMISSAO_SELECAO },
          { key: 'CONVENIOS_MINUTA', label: ETAPA_LABEL.CONVENIOS_MINUTA },
          { key: 'PROCURADORIA_JURIDICA', label: ETAPA_LABEL.PROCURADORIA_JURIDICA },
          { key: 'GESTOR_JURIDICO_REVISAO', label: ETAPA_LABEL.GESTOR_JURIDICO_REVISAO },
          { key: 'CONVENIOS_FORMALIZACAO', label: ETAPA_LABEL.CONVENIOS_FORMALIZACAO },
          { key: 'CONCLUIDO', label: 'Concluído' },
        ]
    ),
  ];
  const currentWorkflowIndex = workflowTimeline.findIndex(step => step.key === currentWorkflowKey);
  const isWorkflowFinished = currentWorkflowKey === 'CONCLUIDO' || etapaAtual === 'CONCLUIDO' || statusAtual === 'CONCLUIDO';
  // Há algum documento obrigatório da etapa ainda não anexado?
  const hasPendingRequiredDoc =
    (Boolean(requiredAnexo) && !hasRequiredAnexo)
    || (Boolean(requiredAnexoSecundario) && isConvenios && !hasRequiredAnexoSecundario);
  // A gaveta de documentos é renderizada para etapas de workflow ativas.
  const docsDrawerRendered = !isWorkflowFinished && !(isOperador && isWorkflowStage);
  // Reseta o gatilho de auto-abertura sempre que a etapa muda.
  useEffect(() => { setDocsAutoOpened(false); }, [etapaAtual]);
  // Abre a gaveta automaticamente quando há documento obrigatório pendente —
  // uma vez por etapa, e nunca reabre se o usuário já a fechou manualmente.
  useEffect(() => {
    if (docsDrawerRendered && !anexosLoading && hasPendingRequiredDoc && !docsAutoOpened) {
      setDocsDrawerOpen(true);
      setDocsAutoOpened(true);
    }
  }, [docsDrawerRendered, anexosLoading, hasPendingRequiredDoc, docsAutoOpened]);
  const workflowPendingHints = useMemo((): WorkflowHint[] => {
    const hints: WorkflowHint[] = [];

    if (currentWorkflowKey === 'INSTITUICAO') {
      return pendingChecklistItems.map(item => ({
        key: item.key,
        label: item.label,
        tone: 'amber',
        onNavigate: item.onNavigate,
      }));
    }

    if (currentWorkflowKey === 'ASSINATURA') {
      const hasPlanoAssinado = anexos.some(a => a.tipoDocumento === 'PLANO_ASSINADO_PRESIDENTE');
      return [{
        key: 'plano-assinado',
        label: hasPlanoAssinado
          ? 'Plano assinado anexado. Solicite a aprovação para enviar ao Convênios.'
          : 'Anexe o plano assinado pelo diretor da instituição para solicitar aprovação.',
        tone: hasPlanoAssinado ? 'emerald' : 'amber',
        onNavigate: () => { setActiveSection('geral'); },
      }];
    }

    if (currentWorkflowKey === 'CONVENIOS_ANALISE') {
      if (!(plan?.gestorId || plan?.gestorNome)) {
        hints.push({
          key: 'gestor',
          label: 'Vincular o gestor da parceria responsável por este plano.',
          tone: 'amber',
        });
      }
      if (requiredAnexo && !hasRequiredAnexo) {
        hints.push({
          key: 'anexo-prefeito',
          label: `Anexar o documento obrigatório desta etapa: ${requiredAnexo.label}.`,
          tone: 'amber',
        });
      }
      if (!plan?.prefeitoAprovadoEm) {
        hints.push({
          key: 'prefeito',
          label: 'Faça o upload do plano assinado pelo Prefeito na aba Documentos para registrar a aprovação.',
          tone: 'amber',
        });
      }
      if (hints.length === 0) {
        hints.push({
          key: 'aprovar-convenios',
          label: 'Etapa pronta para aprovação e envio ao gestor da parceria.',
          tone: 'emerald',
        });
      }
      return hints;
    }

    if (currentWorkflowKey === 'GESTOR_PARCERIA') {
      // Emenda Direta: o gestor é vinculado nesta etapa (Convênios foi pulado) e, ao aprovar,
      // o plano é concluído — sem comissão de seleção.
      const emendaDireta = isEmendaDiretaAtual();
      if (emendaDireta && !(plan?.gestorId || plan?.gestorNome)) {
        hints.push({
          key: 'gestor-vincular',
          label: 'Informe o gestor da parceria (fiscal): o setor de Convênios vincula o gestor, que então aprova esta etapa.',
          tone: 'amber',
        });
      }
      if (!plan?.parecerTecnico) {
        hints.push({
          key: 'parecer-tecnico',
          label: 'Registrar o parecer técnico do gestor da parceria.',
          tone: 'amber',
        });
      }
      if (!emendaDireta) {
        if (!plan?.comissaoId) {
          hints.push({
            key: 'comissao',
            label: 'Vincular a comissão de seleção que fará os pareceres coletivos.',
            tone: 'amber',
          });
        } else if (currentComissaoMembros != null && currentComissaoMembros <= 0) {
          hints.push({
            key: 'comissao-membros',
            label: 'A comissao vinculada precisa ter ao menos 1 membro para iniciar os pareceres.',
            tone: 'amber',
          });
        }
      }
      if (hints.length === 0) {
        hints.push({
          key: 'aprovar-gestor',
          label: emendaDireta
            ? 'Etapa pronta para aprovação — ao aprovar, o plano é concluído.'
            : 'Etapa pronta para aprovação e encaminhamento à comissão.',
          tone: 'emerald',
        });
      }
      return hints;
    }

    if (currentWorkflowKey === 'ORCAMENTO') {
      if (plan?.motivoPendencia) {
        hints.push({
          key: 'motivo-orcamento',
          label: `Revisar o retorno da comissão: ${plan.motivoPendencia}`,
          tone: 'amber',
        });
      }
      hints.push({
        key: 'reenviar-orcamento',
        label: 'Depois dos ajustes, reenviar o plano para nova rodada da comissão.',
        tone: hints.length > 0 ? 'blue' : 'amber',
      });
      return hints;
    }

    if (currentWorkflowKey === 'COMISSAO_SELECAO') {
      const membrosPendentes = parecerResumo?.membros?.filter(membro => membro.statusParecer === 'PENDENTE') || [];
      const meuParecerPendente = membrosPendentes.some(membro => membro.membroLogado);
      if (meuParecerPendente) {
        hints.push({
          key: 'meu-parecer',
          label: 'Registrar o seu parecer individual nesta comissão.',
          tone: 'amber',
        });
      }
      if (membrosPendentes.length > (meuParecerPendente ? 1 : 0)) {
        hints.push({
          key: 'outros-pareceres',
          label: `Aguardar os demais pareceres pendentes (${membrosPendentes.length} de ${parecerResumo?.totalMembros || 0}).`,
          tone: 'blue',
        });
      }
      if (!parecerResumo?.ataAnexada) {
        hints.push({
          key: 'ata',
          label: 'Baixar o modelo de ata, preencher, assinar e anexar o seu documento antes de registrar o parecer.',
          tone: 'amber',
        });
      }
      if (hints.length === 0) {
        hints.push({
          key: 'aprovar-comissao',
          label: 'Aprovar esta etapa para enviar ao Convênios para minuta do termo de fomento.',
          tone: 'emerald',
        });
      }
      return hints;
    }

    if (currentWorkflowKey === 'CONVENIOS_MINUTA') {
      if (requiredAnexo && !hasRequiredAnexo) {
        hints.push({
          key: 'anexo-minuta',
          label: `Anexar o documento obrigatório desta etapa: ${requiredAnexo.label}.`,
          tone: 'amber',
        });
      }
      if (requiredAnexoSecundario && !hasRequiredAnexoSecundario) {
        hints.push({
          key: 'anexo-parecer-convenios',
          label: `Anexar o documento obrigatório desta etapa: ${requiredAnexoSecundario.label}.`,
          sublabel: plan?.analiseIniciadaPor ? `Responsável: ${plan.analiseIniciadaPor}` : undefined,
          tone: 'amber',
        });
      }
      if (hints.length === 0) {
        hints.push({
          key: 'aprovar-minuta',
          label: 'Etapa pronta para envio à Procuradoria Geral Municipal.',
          tone: 'emerald',
        });
      }
      return hints;
    }

    if (currentWorkflowKey === 'PROCURADORIA_JURIDICA') {
      hints.push({
        key: 'aprovar-juridico',
        label: 'Definir o encaminhamento jurídico e concluir a análise da PGM.',
        tone: 'emerald',
      });
      return hints;
    }

    if (currentWorkflowKey === 'GESTOR_JURIDICO_REVISAO') {
      hints.push({
        key: 'revisar-juridico',
        label: 'Revise o parecer jurídico inserido pela PGM e avance para a formalização do termo.',
        tone: 'emerald',
      });
      return hints;
    }

    if (currentWorkflowKey === 'CONVENIOS_FORMALIZACAO') {
      if (requiredAnexo && !hasRequiredAnexo) {
        hints.push({
          key: 'anexo-formalizacao',
          label: `Anexar o documento obrigatório desta etapa: ${requiredAnexo.label}.`,
          tone: 'amber',
        });
      } else {
        hints.push({
          key: 'concluir-formalizacao',
          label: 'Formalização pronta para conclusão do fluxo.',
          tone: 'emerald',
        });
      }
      return hints;
    }

    if (currentWorkflowKey === 'CONCLUIDO') {
      return [{
        key: 'workflow-concluido',
        label: 'Fluxo concluído com sucesso.',
        tone: 'emerald',
      }];
    }

    return hints;
  }, [
    currentWorkflowKey,
    pendingChecklistItems,
    plan?.gestorId,
    plan?.gestorNome,
    plan?.parecerTecnico,
    plan?.parecerJuridico,
    plan?.comissaoId,
    plan?.motivoPendencia,
    currentComissaoMembros,
    parecerResumo,
    requiredAnexo,
    hasRequiredAnexo,
    requiredAnexoSecundario,
    hasRequiredAnexoSecundario,
    anexos,
  ]);
  const workflowNextStepTitle =
    currentWorkflowKey === 'CONCLUIDO'
      ? 'Status do fluxo'
      : currentWorkflowKey === 'INSTITUICAO'
        ? 'O que falta para enviar ao próximo passo'
        : currentWorkflowKey === 'ASSINATURA'
          ? 'O que falta para solicitar aprovação'
        : 'O que falta para o próximo passo';

  const workflowCardClass = isSaasTheme
    ? 'rounded-2xl border border-teal-100 bg-gradient-to-br from-white to-teal-50/60 p-4 shadow-sm dark:border-teal-900/50 dark:from-slate-900 dark:to-slate-900'
    : 'rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900';
  const workflowPanelClass = isSaasTheme
    ? 'rounded-2xl border border-teal-100 bg-white/90 px-4 py-3 shadow-sm dark:border-teal-900/50 dark:bg-slate-900/80'
    : 'rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/70';
  const workflowAccentClass = isSaasTheme ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const workflowPrimaryButtonClass = isSaasTheme
    ? 'bg-teal-600 text-white hover:bg-teal-700'
    : 'bg-blue-700 text-white hover:bg-blue-800';
  const hasWorkflowDecisionOverride = isAdminUser && canUseWorkflowDecisionButtons;
  const phaseOwnerCanAct = (() => {
    if (!etapaAtual || isWorkflowFinished) return false;

    switch (etapaAtual) {
      case 'CONVENIOS_ANALISE':
      case 'CONVENIOS_MINUTA':
      case 'CONVENIOS_FORMALIZACAO':
        return user?.role === 'CONVENIOS';
      case 'GESTOR_PARCERIA':
        return isGestorOrAdmin;
      case 'ORCAMENTO':
        // Backend (validarPermissaoEtapa) so aceita ORCAMENTO nesta etapa.
        return user?.role === 'ORCAMENTO';
      case 'COMISSAO_SELECAO':
        return user?.role === 'CONVENIOS';
      case 'PROCURADORIA_JURIDICA':
        return isProcuradorJuridicoDesignado;
      case 'GESTOR_JURIDICO_REVISAO':
        return user?.role === 'GESTOR_JURIDICO';
      default:
        return false;
    }
  })();
  const canActOnWorkflowStage = canUseWorkflowDecisionButtons && (hasWorkflowDecisionOverride || phaseOwnerCanAct);
  const canUploadRequiredAnexo = Boolean(requiredAnexo) && (
    hasWorkflowDecisionOverride
    || (requiredAnexo?.tipo === 'ATA_COMISSAO' ? (isPresidenteDaComissao || user?.role === 'CONVENIOS') : phaseOwnerCanAct)
  );
  const canUploadRequiredAnexoSecundario = Boolean(requiredAnexoSecundario) && (hasWorkflowDecisionOverride || user?.role === 'CONVENIOS');
  // Visivel a partir do momento em que o plano chega na PGM (editavel apenas la dentro,
  // mas consultavel em modo leitura nas etapas seguintes: revisao do gestor juridico,
  // formalizacao e conclusao).
  const canOpenJuridicalAnalysis = ['PROCURADORIA_JURIDICA', 'GESTOR_JURIDICO_REVISAO', 'CONVENIOS_FORMALIZACAO', 'CONCLUIDO'].includes(etapaAtual || '')
    && ['ADMIN', 'JURIDICO', 'JURIDICO_AUXILIAR', 'GESTOR_JURIDICO'].includes(user?.role || '');
  const workflowVisibleAnexos = anexosEtapaAtual.length > 0
    ? anexosEtapaAtual
    : anexos.filter(anexo =>
      (currentWorkflowKey === 'INSTITUICAO' && ['ENVIO', 'INSTITUICAO', 'RASCUNHO'].includes(anexo.etapa))
      || anexo.etapa === currentWorkflowKey
    );
  const visibleWorkflowPendingHints = workflowPendingHints.slice(0, 1);
  const workflowActiveIndex = currentWorkflowIndex >= 0 ? currentWorkflowIndex : 0;
  const workflowIsReturned = statusAtual === 'DEVOLVIDO_CORRECAO' || statusAtual === 'REPROVADO';
  const workflowStepOwner = (key: string) => {
    switch (key) {
      case 'INSTITUICAO':
      case 'ASSINATURA':
        return 'INSTITUIÇÃO';
      case 'CONVENIOS_ANALISE':
      case 'CONVENIOS_MINUTA':
      case 'CONVENIOS_FORMALIZACAO':
        return 'CONVÊNIOS';
      case 'GESTOR_PARCERIA':
        return 'GESTOR';
      case 'COMISSAO_SELECAO':
        return 'COMISSÃO';
      case 'PROCURADORIA_JURIDICA':
        return 'PGM';
      case 'GESTOR_JURIDICO_REVISAO':
        return 'JURÍDICO';
      case 'ORCAMENTO':
        return 'ORÇAMENTO';
      default:
        return '—';
    }
  };
  const workflowStepShortLabel = (key: string, label: string) => {
    switch (key) {
      case 'CONVENIOS_ANALISE':
        return 'Convênios';
      case 'GESTOR_PARCERIA':
        return 'Gestor';
      case 'COMISSAO_SELECAO':
        return 'Comissão';
      case 'CONVENIOS_MINUTA':
        return 'Minuta';
      case 'PROCURADORIA_JURIDICA':
        return 'PGM';
      case 'GESTOR_JURIDICO_REVISAO':
        return 'Revisão jurídica';
      case 'CONVENIOS_FORMALIZACAO':
        return 'Formalização';
      case 'ASSINATURA':
        return 'Assinatura';
      default:
        return label;
    }
  };

  const summaryCards = [
    { label: 'Valor do Plano', value: planEmendaValor > 0 ? currencyFmt.format(planEmendaValor) : (plan?.valor ? currencyFmt.format(plan.valor) : '—'), tone: 'blue' as const },
    { label: 'Total em Metas', value: currencyFmt.format(totalMetas), tone: 'violet' as const },
    { label: 'Metas Cadastradas', value: String(metas.length), tone: 'emerald' as const },
  ];
  const aprovacaoGestor = historicoWorkflow.find(
    item => item.acao === 'APROVAR' && item.etapa === 'GESTOR_PARCERIA'
  );
  const timelineHistoricoPlano = [...historicoWorkflow].sort(
    (a, b) => new Date(b.dataHora || 0).getTime() - new Date(a.dataHora || 0).getTime()
  );
  const handleDownloadZip = async () => {
    if (!id) return;
    setDownloadingZip(true);
    try {
      const blob = await planoService.downloadTodosDocumentosZip(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plano-${id.substring(0, 8)}-documentos.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      let message = 'Não foi possível gerar o ZIP dos documentos.';
      const responseData = err?.response?.data;
      if (responseData instanceof Blob) {
        const errorText = await responseData.text();
        if (errorText) {
          try {
            const parsed = JSON.parse(errorText);
            message = parsed?.error || message;
          } catch {
            message = errorText;
          }
        }
      } else if (responseData?.error) {
        message = responseData.error;
      }
      setToast({ message, type: 'error' });
    } finally {
      setDownloadingZip(false);
    }
  };
  const showGestorResumo = statusAtual === 'CONCLUIDO' && Boolean(aprovacaoGestor || plan?.parecerTecnico);
  const isFocusedJuridicoReview = focusTarget === 'juridico-review' && etapaAtual === 'PROCURADORIA_JURIDICA';

  useEffect(() => {
    if (!pageLoading && isFocusedJuridicoReview && workflowFocusRef.current) {
      workflowFocusRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pageLoading, isFocusedJuridicoReview]);

  // -- Loading state ---------------------------------------------------------
  useEffect(() => {
    if (!isEditingHeader || !headerNavigationTargetRef.current) return;

    const target = headerNavigationTargetRef.current;
    const timer = setTimeout(() => {
      if (target === 'conta') {
        contaInputRef.current?.focus();
      } else {
        periodoInicioInputRef.current?.focus();
      }
      headerNavigationTargetRef.current = null;
    }, 120);

    return () => clearTimeout(timer);
  }, [isEditingHeader]);

  useEffect(() => {
    setActiveSection(resolveInitialSection(searchParams.get('section')));
  }, [searchParams]);

    // -- XLSX export helper ----------------------------------------------------
    const exportAplicacaoXlsx = () => {
    const rows: Array<Array<string | number>> = [];
    rows.push(['Meta','Item','Descricao','Unidade','Quantidade','Valor Unitario','Valor Total']);
    let grandTotal = 0;
    aplicacao.forEach((m:any) => {
      rows.push([m.metaTitulo || m.metaId,'','','','','','']);
      (m.items||[]).forEach((it:any) => {
        const r = [
          '',
          it.itemTitulo || '',
          it.descricao || '',
          it.unidade || '',
          it.quantidade || '',
          it.valorUnitario || '',
          it.valorTotal || it.valor || ''
        ];
        rows.push(r);
      });
      rows.push(['Subtotal','','','','','', m.totalMeta || 0]);
      grandTotal += Number(m.totalMeta || 0);
    });
    rows.push(['TOTAL GERAL','','','','','', grandTotal]);
    const blob = createXlsxBlob(rows, 'Aplicacao');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `aplicacao_plano_${id}.xlsx`; a.click();
    URL.revokeObjectURL(url);
    };

    // -- Download Desembolso XLSX ---------------------------------------------
    const downloadDesembolso = async () => {
    if (!id) return;
    try {
      setDesembolsoLoading(true);
      const blob = await planoService.downloadDesembolsoXlsx(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `desembolso_plano_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error('Erro ao baixar XLSX de desembolso', e);
      setToast({ message: e?.response?.data?.error || 'Erro ao baixar XLSX de desembolso', type: 'error' });
    } finally {
      setDesembolsoLoading(false);
    }
    };

  // -- Effects -----------------------------------------------------------------
  useEffect(() => {
    const loadAplicacao = async () => {
      if (activeTab !== 'aplicacao' || !id) return;
      try {
        setAplicacaoLoading(true);
        const res = await planoService.getAplicacao(id);
        setAplicacao(res);
      } catch (e) {
        setAplicacao([]);
      } finally { setAplicacaoLoading(false); }
    };
    loadAplicacao();
  }, [activeTab, id]);

  useEffect(() => {
    const loadDesembolso = async () => {
      if (activeTab !== 'desembolso' || !id) return;
      try {
        setDesembolsoLoading(true);
        const res = await planoService.getDesembolso(id);
        setDesembolso(res);
      } catch (e) { setDesembolso(null); } finally { setDesembolsoLoading(false); }
    };
    loadDesembolso();
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab !== 'indicadores' || !id || indicadoresLoaded) return;
    indicadorPlanoService.listByPlano(id)
      .then(data => { setIndicadores(data); setIndicadoresLoaded(true); })
      .catch(() => setIndicadorError('Erro ao carregar indicadores'));
  }, [activeTab, id, indicadoresLoaded]);

  useEffect(() => {
    if ((activeTab !== 'desembolso' && activeTab !== 'cronograma') || !id || parcelasLoaded) return;
    parcelaDesembolsoService.listByPlano(id)
      .then(data => { setParcelas(data); setParcelasLoaded(true); })
      .catch(() => setParcelaError('Erro ao carregar parcelas'));
  }, [activeTab, id, parcelasLoaded]);

  useEffect(() => {
    if (!id || cronogramaExecucaoMesesLoaded) return;
    cronogramaExecucaoMesService.listByPlano(id)
      .then((data) => {
        setCronogramaExecucaoMeses(data);
        setCronogramaExecucaoMesesLoaded(true);
      })
      .catch(() => setCronogramaExecucaoError('Erro ao carregar valores do cronograma de execução.'));
  }, [id, cronogramaExecucaoMesesLoaded]);

  useEffect(() => {
    const nextDrafts = Object.fromEntries(
      cronogramaExecucaoMeses.map((item) => [item.mes, item.valor != null ? formatCurrency(String(item.valor)) : ''])
    );
    setCronogramaExecucaoDrafts(nextDrafts);
  }, [cronogramaExecucaoMeses]);

  // -- Eager load all checklist data when plan is in RASCUNHO --------------
  useEffect(() => {
    if (!id || pageLoading || progressLoaded) return;
    const run = async () => {
      const loads: Promise<void>[] = [];
      if (!indicadoresLoaded) {
        loads.push(
          indicadorPlanoService.listByPlano(id)
            .then(ind => { setIndicadores(ind); setIndicadoresLoaded(true); })
            .catch(() => {})
        );
      }
      if (!parcelasLoaded) {
        loads.push(
          parcelaDesembolsoService.listByPlano(id)
            .then(parc => { setParcelas(parc); setParcelasLoaded(true); })
            .catch(() => {})
        );
      }
      metas.forEach(m => {
        if (!m.id) return;
        if (itemsByMeta[m.id] === undefined) {
          loads.push(
            itemService.listByMeta(m.id)
              .then(items => setItemsByMeta(prev => ({ ...prev, [m.id!]: items })))
              .catch(() => {})
          );
        }
        if (orcamentosByMeta[m.id] === undefined) {
          loads.push(
            orcamentoMetaService.listByMeta(m.id)
              .then(orc => setOrcamentosByMeta(prev => ({ ...prev, [m.id!]: orc })))
              .catch(() => {})
          );
        }
      });
      await Promise.allSettled(loads);
      setProgressLoaded(true);
    };
    run();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, statusAtual, pageLoading, metas.length, progressLoaded]);

  const handleCreateIndicador = async () => {
    setIndicadorError(null);
    if (!id) return setIndicadorError('Não foi possível identificar o plano de trabalho. Recarregue a página.');
    if (!newIndicador.unidade.trim()) return setIndicadorError('Unidade é obrigatória.');
    const qtd = Number(newIndicador.quantidade);
    if (!(qtd > 0)) return setIndicadorError('Quantidade deve ser um número positivo.');
    try {
      setSavingIndicador(true);
      const novo = await indicadorPlanoService.create({
        planoTrabalhoId: id!,
        unidade: newIndicador.unidade.trim(),
        quantidade: qtd,
        descricao: newIndicador.descricao.trim() || undefined,
      });
      setIndicadores(prev => [...prev, novo]);
      setNewIndicador({ unidade: '', quantidade: '', descricao: '' });
      setShowNewIndicador(false);
    } catch (err: any) {
      setIndicadorError(err?.response?.data?.error || 'Erro ao salvar indicador.');
    } finally {
      setSavingIndicador(false);
    }
  };

  const handleUpdateIndicador = async () => {
    setIndicadorError(null);
    if (!id) return setIndicadorError('Não foi possível identificar o plano de trabalho. Recarregue a página.');
    if (!editIndicadorForm.unidade.trim()) return setIndicadorError('Unidade é obrigatória.');
    const qtd = Number(editIndicadorForm.quantidade);
    if (!(qtd > 0)) return setIndicadorError('Quantidade deve ser um número positivo.');
    try {
      setSavingIndicador(true);
      const updated = await indicadorPlanoService.update(editingIndicadorId!, {
        planoTrabalhoId: id!,
        unidade: editIndicadorForm.unidade.trim(),
        quantidade: qtd,
        descricao: editIndicadorForm.descricao.trim() || undefined,
      });
      setIndicadores(prev => prev.map(i => i.id === editingIndicadorId ? updated : i));
      setEditingIndicadorId(null);
    } catch (err: any) {
      setIndicadorError(err?.response?.data?.error || 'Erro ao atualizar indicador.');
    } finally {
      setSavingIndicador(false);
    }
  };

  const handleDeleteIndicador = async (indicadorId: string) => {
    if (!window.confirm('Remover este indicador?')) return;
    try {
      await indicadorPlanoService.delete(indicadorId);
      setIndicadores(prev => prev.filter(i => i.id !== indicadorId));
    } catch { /* silencioso */ }
  };

  // -- Parcelas de desembolso handlers ----------------------------------------
  const handleAddParcela = async () => {
    if (!id) return;
    setParcelaError(null);
    if (!newParcela.mes.match(/^\d{4}-\d{2}$/)) return setParcelaError('Mês inválido. Use o formato AAAA-MM.');
    const val = parseCurrency(newParcela.valor);
    if (!(val > 0)) return setParcelaError('Valor deve ser maior que zero.');
    setSavingParcela(true);
    try {
      const created = await parcelaDesembolsoService.create({ planoTrabalhoId: id, mes: newParcela.mes, valor: val });
      setParcelas(prev => [...prev, created]);
      setNewParcela({ mes: '', valor: '' });
      setShowNewParcela(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Erro ao adicionar parcela.';
      setParcelaError(msg);
    } finally {
      setSavingParcela(false);
    }
  };

  const handleUpdateParcela = async (parcelaId: string) => {
    setParcelaError(null);
    if (!editParcelaForm.mes.match(/^\d{4}-\d{2}$/)) return setParcelaError('Mês inválido. Use o formato AAAA-MM.');
    const val = parseCurrency(editParcelaForm.valor);
    if (!(val > 0)) return setParcelaError('Valor deve ser maior que zero.');
    setSavingParcela(true);
    try {
      const updated = await parcelaDesembolsoService.update(parcelaId, { mes: editParcelaForm.mes, valor: val });
      setParcelas(prev => prev.map(p => p.id === parcelaId ? updated : p));
      setEditingParcelaId(null);
    } catch (err: any) {
      setParcelaError(err?.response?.data?.error || 'Erro ao atualizar parcela.');
    } finally {
      setSavingParcela(false);
    }
  };

  const handleDeleteParcela = async (parcelaId: string) => {
    if (!window.confirm('Remover esta parcela?')) return;
    try {
      await parcelaDesembolsoService.delete(parcelaId);
      setParcelas(prev => prev.filter(p => p.id !== parcelaId));
    } catch { /* silencioso */ }
  };

  const handleCronogramaExecucaoDraftChange = (mes: string, value: string) => {
    setCronogramaExecucaoDrafts((prev) => ({ ...prev, [mes]: sanitizeMoneyDraft(value) }));
  };

  const handleCronogramaExecucaoDraftBlur = async (mes: string) => {
    if (!id) return;
    const formatted = formatMoneyOnBlur(cronogramaExecucaoDrafts[mes] || '');
    setCronogramaExecucaoDrafts((prev) => ({ ...prev, [mes]: formatted }));
    setCronogramaExecucaoError(null);

    const valor = parseCurrency(formatted);
    const existente = cronogramaExecucaoMeses.find((item) => item.mes === mes);

    if (!(valor > 0)) {
      if (existente?.id) {
        try {
          setSavingCronogramaMes(mes);
          await cronogramaExecucaoMesService.delete(existente.id);
          setCronogramaExecucaoMeses((prev) => prev.filter((item) => item.id !== existente.id));
        } catch (err: any) {
          setCronogramaExecucaoError(err?.response?.data?.error || 'Erro ao remover valor do cronograma de execução.');
        } finally {
          setSavingCronogramaMes(null);
        }
      }
      return;
    }

    try {
      setSavingCronogramaMes(mes);
      if (existente?.id) {
        const updated = await cronogramaExecucaoMesService.update(existente.id, {
          planoTrabalhoId: id,
          mes,
          valor,
        });
        setCronogramaExecucaoMeses((prev) => prev.map((item) => item.id === existente.id ? updated : item));
      } else {
        const created = await cronogramaExecucaoMesService.create({
          planoTrabalhoId: id,
          mes,
          valor,
        });
        setCronogramaExecucaoMeses((prev) => [...prev.filter((item) => item.mes !== mes), created].sort((a, b) => a.mes.localeCompare(b.mes)));
      }
    } catch (err: any) {
      setCronogramaExecucaoError(err?.response?.data?.error || 'Erro ao salvar valor do cronograma de execução.');
    } finally {
      setSavingCronogramaMes(null);
    }
  };

  // -- Debugging --------------------------------------------------------------
  useEffect(() => {
    console.log('PlanoFullPage mounted');
    return () => { console.log('PlanoFullPage unmounted'); };
  }, []);

  if (pageLoading) return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm">Carregando...</p>
      </div>
    </div>
  );

  // --------------------------------------------------------------------------
  // CREATE MODE – full-form card
  // --------------------------------------------------------------------------
  if (isCreateMode) {
    return (
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button type="button" onClick={() => navigate(backUrl)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600" title="Voltar">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Novo Plano de Trabalho</h1>
            {instituicaoNome && <p className="text-sm text-gray-500 mt-0.5">{instituicaoNome}</p>}
          </div>
        </div>

        <form onSubmit={handleSavePlan} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-5">
          <GuidanceCard title="Como preencher esta etapa">
            Informe primeiro os dados gerais do plano. Use textos objetivos, explique por que a parceria é necessária e descreva como as ações serão executadas. Depois de salvar, você poderá detalhar metas e itens na etapa seguinte.
          </GuidanceCard>

          {/* Institution selector — shown when not pre-selected via URL param */}
          {!instituicaoIdParam && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instituição <span className="text-red-500">*</span>
              </label>
              {instituicoes.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                  Nenhuma instituição vinculada. <button type="button" className="underline font-medium" onClick={() => navigate('/dashboard/instituicoes')}>Vincular agora</button>
                </div>
              ) : (
                <select
                  value={selectedInstituicaoId}
                  onChange={e => {
                    const instituicaoSelecionadaId = e.target.value;
                    setSelectedInstituicaoId(instituicaoSelecionadaId);
                    // reload emendas for the newly selected institution
                    if (instituicaoSelecionadaId) {
                      Promise.all([
                        emendaService.listWithDetails(),
                        planoService.listByInstituicao(instituicaoSelecionadaId),
                      ]).then(([all, planosDaInstituicao]) => {
                        const emendaIdsComPlano = new Set(planosDaInstituicao.map(plano => plano.emendaId).filter(Boolean));
                        setEmendas(all.filter((em: any) =>
                          em.institutionId === instituicaoSelecionadaId
                          && isEmendaElegivelParaNovoPlano(em)
                          && !emendaIdsComPlano.has(em.id),
                        ));
                        setPlanoForm(f => ({ ...f, emendaId: '' }));
                      }).catch(() => {});
                      institutionService.getByInstitutionId(instituicaoSelecionadaId)
                        .then(inst => setInstituicaoNome(inst?.razaoSocial || inst?.nomeFantasia || ''))
                        .catch(() => {});
                    }
                  }}
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${planErrors.instituicao ? 'border-red-400' : 'border-gray-300'}`}
                >
                  <option value="">— Selecione uma instituição —</option>
                  {instituicoes.map(i => (
                    <option key={i.id} value={i.id}>{i.razaoSocial || i.nomeFantasia || i.cnpj}</option>
                  ))}
                </select>
              )}
              {planErrors.instituicao && <p className="text-xs text-red-500 mt-1">{planErrors.instituicao}</p>}
            </div>
          )}
          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título <span className="text-red-500">*</span></label>
            <input type="text" value={planoForm.titulo}
              onChange={e => setPlanoForm(f => ({ ...f, titulo: toUpperText(e.target.value) }))}
              maxLength={200} placeholder="Ex: Manutenção das atividades socioassistenciais 2026"
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${planErrors.titulo ? 'border-red-400' : 'border-gray-300'}`} />
            {planErrors.titulo && <p className="text-xs text-red-500 mt-1">{planErrors.titulo}</p>}
            <p className="text-xs text-gray-400 mt-1">{planoForm.titulo.length}/200 caracteres</p>
            <p className="text-xs text-gray-500 mt-1">Use um nome direto, que identifique a finalidade do projeto e facilite a análise do plano.</p>
          </div>

          {/* Emenda vinculada */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emenda Vinculada</label>
            <select value={planoForm.emendaId}
              onChange={e => {
                const emId = e.target.value;
                // set the emendaId immediately
                setPlanoForm(f => ({ ...f, emendaId: emId }));
                // if no emenda selected, do nothing further (user may type value manually later)
                if (!emId) return;
                // find the emenda in the loaded list
                const em = emendasDisponiveis.find((x: any) => x.id === emId);
                if (!em) return;
                // Prefill description and value from emenda; description remains editable.
                // Value will be locked (readOnly) when it comes from emenda (em.value != null).
                setPlanoForm(f => ({
                  ...f,
                  descricao: em.description ? String(em.description) : f.descricao,
                  valor: (em.value != null) ? formatCurrency(String(em.value)) : f.valor,
                  emendaId: emId,
                }));
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">— Nenhuma (plano sem emenda) —</option>
              {emendasDisponiveis.map((em: any) => (
                <option key={em.id} value={em.id}>
                  {em.officialCode || em.id}
                  {em.value != null ? ` — ${currencyFmt.format(Number(em.value))}` : ''}
                  {em.description ? ` — ${String(em.description).substring(0, 50)}...` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Cada emenda pode ter apenas 1 plano.</p>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea value={planoForm.descricao}
              onChange={e => setPlanoForm(f => ({ ...f, descricao: toUpperText(e.target.value) }))}
              rows={3} placeholder="Resumo do plano, público atendido e finalidade da parceria"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-gray-500 mt-1">Faça um resumo executivo para situar rapidamente quem vai analisar o plano.</p>
          </div>

          {/* RF03 - Objeto da parceria */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Objeto da parceria <span className="text-red-500">*</span></label>
            <textarea value={planoForm.objetoParceria}
              onChange={e => setPlanoForm(f => ({ ...f, objetoParceria: toUpperText(e.target.value) }))}
              rows={4} placeholder="Descreva com clareza o que será realizado e relacione a ação às metas do plano"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-gray-500 mt-1">Explique a ação principal da parceria. Foque no resultado esperado, não apenas na compra ou contratação.</p>
          </div>

          {/* RF03 - Descrição da realidade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição da realidade <span className="text-red-500">*</span></label>
            <textarea value={planoForm.descricaoRealidade}
              onChange={e => setPlanoForm(f => ({ ...f, descricaoRealidade: toUpperText(e.target.value) }))}
              rows={4} placeholder="Apresente o diagnóstico da demanda, do público atendido e do cenário local"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-gray-500 mt-1">Descreva o problema ou necessidade que justifica a parceria e mostre por que essa execução é necessária.</p>
          </div>

          {/* RF03 - Forma de execução */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Forma de execução <span className="text-red-500">*</span></label>
            <textarea value={planoForm.formaExecucao}
              onChange={e => setPlanoForm(f => ({ ...f, formaExecucao: toUpperText(e.target.value) }))}
              rows={4} placeholder="Explique como as atividades serão executadas, por quem e em qual período"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-gray-500 mt-1">Detalhe etapas, metodologia e forma de execução. Isso deve conversar com metas, itens, períodos e valores cadastrados.</p>
          </div>

          {/* Valor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor do Plano</label>
            {/* lock the value input when emenda provides a value */}
            <input type="text" value={planoForm.valor}
              onChange={e => setPlanoForm(f => ({ ...f, valor: e.target.value }))}
              onBlur={handleValueBlur} placeholder="R$ 0,00"
              readOnly={Boolean(planoForm.emendaId && emendasDisponiveis.find((em: any) => em.id === planoForm.emendaId && em.value != null))}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${planoForm.emendaId && emendasDisponiveis.find((em: any) => em.id === planoForm.emendaId && em.value != null) ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'}`} />
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <span className="font-medium text-slate-800">Status inicial:</span> {STATUS_LABEL.RASCUNHO}
          </div>

          {/* Dados bancários (edit) */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Dados Bancários
              {isEmendaDiretaAtual() && <span className="ml-1 font-normal text-gray-400">(opcional na Emenda Direta)</span>}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Banco</label>
                <div className="relative">
                  <input
                    type="text"
                    value={bankFilter || planoForm.banco || ''}
                    onChange={e => { setBankFilter(e.target.value); setShowBankDropdown(true); }}
                    onFocus={() => setShowBankDropdown(true)}
                    onKeyDown={(e) => {
                      const list = filteredBanks;
                      if (!list.length) return;
                      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedBankIndex(i => Math.min(i + 1, list.length - 1)); }
                      else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedBankIndex(i => Math.max(i - 1, 0)); }
                      else if (e.key === 'Enter') { e.preventDefault(); const sel = list[highlightedBankIndex >= 0 ? highlightedBankIndex : 0]; if (sel) { setPlanoForm(f => ({ ...f, banco: sel.name })); setBankCode(sel.code); setShowBankDropdown(false); setBankFilter(''); } }
                    }}
                    onBlur={() => setTimeout(() => { setShowBankDropdown(false); setBankFilter(''); setHighlightedBankIndex(-1); }, 120)}
                    className="w-full border rounded px-3 py-2 text-sm pr-8"
                    placeholder="Digite nome ou código do banco" />
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {(bankCode || planoForm.banco) ? (
                      <button type="button" onClick={() => { setPlanoForm(f => ({ ...f, banco: '' })); setBankCode(''); setBankFilter(''); }} className="text-gray-400 hover:text-gray-700 px-2 py-1">?</button>
                    ) : null}
                  </div>
                  {showBankDropdown && (
                    <div className="absolute z-40 left-0 right-0 bg-white border rounded mt-1 max-h-48 overflow-auto shadow">
                      {filteredBanks.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhum banco disponível</div>
                      ) : (
                        filteredBanks.map((b, idx) => (
                          <button key={b.code} type="button" onMouseDown={() => { setPlanoForm(f => ({ ...f, banco: b.name })); setBankCode(b.code); setBankFilter(''); setShowBankDropdown(false); }} onMouseEnter={() => setHighlightedBankIndex(idx)} className={`w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex items-center justify-between ${idx === highlightedBankIndex ? 'bg-blue-50' : ''}`}>
                            <span>{b.code} — {b.name}</span>
                            {b.code === bankCode && <span className="text-blue-600 text-xs">Selecionado</span>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Agência</label>
                <input type="text" value={planoForm.agencia || ''} onChange={e => setPlanoForm(f => ({ ...f, agencia: toUpperText(e.target.value) }))} className="w-full border rounded px-3 py-2 text-sm" placeholder="Agência" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Conta Bancária (20 dígitos)</label>
                <input ref={contaInputRef} type="text" value={planoForm.contaBancaria || ''} onChange={e => handleContaChange(e.target.value)} onFocus={handleContaFocus} onBlur={handleContaBlur} maxLength={24} className="w-full border rounded px-3 py-2 text-sm" placeholder="0000 0000 0000 0000 0000" />
              </div>
            </div>
          </div>

          {/* Período */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Período de Início</label>
              <input type="month" value={planoForm.periodoInicio}
                onChange={e => setPlanoForm(f => ({ ...f, periodoInicio: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <p className="mt-1 text-xs text-gray-500">Informe um período compatível com a vigência da parceria e com as metas que serão executadas.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Período de Fim</label>
              <input type="month" value={planoForm.periodoFim}
                onChange={e => setPlanoForm(f => ({ ...f, periodoFim: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <p className="mt-1 text-xs text-gray-500">As datas dos itens deverão respeitar esse intervalo para gerar cronograma e desembolso corretamente.</p>
            </div>
          </div>

          {/* Modalidade */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Modalidade</label>
            <select value={planoForm.modalidade}
              onChange={e => setPlanoForm(f => ({ ...f, modalidade: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">— Selecione —</option>
              <option value="TERMO_COLABORACAO">Termo de Colaboração</option>
              <option value="TERMO_FOMENTO">Termo de Fomento</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Selecione a modalidade correta conforme a natureza da parceria definida pelo órgão concedente.</p>
          </div>

          {/* Alerte user about auto-saving in create mode */}
          {isCreateMode && (
            <div className="p-4 text-sm rounded-lg bg-blue-50 text-blue-800 border border-blue-200">
              Este é um plano novo. As alterações serão salvas automaticamente ao sair deste formulário.
            </div>
          )}

          {planErrors.general && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{planErrors.general}</p>}

          {validationMessages.length > 0 && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {validationMessages.join(' | ')}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={() => navigate(backUrl)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={savingPlan}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {savingPlan ? 'Criando...' : 'Criar Plano e Continuar'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // VIEW/EDIT MODE – unified page
  // --------------------------------------------------------------------------
  if (!plan) return <div className="p-6 text-gray-500">Plano não encontrado</div>;

  return (
    <div className="w-full space-y-6">

      {/* -- HEADER CARD ----------------------------------------------------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
        <div className="flex flex-col gap-4">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                onClick={() => navigate(backUrl)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
              >
                <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 20 20" fill="none">
                  <path d="M11.5 4.5L6 10l5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Voltar para planos
              </button>
              <div className="flex flex-wrap items-center gap-2">
                {id && (
                  <>
                    <NovaSolicitacaoButton
                      tituloTela="Plano de Trabalho"
                      className="inline-flex items-center gap-1.5 rounded-full bg-blue-700 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-blue-800 disabled:opacity-60"
                    />
                    <button
                      type="button"
                      onClick={() => setTimelineDrawerOpen(true)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Linha do tempo
                    </button>
                    {plan?.emendaId && (
                      <button
                        type="button"
                        onClick={() => setEmendaPanelOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        Ver emenda
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        openAuthenticatedTab(`${BASENAME}/plano/impressao/${id}?autoprint=1`);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-900 bg-slate-900 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-slate-800"
                    >
                      <svg aria-hidden="true" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9V3h12v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M6 17H4a2 2 0 01-2-2v-4a2 2 0 012-2h16a2 2 0 012 2v4a2 2 0 01-2 2h-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 14h10v7H7z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Imprimir plano
                    </button>
                    {isConvenios && (
                      <a
                        href={`${BASENAME}/plano/impressao/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => {
                          event.preventDefault();
                          openAuthenticatedTab(`${BASENAME}/plano/impressao/${id}`);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <svg aria-hidden="true" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                          <path d="M12 3v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                        Baixar plano
                      </a>
                    )}
                  </>
                )}
                {canEdit && !isEditingHeader && (
                  <button
                    type="button"
                    onClick={() => setIsEditingHeader(true)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 hover:border-blue-300"
                  >
                    <svg aria-hidden="true" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none">
                      <path d="M14.5 2.5a2.121 2.121 0 013 3L6 17l-4 1 1-4L14.5 2.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Editar dados
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusColor}`}>
                  {STATUS_LABEL[plan.status] || plan.status}
                </span>
                {(etapaAtual || statusAtual === 'RASCUNHO' || statusAtual === 'DEVOLVIDO_CORRECAO' || statusAtual === 'AGUARDANDO_ASSINATURA') && (
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                    Etapa atual: {etapaLabelDetalhada}
                  </span>
                )}
                {plan.numeroProtocolo && (
                  <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
                    Protocolo: {plan.numeroProtocolo}
                  </span>
                )}
                {!canEdit && (
                  <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                    Edição bloqueada
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                  {displayInstituicaoNome}
                </p>
                {Array.isArray(plan?.instituicaoAreasAtuacao) && plan.instituicaoAreasAtuacao.length > 0 && (
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    <span className="font-semibold">Área de atuação:</span> {plan.instituicaoAreasAtuacao.join(' · ')}
                  </p>
                )}
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">{planTitle}</h1>
                {planDescricao && (
                  <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-600 dark:text-slate-300">{highlightedPlanDescricao}</p>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {Array.isArray(plan?.documentosVencidos) && plan.documentosVencidos.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 dark:border-red-900/60 dark:bg-red-950/30">
          <div className="flex items-start gap-3">
            <span className="text-2xl leading-none">⚠️</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-red-900 dark:text-red-200">
                Documentos vencidos — é preciso renovar antes de enviar o plano para análise
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-red-800 dark:text-red-300">
                {plan.documentosVencidos.map((pendencia: string) => (
                  <li key={pendencia}>{pendencia}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div
        ref={workflowFocusRef}
        className={`${workflowCardClass} ${
          isFocusedJuridicoReview ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Tramitação do plano</span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Etapa {workflowActiveIndex + 1} de {workflowTimeline.length} ·{' '}
              {workflowIsReturned ? (
                <b className="font-semibold text-red-600 dark:text-red-400">devolvida em {workflowTimeline[workflowActiveIndex]?.label || etapaLabel}</b>
              ) : (
                <>em <b className={`font-semibold ${isSaasTheme ? 'text-teal-700 dark:text-teal-300' : 'text-amber-700 dark:text-amber-400'}`}>{workflowTimeline[workflowActiveIndex]?.label || etapaLabel}</b></>
              )}
            </span>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto px-1 pb-7 pt-4">
          <ol
            className="grid min-w-[60rem]"
            style={{ gridTemplateColumns: `repeat(${workflowTimeline.length}, minmax(0, 1fr))` }}
          >
            {workflowTimeline.map((step, index) => {
              const isDone = index < workflowActiveIndex;
              const isActive = index === workflowActiveIndex;
              const returnedHere = isActive && workflowIsReturned;
              const activeTone = isSaasTheme ? 'teal' : 'amber';
              return (
                <li key={step.key} className="relative flex flex-col items-center text-center">
                  {index > 0 && (
                    <div
                      className={`absolute top-[11px] h-0.5 ${
                        isDone
                          ? 'bg-sky-400'
                          : isActive
                            ? returnedHere
                              ? 'bg-gradient-to-r from-sky-500 to-red-400'
                              : isSaasTheme
                                ? 'bg-gradient-to-r from-sky-500 to-teal-400'
                                : 'bg-gradient-to-r from-sky-500 to-amber-400'
                            : 'bg-slate-200 dark:bg-slate-700'
                      }`}
                      style={{ left: '-50%', width: '100%' }}
                    />
                  )}
                  <span
                    className={[
                      'relative z-10 flex items-center justify-center rounded-full border-2 font-bold transition-all duration-200',
                      isDone ? 'h-6 w-6 border-sky-600 bg-sky-600 text-[11px] text-white' : '',
                      returnedHere ? 'h-7 w-7 border-red-500 bg-red-500 text-[11px] text-white ring-4 ring-red-300/60 dark:ring-red-500/30' : '',
                      isActive && !returnedHere && activeTone === 'teal' ? 'h-7 w-7 border-teal-500 bg-teal-500 text-[11px] text-white ring-4 ring-teal-300/60 dark:ring-teal-500/30' : '',
                      isActive && !returnedHere && activeTone === 'amber' ? 'h-7 w-7 border-amber-500 bg-amber-500 text-[11px] text-white ring-4 ring-amber-300/60 dark:ring-amber-500/30' : '',
                      !isDone && !isActive ? 'h-6 w-6 border-slate-300 bg-white text-[11px] text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-500' : '',
                    ].join(' ')}
                  >
                    {isDone ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" /> : returnedHere ? '!' : index + 1}
                  </span>
                  <span title={step.label} className={[
                    'mt-2 max-w-[7.25rem] truncate leading-tight',
                    isDone ? 'text-[0.75rem] font-medium text-slate-500 dark:text-slate-500' : '',
                    returnedHere ? 'text-[0.8rem] font-bold text-red-600 dark:text-red-400' : '',
                    isActive && !returnedHere && activeTone === 'teal' ? 'text-[0.8rem] font-bold text-teal-700 dark:text-teal-300' : '',
                    isActive && !returnedHere && activeTone === 'amber' ? 'text-[0.8rem] font-bold text-amber-700 dark:text-amber-300' : '',
                    !isDone && !isActive ? 'text-[0.75rem] font-medium text-slate-400 dark:text-slate-600' : '',
                  ].join(' ')}>
                    {workflowStepShortLabel(step.key, step.label)}
                  </span>
                  <span className={[
                    'mt-0.5 text-[0.62rem] uppercase tracking-[0.06em]',
                    returnedHere ? 'font-semibold text-red-500 dark:text-red-400' : '',
                    isActive && !returnedHere && activeTone === 'teal' ? 'font-semibold text-teal-500 dark:text-teal-400' : '',
                    isActive && !returnedHere && activeTone === 'amber' ? 'font-semibold text-amber-500 dark:text-amber-400' : '',
                    !isActive ? 'text-slate-400 dark:text-slate-600' : '',
                  ].join(' ')}>
                    {workflowStepOwner(step.key)}
                  </span>
                  {isActive && (
                    <span className={`mt-1 h-1 w-1 rounded-full ${returnedHere ? 'bg-red-400' : isSaasTheme ? 'bg-teal-400' : 'bg-amber-400'}`} />
                  )}
                </li>
              );
            })}
          </ol>
        </div>
        {(workflowDescricao || contagemPareceresComissao) && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
            <span>{workflowDescricao}</span>
            {contagemPareceresComissao && <span className="font-semibold text-blue-700 dark:text-blue-300">{contagemPareceresComissao}</span>}
          </div>
        )}
      </div>

      {/* -- PROGRESS CHECKLIST (RASCUNHO only) ------------------------------- */}
      {statusAtual === 'RASCUNHO' && checklist.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {/* Header row */}
          <button
            type="button"
            onClick={() => setChecklistCollapsed(c => !c)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-slate-800">Progresso para envio à análise</span>
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${doneCount === checklist.length ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {doneCount}/{checklist.length} {doneCount === checklist.length ? <>completo <CheckIcon className="w-3 h-3" /></> : 'pendente'}
              </span>
            </div>
            <svg className={`h-4 w-4 text-slate-400 transition-transform ${checklistCollapsed ? '' : 'rotate-180'}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          {/* Progress bar */}
          <div className="h-1 bg-slate-100">
            <div
              className={`h-1 transition-all ${doneCount === checklist.length ? 'bg-emerald-500' : 'bg-amber-400'}`}
              style={{ width: `${Math.round((doneCount / checklist.length) * 100)}%` }}
            />
          </div>
          {/* Checklist items */}
          {!checklistCollapsed && (
            <ul className="divide-y divide-slate-100">
              {checklist.map(item => (
                <li key={item.key} className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {item.loading ? (
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center">
                        <span className="w-3 h-3 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                      </span>
                    ) : item.done ? (
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                        <CheckIcon className="w-3 h-3" />
                      </span>
                    ) : (
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                        <ExclamationCircleIcon className="w-3 h-3" />
                      </span>
                    )}
                    <span className={`text-sm truncate ${item.done ? 'text-slate-500' : item.loading ? 'text-slate-400' : 'text-slate-800'}`}>
                      {item.label}
                    </span>
                  </div>
                  {!item.done && !item.loading && item.onNavigate && (
                    <button
                      type="button"
                      onClick={item.onNavigate}
                      className="flex-shrink-0 ml-3 text-xs text-blue-600 hover:underline whitespace-nowrap"
                    >
                      Ir para →
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {false && canSendForApproval && !isEditingHeader && (() => {
        const planoAssinadoAnexo = anexos.find(a => a.tipoDocumento === 'PLANO_ASSINADO_PRESIDENTE');
        return (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <svg className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 20h10a2 2 0 002-2V6a2 2 0 00-2-2h-3.5a1.5 1.5 0 01-1.2-.6l-.6-.8a1.5 1.5 0 00-1.2-.6H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Plano assinado pelo diretor da instituição</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Baixe o plano de trabalho sem marca d'água, colete a assinatura do diretor da instituição e anexe o PDF assinado antes de solicitar aprovação.
                  </p>
                </div>
              </div>
              {id && (
                <a
                  href={`${BASENAME}/plano/impressao/${id}?autoprint=1`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    event.preventDefault();
                    openAuthenticatedTab(`${BASENAME}/plano/impressao/${id}?autoprint=1`);
                  }}
                  className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  Baixar
                </a>
              )}
            </div>
            {planoAssinadoAnexo ? (
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-sm font-semibold text-emerald-800">Plano assinado enviado</span>
                </div>
                <p className="text-xs text-emerald-700">{planoAssinadoAnexo.nomeOriginal}</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAnexoViewer(planoAssinadoAnexo)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                  >
                    Visualizar
                  </button>
                  <AuthenticatedFileButton
                    action="download"
                    url={`/api${planoAssinadoAnexo.viewUrl}`}
                    fileName={planoAssinadoAnexo.nomeOriginal}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                  >
                    <svg aria-hidden="true" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                      <path d="M12 3v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M5 19h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Baixar
                  </AuthenticatedFileButton>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!id || !window.confirm('Remover o plano assinado? Você poderá enviar um novo em seguida.')) return;
                      try {
                        await planoService.deleteAnexo(id, planoAssinadoAnexo.id);
                        await loadAnexos();
                        setToast({ message: 'Plano assinado removido. Envie um novo arquivo.', type: 'success' });
                      } catch (err: any) {
                        setToast({ message: err?.response?.data?.error || 'Erro ao remover plano assinado', type: 'error' });
                      }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                  >
                    Remover e reenviar
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-blue-200 bg-white p-3 space-y-2">
                <label className="block text-xs font-medium text-blue-900">PDF do plano assinado <span className="text-red-500">*</span></label>
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-blue-300 bg-blue-50 px-4 py-3 hover:bg-blue-100">
                  <svg className="h-5 w-5 shrink-0 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                  <span className="text-sm text-blue-800">
                    {planoAssinadoFile ? planoAssinadoFile.name : 'Clique para selecionar o arquivo PDF assinado'}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    className="hidden"
                    onChange={e => setPlanoAssinadoFile(e.target.files?.[0] || null)}
                  />
                </label>
                <button
                  type="button"
                  onClick={handleUploadPlanoAssinado}
                  disabled={!planoAssinadoFile || uploadingPlanoAssinado}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingPlanoAssinado ? 'Enviando...' : 'Enviar plano assinado'}
                </button>
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleRequestSendForApproval}
                disabled={sendingForApproval || !planoAssinadoAnexo}
                className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Solicitar aprovação
              </button>
            </div>
          </div>
        );
      })()}

      {statusAtual === 'PENDENTE_RETIFICACAO' && !isEditingHeader && !isPlanoReadOnlyRole && (
        <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-2xl leading-none">⚠️</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-orange-900">Retificação solicitada</p>
              {etapaAtual && (
                <p className="text-xs font-medium text-orange-700 mt-0.5">
                  Ao enviar, o plano retornará para: <strong>{ETAPA_LABEL[etapaAtual] || etapaAtual}</strong>
                </p>
              )}
              {plan?.motivoPendencia && (
                <p className="text-sm text-orange-800 mt-1">{plan.motivoPendencia}</p>
              )}
              {!isConvenios && (
                <p className="text-xs text-orange-700 mt-2">
                  Corrija os documentos e/ou o plano de trabalho e clique em "Enviar Retificação" para retornar à análise.
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleEnviarRetificacao}
              disabled={enviandoRetificacao}
              className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {enviandoRetificacao ? 'Enviando…' : 'Retificar'}
            </button>
          </div>
        </div>
      )}

      {!isEditingHeader && (
        <div className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <div className="grid gap-3 lg:grid-cols-3">
            {PLANO_SECTION_OPTIONS.map(section => {
              const active = activeSection === section.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setActiveSection(section.key)}
                  className={`rounded-2xl border px-4 py-4 text-left transition-colors ${
                    active
                      ? 'border-blue-200 bg-blue-50 shadow-sm dark:border-blue-800 dark:bg-blue-950/30'
                      : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      active ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700'
                    }`}>
                      {section.step}
                    </span>
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold ${active ? 'text-blue-900 dark:text-blue-100' : 'text-slate-900 dark:text-slate-100'}`}>{section.label}</p>
                      <p className={`mt-1 text-xs leading-5 ${active ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'}`}>{section.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!isEditingHeader && activeSection === 'geral' && (
        <div className="space-y-5">
            <DetailSection
              eyebrow="Panorama"
              title="Resumo e andamento"
              description="Aqui ficam os números principais do plano, a identificação da parceria e o bloco central do workflow."
            >
            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Resumo do plano</p>
                <div className="mt-3 grid gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Emenda vinculada</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{planCode}</p>
                    {plan?.emendaDestinacao && (
                      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                        <span className="font-semibold">Destinação original:</span> {plan.emendaDestinacao}
                      </p>
                    )}
                    {plan?.emendaDescricao && (
                      <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                        <span className="font-semibold">Texto original:</span> {plan.emendaDescricao}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Valor do Plano</p>
                    <p className="mt-1 text-sm font-semibold text-blue-700 dark:text-blue-300">{planEmendaValor > 0 ? currencyFmt.format(planEmendaValor) : (plan?.valor ? currencyFmt.format(plan.valor) : '—')}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Total em Metas</p>
                    <p className="mt-1 text-sm font-semibold text-violet-700 dark:text-violet-300">{currencyFmt.format(totalMetas)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Metas cadastradas</p>
                    <p className="mt-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300">{metas.length}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Instituição</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{displayInstituicaoNome}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Dirigente</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{dirigenteNome}</p>
                  </div>
                  {/* Emenda Direta não passa por comissão de seleção — campo omitido. */}
                  {!isEmendaDiretaAtual() && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Comissão de seleção</p>
                      <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{comissaoAtualLabel}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Gestor da parceria</p>
                    <p className={`mt-1 text-sm font-semibold ${plan?.gestorNome ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400 italic dark:text-slate-500'}`}>{plan?.gestorNome || 'Não vinculado'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Período</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{planPeriodo}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Modalidade</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{planModalidade}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Banco</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{planBanco}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Agência</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{planAgencia}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">Conta bancária</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{planConta}</p>
                  </div>
                </div>
              </div>

            <div className="hidden rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Timeline</p>
                <h3 className="mt-2 text-lg font-semibold text-slate-900">Histórico do plano de trabalho</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Acompanhe os marcos registrados no workflow, com responsável, data e observações vinculadas.
                </p>
              </div>

              {timelineHistoricoPlano.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                  Nenhum histórico registrado para este plano.
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute bottom-0 left-[11px] top-0 w-px bg-gradient-to-b from-sky-200 via-slate-200 to-slate-100" />
                  <div className="space-y-5">
                    {timelineHistoricoPlano.map(item => {
                      const tone = planoHistoricoTone(item.acao);
                      const responsavel = item.usuarioNome || item.usuario || 'Sistema';
                      const isInicioEtapa = ['CREATE', 'ENVIAR_PARA_APROVACAO', 'APROVAR'].includes(item.acao);
                      return (
                        <div key={item.id} className="relative pl-9">
                          <div className={`absolute left-0 top-5 h-6 w-6 rounded-full border-4 border-white shadow-lg ${tone.dot}`} />
                          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="space-y-2">
                                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${tone.badge}`}>
                                  {planoHistoricoLabel(item.acao)}
                                </span>
                                <p className="text-sm font-semibold text-slate-800">
                                  {item.decisao || (item.etapa ? ETAPA_LABEL[item.etapa] || item.etapa : '') || 'Atualização do workflow'}
                                </p>
                              </div>
                              <p className="text-xs font-medium text-slate-500">{formatTimelineDateTime(item.dataHora)}</p>
                            </div>
                            {item.motivo && (
                              <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-600">{item.motivo}</p>
                            )}
                            {item.parecerTexto && (
                              <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                                <strong>Parecer:</strong> {item.parecerTexto}
                              </div>
                            )}
                            {item.acao === 'JUNTADA_DOCUMENTO' && (item.documentoTipo || item.documentoNome) && (
                              <div className="mt-2 flex items-center gap-2 rounded-xl bg-violet-50 border border-violet-100 px-3 py-2 text-sm text-violet-800">
                                <PaperClipIcon className="h-4 w-4 flex-shrink-0 text-violet-500" />
                                <span>{item.documentoNome || item.documentoTipo}</span>
                              </div>
                            )}
                            <div className="mt-3 text-xs text-slate-500">
                              {isInicioEtapa ? `Iniciado por ${responsavel}` : `Ação por ${responsavel}`}
                              {item.acao !== 'JUNTADA_DOCUMENTO' && item.documentoTipo ? ` · Documento: ${item.documentoTipo}` : ''}
                              {item.acao !== 'JUNTADA_DOCUMENTO' && item.documentoNome ? ` · ${item.documentoNome}` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div
                className={`${workflowCardClass} ${
                  isFocusedJuridicoReview ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Arquivos e ações da etapa</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{etapaLabel}</span>
                    {isConvenios && (plan as any)?.instituicaoId && (
                      <button
                        type="button"
                        onClick={() => setDiligenciaPlanoOpen(true)}
                        className="rounded-lg border border-amber-400 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-800 shadow-sm transition-colors hover:bg-amber-200 dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-200 dark:hover:bg-amber-900/70"
                      >
                        Enviar diligência
                      </button>
                    )}
                    {user?.role === 'CONVENIOS' && isWorkflowStage && statusAtual !== 'PENDENTE_RETIFICACAO' && (
                      <button
                        type="button"
                        onClick={() => setConveniosIniciarRetOpen(true)}
                        className="rounded-lg border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm transition-colors hover:bg-orange-100"
                      >
                        Retificar
                      </button>
                    )}
                  </div>
                </div>
                <div className="hidden items-baseline justify-between gap-4">
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Tramitação do plano</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    Etapa {workflowActiveIndex + 1} de {workflowTimeline.length} ·{' '}
                    {workflowIsReturned ? (
                      <b className="font-semibold text-red-600 dark:text-red-400">devolvida em {workflowTimeline[workflowActiveIndex]?.label || etapaLabel}</b>
                    ) : (
                      <>em <b className={`font-semibold ${isSaasTheme ? 'text-teal-700 dark:text-teal-300' : 'text-amber-700 dark:text-amber-400'}`}>{workflowTimeline[workflowActiveIndex]?.label || etapaLabel}</b></>
                    )}
                  </span>
                </div>
                <div className="mt-4 space-y-3">
                  {isFocusedJuridicoReview && (
                    <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Entrada direta do juridico</p>
                      <p className="mt-1 text-sm text-blue-900">
                        Voce entrou diretamente no ponto de analise da procuradoria. Revise o parecer juridico e registre a decisao desta etapa abaixo.
                      </p>
                    </div>
                  )}
                  <div className="hidden overflow-x-auto px-1 pb-7 pt-4">
                    <ol
                      className="grid min-w-[60rem]"
                      style={{ gridTemplateColumns: `repeat(${workflowTimeline.length}, minmax(0, 1fr))` }}
                    >
                      {workflowTimeline.map((step, index) => {
                        const isDone = index < workflowActiveIndex;
                        const isActive = index === workflowActiveIndex;
                        const returnedHere = isActive && workflowIsReturned;
                        const activeTone = isSaasTheme ? 'teal' : 'amber';
                        return (
                          <li key={step.key} className="relative flex flex-col items-center text-center">
                            {index > 0 && (
                              <div
                                className={`absolute top-[11px] h-0.5 ${
                                  isDone
                                    ? 'bg-sky-400'
                                    : isActive
                                      ? returnedHere
                                        ? 'bg-gradient-to-r from-sky-500 to-red-400'
                                        : isSaasTheme
                                          ? 'bg-gradient-to-r from-sky-500 to-teal-400'
                                          : 'bg-gradient-to-r from-sky-500 to-amber-400'
                                      : 'bg-slate-200 dark:bg-slate-700'
                                }`}
                                style={{ left: '-50%', width: '100%' }}
                              />
                            )}
                            <span
                              className={[
                                'relative z-10 flex items-center justify-center rounded-full border-2 font-bold transition-all duration-200',
                                isDone ? 'h-6 w-6 border-sky-600 bg-sky-600 text-[11px] text-white' : '',
                                returnedHere ? 'h-7 w-7 border-red-500 bg-red-500 text-[11px] text-white ring-4 ring-red-300/60 dark:ring-red-500/30' : '',
                                isActive && !returnedHere && activeTone === 'teal' ? 'h-7 w-7 border-teal-500 bg-teal-500 text-[11px] text-white ring-4 ring-teal-300/60 dark:ring-teal-500/30' : '',
                                isActive && !returnedHere && activeTone === 'amber' ? 'h-7 w-7 border-amber-500 bg-amber-500 text-[11px] text-white ring-4 ring-amber-300/60 dark:ring-amber-500/30' : '',
                                !isDone && !isActive ? 'h-6 w-6 border-slate-300 bg-white text-[11px] text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-500' : '',
                              ].join(' ')}
                            >
                              {isDone ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" /> : returnedHere ? '!' : index + 1}
                            </span>
                            <span title={step.label} className={[
                              'mt-2 max-w-[7.25rem] truncate leading-tight',
                              isDone ? 'text-[0.75rem] font-medium text-slate-500 dark:text-slate-500' : '',
                              returnedHere ? 'text-[0.8rem] font-bold text-red-600 dark:text-red-400' : '',
                              isActive && !returnedHere && activeTone === 'teal' ? 'text-[0.8rem] font-bold text-teal-700 dark:text-teal-300' : '',
                              isActive && !returnedHere && activeTone === 'amber' ? 'text-[0.8rem] font-bold text-amber-700 dark:text-amber-300' : '',
                              !isDone && !isActive ? 'text-[0.75rem] font-medium text-slate-400 dark:text-slate-600' : '',
                            ].join(' ')}>
                              {workflowStepShortLabel(step.key, step.label)}
                            </span>
                            <span className={[
                              'mt-0.5 text-[0.62rem] uppercase tracking-[0.06em]',
                              returnedHere ? 'font-semibold text-red-500 dark:text-red-400' : '',
                              isActive && !returnedHere && activeTone === 'teal' ? 'font-semibold text-teal-500 dark:text-teal-400' : '',
                              isActive && !returnedHere && activeTone === 'amber' ? 'font-semibold text-amber-500 dark:text-amber-400' : '',
                              !isActive ? 'text-slate-400 dark:text-slate-600' : '',
                            ].join(' ')}>
                              {workflowStepOwner(step.key)}
                            </span>
                            {isActive && (
                              <span className={`mt-1 h-1 w-1 rounded-full ${returnedHere ? 'bg-red-400' : isSaasTheme ? 'bg-teal-400' : 'bg-amber-400'}`} />
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                  {(workflowDescricao || contagemPareceresComissao) && (
                    <div className="hidden flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                      <span>{workflowDescricao}</span>
                      {contagemPareceresComissao && <span className="font-semibold text-blue-700 dark:text-blue-300">{contagemPareceresComissao}</span>}
                    </div>
                  )}
                  {isWorkflowStage && ['CONVENIOS_ANALISE', 'GESTOR_PARCERIA', 'CONVENIOS_MINUTA', 'PROCURADORIA_JURIDICA', 'GESTOR_JURIDICO_REVISAO', 'CONVENIOS_FORMALIZACAO'].includes(etapaAtual || '') && (
                    <div className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                      <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Análise:</span>
                      {plan?.analiseIniciadaEm ? (
                        <>
                          <svg className="h-4 w-4 shrink-0 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{plan.analiseIniciadaPor}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">· {new Date(plan.analiseIniciadaEm).toLocaleString('pt-BR')}</span>
                        </>
                      ) : isConvenios && etapaAtual === 'CONVENIOS_ANALISE' ? (
                        <button
                          type="button"
                          onClick={handleIniciarAnalise}
                          disabled={iniciandoAnalise}
                          className="inline-flex items-center gap-1.5 rounded-md bg-slate-700 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-600 dark:hover:bg-slate-500"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {iniciandoAnalise ? 'Iniciando...' : 'Iniciar análise'}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400 italic dark:text-slate-500">Nenhum analista iniciou a análise ainda.</span>
                      )}
                    </div>
                  )}
                  <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-4 shadow-sm dark:border-blue-900/50 dark:bg-blue-950/20">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">Ação atual</p>
                        <p className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">{etapaLabel}</p>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {canConcluirParaAssinatura && (
                          <span title={hasPending ? pendingChecklistItems.map(c => `• ${c.label}`).join('\n') : undefined} className="inline-flex">
                            <button
                              type="button"
                              onClick={hasPending ? () => setShowPendingModal(true) : handleConcluirParaAssinatura}
                              disabled={sendingForApproval}
                              aria-disabled={hasPending}
                              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${hasPending ? 'bg-slate-400 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600'}`}
                            >
                              {sendingForApproval ? 'Concluindo...' : 'Concluir plano'}
                            </button>
                          </span>
                        )}
                        {isAdminUser && (
                          <button
                            type="button"
                            onClick={() => { setAdminFaseStatus(plan?.status || ''); setAdminFaseEtapa(plan?.etapaAtual || ''); setAdminFaseMotivo(''); setShowAdminFaseModal(true); }}
                            className="inline-flex items-center gap-2 rounded-full border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm transition hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-300 dark:hover:bg-orange-950/70"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                            </svg>
                            Editar fase
                          </button>
                        )}
                        <span className={`rounded-full px-4 py-2 text-sm font-semibold shadow-sm ${
                          isWorkflowFinished
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200'
                            : 'bg-blue-600 text-white dark:bg-blue-500'
                        }`}>
                          {isWorkflowFinished ? 'Concluído' : 'Em andamento'}
                        </span>
                      </div>
                    </div>
                    {canVincularGestorDireta && (
                      <div className="mt-4 border-t border-blue-200 pt-4 dark:border-blue-900/60">
                        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm space-y-2 dark:border-slate-700 dark:bg-slate-950">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">Gestor da parceria (fiscal)</p>
                          <p className="text-sm text-gray-700 dark:text-slate-300">
                            Atual: <strong>{plan?.gestorNome || 'Não vinculado'}</strong>
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Informe o CPF de uma pessoa ativa com perfil Gestor. O CNPJ da Secretaria não pode ser vinculado como fiscal.
                          </p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              inputMode="numeric"
                              value={formatCPF(gestorCpf)}
                              onChange={e => setGestorCpf(onlyDigits(e.target.value).slice(0, 11))}
                              maxLength={14}
                              className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                              placeholder="CPF do gestor (pessoa física)"
                            />
                            <button
                              type="button"
                              onClick={() => gestorLocalizadoDireta && handleVincularGestorSelecionado(gestorLocalizadoDireta.id)}
                              disabled={savingGestor || !gestorLocalizadoDireta}
                              className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                            >
                              {savingGestor ? '...' : 'Vincular'}
                            </button>
                          </div>
                          {gestorCpf && !gestorLocalizadoDireta && onlyDigits(gestorCpf).length === 11 && (
                            <p className="text-xs text-blue-700">
                              Nenhum Gestor da Parceria ativo encontrado para esse CPF.
                            </p>
                          )}
                          {gestorLocalizadoDireta && (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                              <p className="text-sm font-medium text-emerald-800">{gestorLocalizadoDireta.nomeCompleto}</p>
                              <p className="text-xs text-emerald-700">
                                {[formatCPF(gestorLocalizadoDireta.documento || ''), gestorLocalizadoDireta.secretaria, gestorLocalizadoDireta.cargoFuncao].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {etapaAtual === 'COMISSAO_SELECAO' && parecerResumo?.membros?.length ? (
                      <div className="mt-3 border-t border-blue-200 pt-3 dark:border-blue-900/60">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Membros da comissão — {parecerResumo.pareceresConcluidos}/{parecerResumo.totalMembros || 0} votaram
                        </p>
                        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                          {parecerResumo.membros.map(m => (
                            <div key={m.membroId} className="flex min-w-0 items-start justify-between gap-3 rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm shadow-sm dark:border-blue-900/40 dark:bg-slate-950">
                              <span className="min-w-0 text-slate-700 dark:text-slate-300">
                                <span className="block truncate font-semibold text-slate-800 dark:text-slate-100">{m.nomeCompleto || 'Membro não identificado'}</span>
                                {m.isPresidente && <span className="mt-0.5 block text-[11px] font-medium text-blue-600 dark:text-blue-300">Presidente</span>}
                              </span>
                              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                m.statusParecer === 'APROVADO' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' :
                                m.statusParecer === 'REPROVADO' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' :
                                'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                              }`}>
                                {m.statusParecer === 'APROVADO' ? 'Aprovado' : m.statusParecer === 'REPROVADO' ? 'Reprovado' : 'Pendente'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {isMembroDaComissao && etapaAtual === 'COMISSAO_SELECAO' && (
                      <div className="mt-3 border-t border-blue-200 pt-3 space-y-3 dark:border-blue-900/60">
                        {parecerResumo?.membroLogado && (
                          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Identificação do membro</p>
                            <p className="mt-1 text-sm font-medium text-slate-800">
                              {parecerResumo.membroLogado.nomeCompleto || user?.name || 'Membro logado'} — Comissão nº {parecerResumo.numeroDaComissao ?? '—'}, nomeado pela Portaria nº {parecerResumo.numeroPortaria ?? '—'}/{parecerResumo.anoPortaria ?? '—'}
                            </p>
                          </div>
                        )}
                        {!parecerResumo?.membros?.some(m => m.membroLogado && m.statusParecer !== 'PENDENTE') ? (
                          <form onSubmit={handleEmitirParecer} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Emitir meu parecer</p>
                            <textarea
                              value={parecerForm.justificativa}
                              onChange={e => setParecerForm(f => ({ ...f, justificativa: toUpperText(e.target.value) }))}
                              rows={3}
                              placeholder="Justificativa (mínimo 20 caracteres)..."
                              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                            <div className="flex justify-center gap-3">
                              <button
                                type="submit"
                                onClick={() => setParecerForm(f => ({ ...f, resultado: 'APROVADO' }))}
                                disabled={parecerForm.justificativa.length < 20 || submittingParecer}
                                className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                              >
                                {submittingParecer && parecerForm.resultado === 'APROVADO' ? 'Enviando...' : 'Aprovar'}
                              </button>
                              <button
                                type="submit"
                                onClick={() => setParecerForm(f => ({ ...f, resultado: 'REPROVADO' }))}
                                disabled={parecerForm.justificativa.length < 20 || submittingParecer}
                                className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                              >
                                {submittingParecer && parecerForm.resultado === 'REPROVADO' ? 'Enviando...' : 'Reprovar'}
                              </button>
                            </div>
                          </form>
                        ) : (
                          <p className="text-xs text-emerald-700 font-medium px-1">Você já emitiu seu parecer para este plano.</p>
                        )}
                      </div>
                    )}
                    {!isEditingHeader && isWorkflowStage && statusAtual === 'REPROVADO' && etapaAtual === 'ORCAMENTO' && ['ADMIN', 'ORCAMENTO'].includes(user?.role || '') && (
                      <div className="mt-4 border-t border-blue-200 pt-4 dark:border-blue-900/60">
                        <button
                          onClick={() => setWorkflowAction('confirmarReprovacao')}
                          className="inline-flex w-full items-center justify-center rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
                        >
                          Confirmar devolução da emenda
                        </button>
                      </div>
                    )}
                    {!isEditingHeader && isWorkflowStage && etapaAtual === 'PROCURADORIA_JURIDICA' && !canActOnWorkflowStage && (
                      <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-300">
                        Esta etapa é conduzida pela <strong>Procuradoria Geral Municipal</strong>. Aguarde a conclusão da análise jurídica.
                      </div>
                    )}
                    {canSendForApproval && !isEditingHeader && (() => {
                      const planoAssinadoAnexo = anexos.find(a => a.tipoDocumento === 'PLANO_ASSINADO_PRESIDENTE' && a.etapa === 'ENVIO');
                      return (
                        <div className="mt-4 border-t border-blue-200 pt-4 space-y-3 dark:border-blue-900/60">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-start gap-3">
                              <svg className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 20h10a2 2 0 002-2V6a2 2 0 00-2-2h-3.5a1.5 1.5 0 01-1.2-.6l-.6-.8a1.5 1.5 0 00-1.2-.6H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <div>
                                <p className="text-sm font-semibold text-slate-800">Plano assinado pelo diretor da instituição</p>
                                <p className="mt-1 text-xs text-slate-500">Baixe o plano, colete a assinatura do diretor e anexe o PDF assinado antes de solicitar aprovação.</p>
                              </div>
                            </div>
                            {id && (
                              <a href={`${BASENAME}/plano/impressao/${id}?autoprint=1`} target="_blank" rel="noopener noreferrer" onClick={(event) => { event.preventDefault(); openAuthenticatedTab(`${BASENAME}/plano/impressao/${id}?autoprint=1`); }} className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                                <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none"><path d="M12 3v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                                Baixar
                              </a>
                            )}
                          </div>
                          {planoAssinadoAnexo ? (
                            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 space-y-2">
                              <div className="flex items-center gap-2">
                                <svg className="h-4 w-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <span className="text-sm font-semibold text-emerald-800">Plano assinado enviado</span>
                              </div>
                              <p className="text-xs text-emerald-700">{planoAssinadoAnexo.nomeOriginal}</p>
                              <div className="flex gap-2">
                                <button type="button" onClick={() => setAnexoViewer(planoAssinadoAnexo)} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50">Visualizar</button>
                                <AuthenticatedFileButton action="download" url={`/api${planoAssinadoAnexo.viewUrl}`} fileName={planoAssinadoAnexo.nomeOriginal} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50">Baixar</AuthenticatedFileButton>
                                <button type="button" onClick={async () => { if (!id || !window.confirm('Remover o plano assinado?')) return; try { await planoService.deleteAnexo(id, planoAssinadoAnexo.id); await loadAnexos(); setToast({ message: 'Plano assinado removido.', type: 'success' }); } catch (err: any) { setToast({ message: err?.response?.data?.error || 'Erro ao remover', type: 'error' }); } }} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">Remover e reenviar</button>
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-lg border border-blue-200 bg-white p-3 space-y-2">
                              <label className="block text-xs font-medium text-blue-900">PDF do plano assinado <span className="text-red-500">*</span></label>
                              <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-blue-300 bg-blue-50 px-4 py-3 hover:bg-blue-100">
                                <svg className="h-5 w-5 shrink-0 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                <span className="text-sm text-blue-800">{planoAssinadoFile ? planoAssinadoFile.name : 'Clique para selecionar o arquivo PDF assinado'}</span>
                                <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={e => setPlanoAssinadoFile(e.target.files?.[0] || null)} />
                              </label>
                              <button type="button" onClick={handleUploadPlanoAssinado} disabled={!planoAssinadoFile || uploadingPlanoAssinado} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {uploadingPlanoAssinado ? 'Enviando...' : 'Enviar plano assinado'}
                              </button>
                            </div>
                          )}
                          <div className="flex justify-end">
                            <button type="button" onClick={handleRequestSendForApproval} disabled={sendingForApproval || !planoAssinadoAnexo} className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50">
                              Solicitar aprovação
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                    {!isEditingHeader && isWorkflowStage && statusAtual !== 'REPROVADO' && canActOnWorkflowStage && !(user?.role !== 'ADMIN' && user?.role !== 'CONVENIOS' && isMembroDaComissao && etapaAtual === 'COMISSAO_SELECAO') && (() => {
                      const abrirJuntada = () => {
                        if (etapaAtual) {
                          setJuntadaEtapa(etapaAtual);
                          const primario = ETAPA_ANEXO_OBRIGATORIO[etapaAtual];
                          const secundario = ETAPA_ANEXO_SECUNDARIO[etapaAtual];
                          const faltaPrimario = primario && !anexos.some(a => a.etapa === etapaAtual && a.tipoDocumento === primario.tipo);
                          const faltaSecundario = secundario && !anexos.some(a => a.etapa === etapaAtual && a.tipoDocumento === secundario.tipo);
                          let tipoSelecionado: string;
                          if (!faltaPrimario && faltaSecundario) {
                            tipoSelecionado = secundario.tipo;
                          } else {
                            tipoSelecionado = primario?.tipo || ETAPA_TIPO_PADRAO[etapaAtual] || '';
                          }
                          setJuntadaTipo(tipoSelecionado);
                          const existente = tipoSelecionado ? anexos.find(a => a.etapa === etapaAtual && a.tipoDocumento === tipoSelecionado) ?? null : null;
                          setJuntadaAnexoExistente(existente);
                        }
                        setJuntadaFaseLocked(true);
                        setShowJuntadaModal(true);
                      };
                      const analiseCompleta = !!(analiseJuridica?.encaminhamentoFinal && analiseJuridica?.numeroParecer && analiseJuridica?.anoParecer);
                      if (etapaAtual === 'PROCURADORIA_JURIDICA') {
                        return (
                          <div className="mt-4 flex flex-col items-center gap-3 border-t border-blue-200 pt-4 dark:border-blue-900/60">
                            <button
                              type="button"
                              onClick={() => navigate(`/dashboard/plano/${plan.id}/analise-juridica`)}
                              className="inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                            >
                              {analiseCompleta ? '✓ Análise jurídica preenchida — editar' : 'Preencher análise jurídica'}
                            </button>
                            <div className="flex flex-wrap justify-center gap-2">
                              <button
                                onClick={() => setWorkflowAction('aprovar')}
                                disabled={!analiseCompleta}
                                title={!analiseCompleta ? 'Preencha a análise jurídica antes de aprovar' : undefined}
                                className="inline-flex min-w-[13rem] items-center justify-center rounded-lg bg-emerald-600 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Aprovar
                              </button>
                              {canReprovarEtapa && (
                                <button
                                  onClick={() => setWorkflowAction('reprovar')}
                                  className="inline-flex min-w-[7rem] items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
                                >
                                  Reprovar
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      }
                      if (etapaAtual === 'GESTOR_PARCERIA') {
                        return (
                          <div className="mt-4 flex flex-col items-center gap-3 border-t border-blue-200 pt-4 dark:border-blue-900/60">
                            <div className="w-full space-y-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Parecer em PDF (obrigatório)</p>
                              {gestorParecerAnexo && (
                                <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 dark:border-violet-800 dark:bg-violet-950/30">
                                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">Arquivo atual</p>
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <button type="button" onClick={handleAbrirGestorParecer} disabled={abrindoGestorParecer} className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:underline dark:text-violet-300 disabled:opacity-50">
                                      <PaperClipIcon className="h-4 w-4 shrink-0" />
                                      <span className="truncate max-w-[22rem]">{abrindoGestorParecer ? 'Abrindo…' : gestorParecerAnexo.nomeOriginal}</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleRemoverGestorParecer}
                                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                    >
                                      Remover
                                    </button>
                                  </div>
                                </div>
                              )}
                              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,auto)]">
                                <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 hover:bg-slate-50">
                                  <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                                  <span className="min-w-0 truncate text-sm text-slate-500">{gestorParecerFile ? gestorParecerFile.name : gestorParecerAnexo ? 'Substituir arquivo…' : 'Parecer em PDF'}</span>
                                  <input type="file" accept="application/pdf" className="hidden" onChange={e => setGestorParecerFile(e.target.files?.[0] || null)} />
                                </label>
                                <button
                                  type="button"
                                  onClick={() => { handleUploadGestorParecer(gestorParecerFile); setGestorParecerFile(null); }}
                                  disabled={!gestorParecerFile || gestorParecerUploading}
                                  className={`inline-flex min-h-[48px] items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${workflowPrimaryButtonClass}`}
                                >
                                  {gestorParecerUploading ? 'Enviando...' : gestorParecerAnexo ? 'Substituir' : 'Anexar'}
                                </button>
                              </div>
                            </div>
                            <div className="flex flex-wrap justify-center gap-2">
                              <button
                                onClick={() => { setWorkflowParecer(plan?.parecerTecnico || ''); setWorkflowAction('aprovar'); }}
                                disabled={!gestorParecerAnexo}
                                title={!gestorParecerAnexo ? 'Anexe o parecer em PDF antes de aprovar' : undefined}
                                className="inline-flex min-w-[13rem] items-center justify-center rounded-lg bg-emerald-600 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Aprovar
                              </button>
                              {canReprovarEtapa && (
                                <button
                                  onClick={() => setWorkflowAction('reprovar')}
                                  className="inline-flex min-w-[7rem] items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
                                >
                                  Reprovar
                                </button>
                              )}
                            </div>
                            {!gestorParecerAnexo && (
                              <p className="text-xs font-medium text-amber-600">Anexe o parecer em PDF para habilitar a aprovação.</p>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div className="mt-4 flex flex-col items-center gap-3 border-t border-blue-200 pt-4 dark:border-blue-900/60">
                          {canUploadRequiredAnexo && requiredAnexo && (() => {
                            const anexoAtual = anexos.find(a => a.etapa === etapaAtual && a.tipoDocumento === requiredAnexo.tipo);
                            const planoInstituicaoAnexo = etapaAtual === 'CONVENIOS_ANALISE'
                              ? anexos.find(a => a.tipoDocumento === 'PLANO_ASSINADO_PRESIDENTE' && a.etapa === 'ENVIO')
                              : null;
                            return (
                              <div className="w-full space-y-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{requiredAnexo.label}</p>
                                {planoInstituicaoAnexo && (
                                  <AuthenticatedFileButton
                                    action="download"
                                    url={`/api${planoInstituicaoAnexo.viewUrl.replace(/\/view$/, '/download')}`}
                                    fileName={planoInstituicaoAnexo.nomeOriginal}
                                    className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                  >
                                    <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none"><path d="M12 3v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                                    Baixar plano assinado pela instituição
                                  </AuthenticatedFileButton>
                                )}
                                {anexoAtual && (
                                  <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 dark:border-violet-800 dark:bg-violet-950/30">
                                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">Arquivo atual</p>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <button type="button" onClick={() => setAnexoViewer(anexoAtual)} className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:underline dark:text-violet-300">
                                        <PaperClipIcon className="h-4 w-4 shrink-0" />
                                        <span className="truncate max-w-[22rem]">{anexoAtual.nomeOriginal}</span>
                                      </button>
                                      <div className="flex items-center gap-2">
                                        <AuthenticatedFileButton
                                          action="download"
                                          url={`/api${anexoAtual.viewUrl.replace(/\/view$/, '/download')}`}
                                          fileName={anexoAtual.nomeOriginal}
                                          className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-white px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-50"
                                        >
                                          <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" />
                                          Baixar
                                        </AuthenticatedFileButton>
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            if (!id || !window.confirm('Remover este arquivo? Você poderá enviar um novo em seguida.')) return;
                                            try {
                                              await planoService.deleteAnexo(id, anexoAtual.id);
                                              await loadAnexos();
                                              setToast({ message: 'Arquivo removido.', type: 'success' });
                                            } catch (err: any) {
                                              setToast({ message: err?.response?.data?.error || 'Erro ao remover arquivo', type: 'error' });
                                            }
                                          }}
                                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                        >
                                          Remover
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,auto)]">
                                  <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 hover:bg-slate-50">
                                    <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                                    <span className="min-w-0 truncate text-sm text-slate-500">{selectedAnexoFile ? selectedAnexoFile.name : (anexoAtual ? 'Substituir arquivo…' : requiredAnexo.label)}</span>
                                    <input type="file" className="hidden" accept="application/pdf" onChange={e => setSelectedAnexoFile(e.target.files?.[0] || null)} />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={handleUploadAnexoWorkflow}
                                    disabled={!selectedAnexoFile || uploadingAnexo}
                                    className={`inline-flex min-h-[48px] items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${workflowPrimaryButtonClass}`}
                                  >
                                    {uploadingAnexo ? 'Enviando...' : anexoAtual ? 'Substituir' : 'Anexar'}
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                          {!isWorkflowFinished && requiredAnexoSecundario && canUploadRequiredAnexoSecundario && (() => {
                            const anexoSecAtual = anexos.find(a => a.etapa === etapaAtual && a.tipoDocumento === requiredAnexoSecundario.tipo);
                            return (
                              <div className="w-full space-y-3 border-t border-slate-200 pt-3 dark:border-slate-700">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{requiredAnexoSecundario.label}</p>
                                {anexoSecAtual && (
                                  <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 dark:border-violet-800 dark:bg-violet-950/30">
                                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">Arquivo atual</p>
                                    <button type="button" onClick={() => setAnexoViewer(anexoSecAtual)} className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:underline dark:text-violet-300">
                                      <PaperClipIcon className="h-4 w-4 shrink-0" />
                                      <span className="truncate max-w-[22rem]">{anexoSecAtual.nomeOriginal}</span>
                                    </button>
                                    {(anexoSecAtual.uploaderNome || anexoSecAtual.usuarioUpload) && (
                                      <p className="mt-0.5 text-xs text-violet-500">por {anexoSecAtual.uploaderNome || anexoSecAtual.usuarioUpload}{anexoSecAtual.dataUpload && <> · {new Date(anexoSecAtual.dataUpload).toLocaleDateString('pt-BR')}</>}</p>
                                    )}
                                  </div>
                                )}
                                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,auto)]">
                                  <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 hover:bg-slate-50">
                                    <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                                    <span className="min-w-0 truncate text-sm text-slate-500">{selectedAnexoSecundarioFile ? selectedAnexoSecundarioFile.name : (anexoSecAtual ? 'Substituir arquivo…' : requiredAnexoSecundario.label)}</span>
                                    <input type="file" className="hidden" accept="application/pdf" onChange={e => setSelectedAnexoSecundarioFile(e.target.files?.[0] || null)} />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={handleUploadAnexoSecundario}
                                    disabled={!selectedAnexoSecundarioFile || uploadingAnexoSecundario}
                                    className={`inline-flex min-h-[48px] items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${workflowPrimaryButtonClass}`}
                                  >
                                    {uploadingAnexoSecundario ? 'Enviando...' : anexoSecAtual ? 'Substituir' : 'Anexar'}
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                          {isConvenios && etapaAtual === 'CONVENIOS_ANALISE' && (
                            <div className="w-full grid gap-3 border-t border-slate-200 pt-4 dark:border-slate-700 md:grid-cols-2">
                              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm space-y-2 dark:border-slate-700 dark:bg-slate-950">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Gestor da Parceria</p>
                                <p className="text-sm text-gray-700 dark:text-slate-300">
                                  Atual: <strong>{plan?.gestorNome || 'Não vinculado'}</strong>
                                </p>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={formatCPF(gestorCpf)}
                                    onChange={e => setGestorCpf(onlyDigits(e.target.value))}
                                    className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Digite o CPF do gestor..."
                                  />
                                  <button
                                    type="button"
                                    onClick={handleVincularGestor}
                                    disabled={savingGestor || !gestorLocalizado}
                                    className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    {savingGestor ? '...' : 'Vincular'}
                                  </button>
                                </div>
                                {gestorCpf && !gestorLocalizado && onlyDigits(gestorCpf).length === 11 && (
                                  <p className="text-xs text-blue-700">
                                    Nenhum usuário ativo elegível para o gestor da parceria foi encontrado para esse CPF.
                                  </p>
                                )}
                                {gestorLocalizado && (
                                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                                    <p className="text-sm font-medium text-emerald-800">{gestorLocalizado.nomeCompleto}</p>
                                    <p className="text-xs text-emerald-700">
                                      {[formatCPF(gestorLocalizado.documento || ''), gestorLocalizado.email, gestorLocalizado.cargoFuncao].filter(Boolean).join(' · ')}
                                    </p>
                                  </div>
                                )}
                              </div>
                              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm space-y-2 dark:border-slate-700 dark:bg-slate-950">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Comissão de Seleção</p>
                                <p className="text-sm text-gray-700 dark:text-slate-300">
                                  Atual: <strong>{comissaoAtualLabel}</strong>
                                </p>
                                {comissoes.length === 0 ? (
                                  <p className="text-xs text-violet-700">Nenhuma comissão cadastrada.</p>
                                ) : (
                                  <div className="flex gap-2">
                                    <select
                                      value={comissaoPickerId}
                                      onChange={e => setComissaoPickerId(e.target.value)}
                                      className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="">Selecionar comissão...</option>
                                      {comissoes.map(comissao => (
                                        <option key={comissao.id} value={comissao.id}>
                                          Comissão {comissao.numero} - Portaria {comissao.numeroPortaria}/{comissao.anoPortaria} ({comissao.membros?.filter((m: any) => m.ativo !== false).length || 0} membros)
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      type="button"
                                      onClick={handleVincularComissao}
                                      disabled={savingComissao || !comissaoPickerId}
                                      className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                                    >
                                      {savingComissao ? '...' : 'Vincular'}
                                    </button>
                                  </div>
                                )}
                                <p className="text-xs text-gray-500">
                                  O plano só segue para a comissão quando houver uma comissão vinculada com ao menos 1 membro.
                                </p>
                              </div>
                            </div>
                          )}
                          {isConvenios && etapaAtual === 'CONVENIOS_FORMALIZACAO' && id && (
                            <PrestacaoPcConfigPanel planoId={id} onConfiguradoChange={setPcConfigurado} />
                          )}
                          {isConvenios && etapaAtual !== 'GESTOR_PARCERIA' && !requiredAnexo && ['EM_ANALISE', 'APROVADO', 'PENDENTE_RETIFICACAO'].includes(statusAtual || '') && !isWorkflowFinished && (
                            <button
                              type="button"
                              onClick={abrirJuntada}
                              className="inline-flex items-center gap-1.5 rounded-md border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700 transition-colors hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/60"
                            >
                              <PaperClipIcon className="h-4 w-4" />
                              Adicionar documento da fase
                            </button>
                          )}
                          <div className="flex flex-wrap justify-center gap-2">
                            <button
                              onClick={() => { setWorkflowParecer(etapaAtual === 'GESTOR_PARCERIA' ? (plan?.parecerTecnico || '') : ''); setWorkflowAction('aprovar'); }}
                              disabled={(isConvenios && etapaAtual === 'CONVENIOS_ANALISE' && (!hasRequiredAnexo || !plan?.prefeitoAprovadoEm || !plan?.gestorId || !plan?.comissaoId)) || (etapaAtual === 'CONVENIOS_FORMALIZACAO' && !pcConfigurado)}
                              title={isConvenios && etapaAtual === 'CONVENIOS_ANALISE' && (!hasRequiredAnexo || !plan?.prefeitoAprovadoEm || !plan?.gestorId || !plan?.comissaoId) ? 'Anexe o plano assinado, registre a aprovação do Prefeito, vincule o gestor da parceria e a comissão de seleção para habilitar esta ação' : undefined}
                              className="inline-flex min-w-[13rem] items-center justify-center rounded-lg bg-emerald-600 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {etapaAtual === 'ORCAMENTO'
                                ? 'Reenviar para comissão'
                                : etapaAtual === 'COMISSAO_SELECAO'
                                  ? 'Enviar para minuta do termo de fomento'
                                  : etapaAtual === 'CONVENIOS_MINUTA'
                                    ? 'Enviar para PGM'
                                    : etapaAtual === 'GESTOR_JURIDICO_REVISAO'
                                      ? 'Avançar para formalização'
                                  : 'Aprovar'}
                            </button>
                            {etapaAtual === 'CONVENIOS_ANALISE' && (
                              <button
                                onClick={() => setWorkflowAction('devolver')}
                                className="inline-flex min-w-[9rem] items-center justify-center rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100"
                              >
                                Devolver para correção
                              </button>
                            )}
                            {canReprovarEtapa && (
                              <button
                                onClick={() => setWorkflowAction('reprovar')}
                                className="inline-flex min-w-[7rem] items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
                              >
                                Reprovar
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {(anexos.length > 0 || parecerAnexoInfo || gestorParecerAnexo || (isConvenios && ['EM_ANALISE', 'APROVADO', 'PENDENTE_RETIFICACAO'].includes(statusAtual || ''))) && (
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Documentos do processo
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                            {anexos.length} arquivo{anexos.length === 1 ? '' : 's'}
                          </span>
                          {isConvenios && ['EM_ANALISE', 'APROVADO', 'PENDENTE_RETIFICACAO'].includes(statusAtual || '') && (
                            <button
                              onClick={() => { setJuntadaEtapa(''); setJuntadaTipo(''); setJuntadaFaseLocked(false); setShowJuntadaModal(true); }}
                              className="inline-flex items-center gap-2 rounded-full border border-violet-300 bg-violet-100 px-4 py-2 text-sm font-semibold text-violet-700 shadow-sm transition-colors hover:bg-violet-200 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/60"
                            >
                              <PaperClipIcon className="h-4 w-4" />
                              Juntar documento
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleDownloadZip}
                            disabled={downloadingZip || anexos.length === 0}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
                          >
                            {downloadingZip ? 'Gerando ZIP...' : 'Baixar todos'}
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 space-y-4">
                        {anexos.filter(a => a.etapa === 'RASCUNHO').length > 0 && (
                          <div>
                            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Dados do Plano</p>
                            <div className="flex flex-wrap gap-2">
                              {anexos.filter(a => a.etapa === 'RASCUNHO').map(anexo => (
                                <div key={anexo.id} className="flex flex-col gap-0.5">
                                  <button type="button" onClick={() => setAnexoViewer(anexo)} className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:underline underline-offset-2">
                                    <PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
                                    <span className="block max-w-[20rem] truncate">{anexo.nomeOriginal}</span>
                                  </button>
                                  {(anexo.uploaderNome || anexo.usuarioUpload) && (
                                    <span className="pl-1 text-xs text-slate-500 font-medium">
                                      por {anexo.uploaderNome || anexo.usuarioUpload}
                                      {anexo.dataUpload && <> · {new Date(anexo.dataUpload).toLocaleDateString('pt-BR')}</>}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {workflowTimeline
                          .filter(step =>
                            anexos.some(a => a.etapa === step.key) ||
                            (step.key === 'PROCURADORIA_JURIDICA' && !!parecerAnexoInfo) ||
                            (step.key === 'GESTOR_PARCERIA' && !!gestorParecerAnexo)
                          )
                          .map(step => {
                            const docs = anexos.filter(a => a.etapa === step.key);
                            return (
                              <div key={step.key}>
                                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{step.label}</p>
                                {step.key === 'PROCURADORIA_JURIDICA' && (plan?.procuradorJuridicoNome || plan?.parecerJuridicoEmitidoPor) && (
                                  <p className="mb-1.5 text-xs text-slate-500">
                                    Parecer emitido por <span className="font-medium text-slate-700">{plan?.procuradorJuridicoNome || plan?.parecerJuridicoEmitidoPor}</span>
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                  {docs.map(anexo => (
                                    <div key={anexo.id} className="flex flex-col gap-0.5">
                                      <button
                                        type="button"
                                        onClick={() => setAnexoViewer(anexo)}
                                        className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:underline underline-offset-2"
                                      >
                                        <PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
                                        <span className="block max-w-[20rem] truncate">
                                          {anexo.nomeOriginal}
                                          {anexo.documentCode && (
                                            <span className="ml-1 font-mono text-[10px] text-slate-400">{anexo.documentCode}</span>
                                          )}
                                        </span>
                                      </button>
                                      <div className="pl-1 flex items-center gap-1.5 flex-wrap">
                                        {(anexo.uploaderNome || anexo.usuarioUpload) && (
                                          <span className="text-xs text-slate-500 font-medium">
                                            por {anexo.uploaderNome || anexo.usuarioUpload}
                                            {anexo.dataUpload && (
                                              <> · {new Date(anexo.dataUpload).toLocaleDateString('pt-BR')}</>
                                            )}
                                          </span>
                                        )}
                                        {anexo.origemRetificacao && (
                                          <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-orange-700">
                                            Retificação
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  {step.key === 'PROCURADORIA_JURIDICA' && parecerAnexoInfo && (
                                    <button
                                      type="button"
                                      onClick={handleAbrirParecerAnexo}
                                      disabled={abrindoParecerAnexo}
                                      className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                                    >
                                      <PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
                                      <span className="block max-w-[20rem] truncate">
                                        {abrindoParecerAnexo ? 'Abrindo…' : parecerAnexoInfo.nomeOriginal}
                                        {parecerAnexoInfo.documentCode && (
                                          <span className="ml-1 font-mono text-[10px] text-slate-400">{parecerAnexoInfo.documentCode}</span>
                                        )}
                                      </span>
                                    </button>
                                  )}
                                  {step.key === 'GESTOR_PARCERIA' && gestorParecerAnexo && (
                                    <button
                                      type="button"
                                      onClick={handleAbrirGestorParecer}
                                      disabled={abrindoGestorParecer}
                                      className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                                    >
                                      <PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
                                      <span className="block max-w-[20rem] truncate">
                                        {abrindoGestorParecer ? 'Abrindo…' : gestorParecerAnexo.nomeOriginal}
                                        {gestorParecerAnexo.documentCode && (
                                          <span className="ml-1 font-mono text-[10px] text-slate-400">{gestorParecerAnexo.documentCode}</span>
                                        )}
                                      </span>
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}


                  <div className="hidden">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">Ação atual</p>
                        <p className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">{etapaLabel}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          isWorkflowFinished
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200'
                            : 'bg-blue-600 text-white dark:bg-blue-500'
                        }`}>
                          {isWorkflowFinished ? 'Concluído' : 'Em andamento'}
                        </span>
                      </div>
                    </div>
                    <div className="hidden mt-4 space-y-5">
                      {workflowTimeline
                        .filter((_, index) => currentWorkflowIndex === -1 || index <= currentWorkflowIndex)
                        .map((step, index) => {
                        const isComissaoDevolvida = step.key === 'COMISSAO_SELECAO' && isDevolvido_ORCAMENTO;
                        const isOrcamentoReprovado = step.key === 'ORCAMENTO' && isReprovado_ORCAMENTO;
                        const isConcluidoStepComplete = step.key === 'CONCLUIDO' && isWorkflowFinished;
                        const state = currentWorkflowIndex === -1
                          ? 'upcoming'
                          : index < currentWorkflowIndex
                            ? 'done'
                            : isConcluidoStepComplete
                              ? 'done'
                            : index === currentWorkflowIndex
                              ? isWorkflowFinished ? 'done' : 'current'
                              : 'upcoming';
                        const stateClasses = isOrcamentoReprovado
                          ? 'border-red-300 bg-red-50 text-red-900'
                          : isComissaoDevolvida
                            ? 'border-amber-300 bg-amber-50 text-amber-900'
                            : state === 'done'
                              ? 'border-green-200 bg-green-50 text-green-800'
                              : state === 'current'
                                ? 'border-blue-300 bg-white text-gray-900 shadow-sm ring-1 ring-blue-100'
                                : 'border-gray-200 bg-gray-50 text-gray-500';
                        const markerClasses = isOrcamentoReprovado
                          ? 'bg-red-600 text-white'
                          : isComissaoDevolvida
                            ? 'bg-amber-500 text-white'
                            : state === 'done'
                              ? 'bg-green-600 text-white'
                              : state === 'current'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-500';
                        const stageAnexos = anexos.filter(anexo =>
                          anexo.etapa === step.key ||
                          (step.key === 'INSTITUICAO' && ['ENVIO', 'INSTITUICAO'].includes(anexo.etapa))
                        );
                        return (
                          <div key={step.key} className="relative grid grid-cols-[2rem_minmax(0,1fr)] gap-4 pb-5 last:pb-0">
                            {index < workflowTimeline.length - 1 && (
                              <span className={`absolute left-4 top-8 h-[calc(100%-1.5rem)] w-px ${
                                isOrcamentoReprovado ? 'bg-red-200' : isComissaoDevolvida ? 'bg-amber-200' : state === 'done' ? 'bg-green-200' : state === 'current' ? 'bg-blue-200' : 'bg-gray-200'
                              }`} />
                            )}
                            <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${markerClasses}`}>
                              {(state === 'done' || isComissaoDevolvida) ? <CheckIcon className="h-4 w-4" aria-hidden="true" /> : index + 1}
                            </span>
                            <div className={`rounded-lg border px-4 py-3 ${stateClasses}`}>
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold">{step.label}</p>
                                  <p className="text-xs opacity-80">
                                    {isComissaoDevolvida ? 'Concluído — Devolvido' : state === 'done' ? 'Etapa concluída' : state === 'current' ? 'Etapa atual' : 'Próxima etapa'}
                                  </p>
                                  {isComissaoDevolvida && plan?.motivoPendencia && (
                                    <p className="mt-1 text-xs text-amber-800">{plan.motivoPendencia}</p>
                                  )}
                                </div>
                                </div>
                              {step.key === 'GESTOR_PARCERIA' && plan?.parecerTecnico && (
                                <div className="mt-3 border-t border-gray-200 pt-3">
                                  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Parecer Técnico do Gestor da Parceria</p>
                                      {(plan.parecerTecnicoInseridoPor || plan.gestorNome) && (
                                        <span className="text-xs text-slate-500 text-right">
                                          {plan.parecerTecnicoInseridoPor || plan.gestorNome}
                                          {plan.gestorSecretaria && (
                                            <span className="block opacity-75">{plan.gestorSecretaria}</span>
                                          )}
                                          {plan.parecerTecnicoInseridoEm && (
                                            <span className="block opacity-75">{new Date(plan.parecerTecnicoInseridoEm).toLocaleDateString('pt-BR')}</span>
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    <p className={`mt-2 text-sm text-slate-800 whitespace-pre-wrap ${expandedParecerTecnico ? '' : 'line-clamp-4'}`}>
                                      {plan.parecerTecnico}
                                    </p>
                                    {plan.parecerTecnico.length > 300 && (
                                      <button
                                        type="button"
                                        onClick={() => setExpandedParecerTecnico(v => !v)}
                                        className="mt-2 text-xs font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900"
                                      >
                                        {expandedParecerTecnico ? 'Ver menos' : 'Ver mais'}
                                      </button>
                                    )}
                                    {plan.parecerTecnicoHash && (
                                      <p className="mt-2 text-[10px] text-slate-400 font-mono break-all">
                                        SHA-256: {plan.parecerTecnicoHash}
                                      </p>
                                    )}
                                    {gestorParecerAnexo && (
                                      <button
                                        type="button"
                                        onClick={handleAbrirGestorParecer}
                                        disabled={abrindoGestorParecer}
                                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline disabled:opacity-60"
                                      >
                                        {abrindoGestorParecer ? 'Abrindo…' : 'Abrir parecer (PDF)'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                              {step.key === 'PROCURADORIA_JURIDICA' && (parecerAnexoInfo || plan?.parecerJuridico) && (
                                <div className="mt-3 border-t border-gray-200 pt-3">
                                  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Parecer Jurídico</p>
                                    {plan?.procuradorJuridicoNome && (
                                      <p className="mt-1 text-sm font-medium text-slate-800">
                                        Procurador responsável: {plan.procuradorJuridicoNome}
                                      </p>
                                    )}
                                    {plan?.parecerJuridico && (
                                      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{plan.parecerJuridico}</p>
                                    )}
                                    {parecerAnexoInfo && (
                                      <div className="mt-3 flex flex-wrap gap-2">
                                        <button
                                          type="button"
                                          onClick={handleAbrirParecerAnexo}
                                          disabled={abrindoParecerAnexo}
                                          className="inline-flex max-w-full items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 underline-offset-2 hover:underline disabled:opacity-50"
                                        >
                                          <PaperClipIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                                          <span className="block max-w-[18rem] text-truncate">{abrindoParecerAnexo ? 'Abrindo…' : parecerAnexoInfo.nomeOriginal}</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {step.key === 'PROCURADORIA_JURIDICA' && canAssignProcuradorJuridico && state === 'current' && (
                                <div className="mt-4 border-t border-gray-200 pt-4 text-gray-900">
                                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Procurador responsável</p>
                                      <p className="mt-1 text-sm text-gray-700">
                                        Atual: <strong>{plan?.procuradorJuridicoNome || 'Não vinculado'}</strong>
                                      </p>
                                    </div>
                                    {plan?.procuradorJuridicoId && (
                                      <span className="inline-flex rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                        Vinculado
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-3 space-y-3">
                                    {plan?.procuradorJuridicoId && (
                                      <button
                                        type="button"
                                        onClick={handleDesvincularProcuradorJuridico}
                                        disabled={savingProcuradorJuridico}
                                        className="inline-flex rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                      >
                                        Desvincular procurador
                                      </button>
                                    )}
                                    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                                      <select
                                        value={procuradorJuridicoId}
                                        onChange={e => setProcuradorJuridicoId(e.target.value)}
                                        className="min-w-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      >
                                        <option value="">Selecione um procurador...</option>
                                        {procuradoresJuridicos.map(procurador => (
                                          <option key={procurador.id} value={procurador.id}>
                                            {[procurador.nomeCompleto, procurador.email].filter(Boolean).join(' - ')}
                                          </option>
                                        ))}
                                      </select>
                                      <button
                                        type="button"
                                        onClick={handleVincularProcuradorJuridico}
                                        disabled={savingProcuradorJuridico || !procuradorJuridicoId}
                                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                      >
                                        {savingProcuradorJuridico ? 'Salvando...' : 'Vincular'}
                                      </button>
                                    </div>
                                    {procuradoresJuridicos.length === 0 && (
                                      <p className="text-xs text-gray-500">
                                        Nenhum usuário ativo com perfil Procuradoria Geral foi encontrado.
                                      </p>
                                    )}
                                  </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="relative mt-6 grid grid-cols-[2rem_minmax(0,1fr)] gap-4 pb-5">
                      <span className={`absolute left-4 -top-6 h-6 w-px ${isWorkflowFinished ? 'bg-green-200' : 'bg-blue-200'}`} />
                      <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white ${isWorkflowFinished ? 'bg-green-600' : 'bg-blue-600'}`}>
                        {isWorkflowFinished ? <CheckIcon className="h-4 w-4" aria-hidden="true" /> : <DocumentTextIcon className="h-4 w-4" aria-hidden="true" />}
                      </span>
                      <div className="min-w-0 w-full space-y-4">
                        <div className={`rounded-lg border px-4 py-4 shadow-sm ${isWorkflowFinished ? 'border-green-200 bg-green-50 dark:border-green-900/60 dark:bg-green-950/30' : 'border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-950'}`}>
                          <p className={`text-sm font-semibold ${isWorkflowFinished ? 'text-green-900 dark:text-green-200' : 'text-gray-900 dark:text-slate-100'}`}>
                            {isWorkflowFinished ? 'Fluxo concluído' : `Ações e documentos de ${etapaLabel}`}
                          </p>
                          <p className={`mt-1 text-sm ${isWorkflowFinished ? 'text-green-700 dark:text-green-300' : 'text-gray-500 dark:text-slate-400'}`}>
                            {isWorkflowFinished ? 'O plano foi concluído com sucesso e não há novas ações pendentes.' : workflowDescricao}
                          </p>
                        </div>
                  <div className={workflowPanelClass}>
                    {!isWorkflowFinished && canUploadRequiredAnexo && requiredAnexo && (() => {
                      const anexoAtual = anexos.find(a => a.etapa === etapaAtual && a.tipoDocumento === requiredAnexo.tipo);
                      const planoInstituicaoAnexo = etapaAtual === 'CONVENIOS_ANALISE'
                        ? anexos.find(a => a.tipoDocumento === 'PLANO_ASSINADO_PRESIDENTE' && a.etapa === 'ENVIO')
                        : null;
                      return (
                        <div className="mt-4 border-t border-slate-200 pt-4 space-y-3 dark:border-slate-700">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{requiredAnexo.label}</p>
                          {planoInstituicaoAnexo && (
                            <AuthenticatedFileButton
                              action="download"
                              url={`/api${planoInstituicaoAnexo.viewUrl.replace(/\/view$/, '/download')}`}
                              fileName={planoInstituicaoAnexo.nomeOriginal}
                              className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none"><path d="M12 3v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                              Baixar plano assinado pela instituição
                            </AuthenticatedFileButton>
                          )}
                          {anexoAtual && (
                            <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 dark:border-violet-800 dark:bg-violet-950/30">
                              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">Arquivo atual</p>
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <button type="button" onClick={() => setAnexoViewer(anexoAtual)} className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:underline dark:text-violet-300">
                                  <PaperClipIcon className="h-4 w-4 shrink-0" />
                                  <span className="truncate max-w-[22rem]">{anexoAtual.nomeOriginal}</span>
                                </button>
                                <div className="flex items-center gap-2">
                                  <AuthenticatedFileButton
                                    action="download"
                                    url={`/api${anexoAtual.viewUrl.replace(/\/view$/, '/download')}`}
                                    fileName={anexoAtual.nomeOriginal}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-white px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-50"
                                  >
                                    <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" />
                                    Baixar
                                  </AuthenticatedFileButton>
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      if (!id || !window.confirm('Remover este arquivo? Você poderá enviar um novo em seguida.')) return;
                                      try {
                                        await planoService.deleteAnexo(id, anexoAtual.id);
                                        await loadAnexos();
                                        setToast({ message: 'Arquivo removido.', type: 'success' });
                                      } catch (err: any) {
                                        setToast({ message: err?.response?.data?.error || 'Erro ao remover arquivo', type: 'error' });
                                      }
                                    }}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                  >
                                    Remover
                                  </button>
                                </div>
                              </div>
                              {(anexoAtual.uploaderNome || anexoAtual.usuarioUpload) && (
                                <p className="mt-0.5 text-xs text-violet-500">por {anexoAtual.uploaderNome || anexoAtual.usuarioUpload}{anexoAtual.dataUpload && <> · {new Date(anexoAtual.dataUpload).toLocaleDateString('pt-BR')}</>}</p>
                              )}
                            </div>
                          )}
                          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,auto)]">
                            <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 hover:bg-slate-50">
                              <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                              <span className="min-w-0 truncate text-sm text-slate-500">{selectedAnexoFile ? selectedAnexoFile.name : (anexoAtual ? 'Substituir arquivo…' : requiredAnexo.label)}</span>
                              <input type="file" className="hidden" accept="application/pdf" onChange={e => setSelectedAnexoFile(e.target.files?.[0] || null)} />
                            </label>
                            <button
                              type="button"
                              onClick={handleUploadAnexoWorkflow}
                              disabled={!selectedAnexoFile || uploadingAnexo}
                              className={`inline-flex min-h-[48px] items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${workflowPrimaryButtonClass}`}
                            >
                              {uploadingAnexo ? 'Enviando...' : anexoAtual ? 'Substituir' : 'Anexar'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                    {!isWorkflowFinished && requiredAnexoSecundario && canUploadRequiredAnexoSecundario && (() => {
                      const anexoSecAtual = anexos.find(a => a.etapa === etapaAtual && a.tipoDocumento === requiredAnexoSecundario.tipo);
                      return (
                        <div className="mt-4 border-t border-slate-200 pt-4 space-y-3 dark:border-slate-700">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{requiredAnexoSecundario.label}</p>
                          {anexoSecAtual && (
                            <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 dark:border-violet-800 dark:bg-violet-950/30">
                              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">Arquivo atual</p>
                              <button type="button" onClick={() => setAnexoViewer(anexoSecAtual)} className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:underline dark:text-violet-300">
                                <PaperClipIcon className="h-4 w-4 shrink-0" />
                                <span className="truncate max-w-[22rem]">{anexoSecAtual.nomeOriginal}</span>
                              </button>
                              {(anexoSecAtual.uploaderNome || anexoSecAtual.usuarioUpload) && (
                                <p className="mt-0.5 text-xs text-violet-500">por {anexoSecAtual.uploaderNome || anexoSecAtual.usuarioUpload}{anexoSecAtual.dataUpload && <> · {new Date(anexoSecAtual.dataUpload).toLocaleDateString('pt-BR')}</>}</p>
                              )}
                            </div>
                          )}
                          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,auto)]">
                            <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 hover:bg-slate-50">
                              <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                              <span className="min-w-0 truncate text-sm text-slate-500">{selectedAnexoSecundarioFile ? selectedAnexoSecundarioFile.name : (anexoSecAtual ? 'Substituir arquivo…' : requiredAnexoSecundario.label)}</span>
                              <input type="file" className="hidden" accept="application/pdf" onChange={e => setSelectedAnexoSecundarioFile(e.target.files?.[0] || null)} />
                            </label>
                            <button
                              type="button"
                              onClick={handleUploadAnexoSecundario}
                              disabled={!selectedAnexoSecundarioFile || uploadingAnexoSecundario}
                              className={`inline-flex min-h-[48px] items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${workflowPrimaryButtonClass}`}
                            >
                              {uploadingAnexoSecundario ? 'Enviando...' : anexoSecAtual ? 'Substituir' : 'Anexar'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{workflowNextStepTitle}</p>
                    <div className="mt-3 space-y-2">
                      {visibleWorkflowPendingHints.map(hint => {
                        const toneClasses = hint.tone === 'emerald'
                          ? 'border-emerald-200 bg-white text-emerald-800'
                          : hint.tone === 'blue'
                            ? 'border-blue-200 bg-white text-blue-800'
                            : 'border-amber-200 bg-white text-amber-900';
                        return (
                          <div key={hint.key} className={`rounded-lg border px-3 py-3 ${toneClasses}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-2 min-w-0">
                                <span className="mt-0.5 text-sm font-bold">•</span>
                                <span className="text-sm leading-6">
                                  {hint.label}
                                  {hint.sublabel && (
                                    <span className="block text-xs opacity-70 mt-0.5">{hint.sublabel}</span>
                                  )}
                                </span>
                              </div>
                              {hint.onNavigate && (
                                <button
                                  type="button"
                                  onClick={hint.onNavigate}
                                  className="shrink-0 text-xs font-medium underline"
                                >
                                  Abrir
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {visibleWorkflowPendingHints.length === 0 && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
                          Nenhuma pendência registrada nesta etapa.
                        </div>
                      )}
                    </div>
                  </div>
                  {canOpenJuridicalAnalysis && (
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/plano/${plan.id}/analise-juridica`)}
                      className="inline-flex w-full items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                    >
                      Abrir análise jurídica estruturada
                    </button>
                  )}
                  {isConvenios && ['GESTOR_PARCERIA', 'ORCAMENTO'].includes(etapaAtual || '') && (
                    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Gestor da Parceria</p>
                      <p className="text-sm text-gray-700">
                        Atual: <strong>{plan?.gestorNome || 'Não vinculado'}</strong>
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatCPF(gestorCpf)}
                          onChange={e => setGestorCpf(onlyDigits(e.target.value))}
                          className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Digite o CPF do gestor..."
                        />
                        <button
                          type="button"
                          onClick={handleVincularGestor}
                          disabled={savingGestor || !gestorLocalizado}
                          className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                        >
                          {savingGestor ? '...' : 'Vincular'}
                        </button>
                      </div>
                      {gestorCpf && !gestorLocalizado && onlyDigits(gestorCpf).length === 11 && (
                        <p className="text-xs text-blue-700">
                          Nenhum usuário ativo elegível para o gestor da parceria foi encontrado para esse CPF.
                        </p>
                      )}
                      {gestorLocalizado && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                          <p className="text-sm font-medium text-emerald-800">{gestorLocalizado.nomeCompleto}</p>
                          <p className="text-xs text-emerald-700">
                            {[formatCPF(gestorLocalizado.documento || ''), gestorLocalizado.email, gestorLocalizado.cargoFuncao].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {isConvenios && etapaAtual === 'CONVENIOS_ANALISE' && (
                    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Aprovação do Prefeito</p>
                      {plan?.prefeitoAprovadoEm ? (() => {
                        const planoPrefeito = anexos.find(a => a.tipoDocumento === 'PLANO_TRABALHO_ASSINADO_PREFEITO');
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              <span className="text-sm font-semibold text-emerald-800">Plano com assinatura do Prefeito anexado</span>
                            </div>
                            <p className="text-xs text-gray-600">
                              Registrado por <strong>{plan.prefeitoAprovadoPor}</strong> em {new Date(plan.prefeitoAprovadoEm).toLocaleString('pt-BR')}
                            </p>
                            {planoPrefeito && (
                              <div className="flex flex-wrap gap-2 pt-1">
                                <button
                                  type="button"
                                  onClick={() => setAnexoViewer(planoPrefeito)}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                                >
                                  Visualizar
                                </button>
                                <AuthenticatedFileButton
                                  action="download"
                                  url={`/api${planoPrefeito.viewUrl}`}
                                  fileName={planoPrefeito.nomeOriginal}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                                >
                                  Baixar
                                </AuthenticatedFileButton>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!id || !window.confirm('Remover o plano assinado pelo Prefeito? A aprovação será desfeita e você poderá enviar um novo documento.')) return;
                                    try {
                                      await planoService.deleteAnexo(id, planoPrefeito.id);
                                      const [, planRes] = await Promise.all([loadAnexos(), planoService.getFull(id)]);
                                      if (planRes?.data) setPlan(planRes.data);
                                      setToast({ message: 'Plano assinado pelo Prefeito removido. Envie um novo documento.', type: 'success' });
                                    } catch (err: any) {
                                      setToast({ message: err?.response?.data?.error || 'Erro ao remover documento', type: 'error' });
                                    }
                                  }}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                                >
                                  Remover e substituir
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })() : (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-700">
                            Baixe o plano assinado pela instituição, obtenha a assinatura do Prefeito e faça o upload do documento assinado na aba <strong>Documentos</strong>.
                          </p>
                          {(() => {
                            const planoAssinadoAnexo = anexos.find(a => a.tipoDocumento === 'PLANO_ASSINADO_PRESIDENTE' && a.etapa === 'ENVIO');
                            return planoAssinadoAnexo?.viewUrl ? (
                              <AuthenticatedFileButton
                                action="download"
                                url={`/api${planoAssinadoAnexo.viewUrl.replace(/\/view$/, '/download')}`}
                                fileName={planoAssinadoAnexo.nomeOriginal}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                              >
                                <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 3v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                </svg>
                                Baixar plano assinado pela instituição
                              </AuthenticatedFileButton>
                            ) : (
                              <p className="text-xs text-amber-700">Documento assinado pela instituição não encontrado.</p>
                            );
                          })()}
                          <div>
                            <button
                              type="button"
                              onClick={handleRegistrarAprovacaoPrefeito}
                              disabled={!hasRequiredAnexo || registrandoAprovacaoPrefeito}
                              title={!hasRequiredAnexo ? 'Anexe o plano de trabalho assinado pela instituição e pelo Prefeito antes de registrar a aprovação' : undefined}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {registrandoAprovacaoPrefeito ? 'Registrando...' : 'Registrar aprovação do Prefeito'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {canManageComissao && etapaAtual !== 'CONVENIOS_ANALISE' && (
                    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Comissão de Seleção</p>
                      <p className="text-sm text-gray-700">
                        Atual: <strong>{comissaoAtualLabel}</strong>
                      </p>
                      {comissoes.length === 0 ? (
                        <p className="text-xs text-violet-700">Nenhuma comissão cadastrada.</p>
                      ) : (
                        <div className="flex gap-2">
                          <select
                            value={comissaoPickerId}
                            onChange={e => setComissaoPickerId(e.target.value)}
                            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Selecionar comissão...</option>
                            {comissoes.map(comissao => (
                              <option key={comissao.id} value={comissao.id}>
                                Comissão {comissao.numero} - Portaria {comissao.numeroPortaria}/{comissao.anoPortaria} ({comissao.membros?.filter((m: any) => m.ativo !== false).length || 0} membros)
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={handleVincularComissao}
                            disabled={savingComissao || !comissaoPickerId}
                            className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                          >
                            {savingComissao ? '...' : 'Vincular'}
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-gray-500">
                        O plano so segue para a comissao quando houver uma comissao vinculada com ao menos 1 membro.
                      </p>
                    </div>
                  )}

                  {/* -- Diligência ao plano de trabalho ----------------------- */}
                  {(isConvenios || isOperador) && (plan as any)?.instituicaoId && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Diligência</p>
                        {isConvenios && (
                          <button
                            type="button"
                            onClick={() => setDiligenciaPlanoOpen(true)}
                            className="rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                          >
                            Enviar diligência
                          </button>
                        )}
                      </div>
                      {diligenciasPlano.length > 0 && (
                        <div className="space-y-1.5 mt-1">
                          {diligenciasPlano.map(d => {
                            const statusBadge: Record<string, string> = {
                              PENDENTE: 'bg-amber-100 text-amber-700',
                              EM_VERIFICACAO: 'bg-blue-100 text-blue-700',
                              RESOLVIDA: 'bg-emerald-100 text-emerald-700',
                            };
                            const statusLabel: Record<string, string> = {
                              PENDENTE: 'Pendente',
                              EM_VERIFICACAO: 'Aguardando confirmação',
                              RESOLVIDA: 'Resolvida',
                            };
                            return (
                              <div key={d.id} className={`rounded-md border px-3 py-2 text-xs ${d.lida ? 'border-slate-200 bg-white text-slate-600' : 'border-amber-300 bg-white text-slate-800 font-medium'}`}>
                                <p className="leading-snug">{d.mensagem}</p>
                                {d.rejeicaoMotivo && d.status === 'PENDENTE' && d.verificadoEm && (
                                  <p className="mt-1 text-[11px] font-medium text-red-600">Convênios rejeitou: {d.rejeicaoMotivo}</p>
                                )}
                                <p className="mt-1 text-[10px] text-slate-400 flex items-center gap-1.5 flex-wrap">
                                  {d.enviadoPor} · {new Date(d.enviadoEm).toLocaleDateString('pt-BR')}
                                  <span className={`rounded-full px-1.5 py-0.5 ${statusBadge[d.status] ?? 'bg-slate-100 text-slate-600'}`}>
                                    {statusLabel[d.status] ?? d.status}
                                  </span>
                                </p>
                                {isOperador && d.status === 'PENDENTE' && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const observacao = window.prompt('Descreva o que foi providenciado (opcional):');
                                      if (observacao === null) return;
                                      try {
                                        const res = await diligenciaDocumentoService.resolver(d.id, observacao || undefined);
                                        setDiligenciasPlano(prev => prev.map(item => item.id === d.id ? res.data : item));
                                      } catch {
                                        notifyError('Erro ao enviar para verificação. Tente novamente.');
                                      }
                                    }}
                                    className="mt-1.5 rounded-lg border border-emerald-400 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"
                                  >
                                    Marcar como resolvida
                                  </button>
                                )}
                                {isConvenios && d.status === 'EM_VERIFICACAO' && (
                                  <div className="mt-1.5 flex gap-2">
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        const motivo = window.prompt('Motivo da rejeição (a diligência volta para o operador):');
                                        if (motivo === null) return;
                                        try {
                                          const res = await diligenciaDocumentoService.verificar(d.id, false, motivo);
                                          setDiligenciasPlano(prev => prev.map(item => item.id === d.id ? res.data : item));
                                        } catch {
                                          notifyError('Erro ao rejeitar diligência. Tente novamente.');
                                        }
                                      }}
                                      className="rounded-lg border border-red-300 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-100"
                                    >
                                      Rejeitar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          const res = await diligenciaDocumentoService.verificar(d.id, true);
                                          setDiligenciasPlano(prev => prev.map(item => item.id === d.id ? res.data : item));
                                        } catch {
                                          notifyError('Erro ao confirmar diligência. Tente novamente.');
                                        }
                                      }}
                                      className="rounded-lg bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
                                    >
                                      Confirmar resolução
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {diligenciasPlano.length === 0 && (
                        <p className="text-xs text-amber-600">Nenhuma diligência enviada para este plano.</p>
                      )}
                    </div>
                  )}

                  {/* -- Comissão de Seleção — painel de pareceres --------------- */}
                  {(etapaAtual === 'COMISSAO_SELECAO' || (statusAtual === 'DEVOLVIDO_CORRECAO' && Boolean(parecerResumo?.membros.length))) && (
                    <div className="space-y-3">
                      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pareceres da Comissão</p>
                            <p className="mt-1 text-sm font-medium text-slate-800">{parecerResumo?.statusGeral || 'Aguardando pareceres'}</p>
                          </div>
                          <div className="flex gap-2">
                            <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 border border-slate-200">
                              {parecerResumo ? `${parecerResumo.pareceresConcluidos}/${parecerResumo.totalMembros || 0} concluidos` : '0/0 concluidos'}
                            </span>
                          </div>
                        </div>
                        {false && etapaAtual === 'COMISSAO_SELECAO' && !parecerResumo?.ataAnexada && (
                          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            ATA pendente — apos a conclusao da votacao, o presidente deve inserir a resolucao/ATA assinada para o plano avançar.
                          </div>
                        )}

                        {pareceresLoading ? (
                          <p className="mt-3 text-sm text-slate-400">Carregando...</p>
                        ) : parecerResumo?.membros?.length ? (
                          <div className="mt-4 space-y-2">
                            {parecerResumo.membros.map(membro => (
                              <div
                                key={membro.membroId}
                                className={`rounded-lg border px-3 py-3 ${
                                  membro.statusParecer === 'REPROVADO'
                                    ? 'border-red-200 bg-red-50'
                                    : membro.statusParecer === 'APROVADO'
                                      ? 'border-emerald-200 bg-emerald-50'
                                      : 'border-slate-200 bg-white'
                                }`}
                              >
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-semibold text-slate-800">{membro.nomeCompleto || 'Membro não identificado'}</p>
                                      {membro.isPresidente && (
                                        <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                          Presidente
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                      {[membro.cargoFuncao, membro.perfil].filter(Boolean).join(' · ') || 'Comissão de Seleção'}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className={`text-xs font-semibold ${
                                      membro.statusParecer === 'REPROVADO'
                                        ? 'text-red-700'
                                        : membro.statusParecer === 'APROVADO'
                                          ? 'text-emerald-700'
                                          : 'text-slate-500'
                                    }`}>
                                      {membro.statusParecer}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                      {membro.dataEmissao ? new Date(membro.dataEmissao).toLocaleString('pt-BR') : 'Parecer pendente'}
                                    </p>
                                  </div>
                                </div>
                                <p className="mt-2 text-xs font-medium text-slate-600">
                                  Comissão {membro.numeroDaComissao ?? parecerResumo.numeroDaComissao ?? '—'}, Portaria {membro.numeroPortaria ?? parecerResumo.numeroPortaria ?? '—'}/{membro.anoPortaria ?? parecerResumo.anoPortaria ?? '—'}
                                </p>
                                {membro.justificativa && (
                                  <p className="mt-2 text-sm text-slate-600 whitespace-pre-line">{membro.justificativa}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-3 text-sm text-slate-500 italic">Nenhum parecer emitido ainda.</p>
                        )}
                      </div>

                      {isMembroDaComissao && etapaAtual === 'COMISSAO_SELECAO' && parecerResumo?.membroLogado && (
                        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Identificação do membro</p>
                          <p className="mt-1 text-sm font-medium text-slate-800">
                            {parecerResumo.membroLogado.nomeCompleto || user?.name || 'Membro logado'} — Comissão nº {parecerResumo.numeroDaComissao ?? '—'}, nomeado pela Portaria nº {parecerResumo.numeroPortaria ?? '—'}/{parecerResumo.anoPortaria ?? '—'}
                          </p>
                        </div>
                      )}

                      {isMembroDaComissao && etapaAtual === 'COMISSAO_SELECAO' && !parecerResumo?.membros?.some(m => m.membroLogado && m.statusParecer !== 'PENDENTE') && (
                        <form onSubmit={handleEmitirParecer} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Emitir meu parecer</p>
                          <div className="flex gap-2">
                            {(['APROVADO', 'REPROVADO'] as const).map(op => (
                              <button
                                key={op}
                                type="button"
                                onClick={() => setParecerForm(f => ({ ...f, resultado: op }))}
                                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                                  parecerForm.resultado === op
                                    ? op === 'APROVADO'
                                      ? 'border-emerald-500 bg-emerald-600 text-white'
                                      : 'border-red-500 bg-red-600 text-white'
                                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                {op === 'APROVADO' ? 'Aprovar' : 'Reprovar'}
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={parecerForm.justificativa}
                            onChange={e => setParecerForm(f => ({ ...f, justificativa: toUpperText(e.target.value) }))}
                            rows={3}
                            placeholder="Justificativa (mínimo 20 caracteres)..."
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                          <button
                            type="submit"
                            disabled={!parecerForm.resultado || parecerForm.justificativa.length < 20 || submittingParecer}
                            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            {submittingParecer ? 'Enviando...' : 'Registrar parecer'}
                          </button>
                        </form>
                      )}
                      {isMembroDaComissao && etapaAtual === 'COMISSAO_SELECAO' && parecerResumo?.membros?.some(m => m.membroLogado && m.statusParecer !== 'PENDENTE') && (
                        <p className="text-xs text-emerald-700 font-medium px-1">Você já emitiu seu parecer para este plano.</p>
                      )}
                    </div>
                  )}


                {etapaAtual === 'GESTOR_PARCERIA' && isGestorOrAdmin && !isWorkflowFinished && (
                  <div className="mt-4 w-full min-w-0 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="flex w-full min-w-0 items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                        <DocumentTextIcon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Parecer do Gestor da Parceria</p>
                        <p className="mt-1 text-sm text-gray-500">
                          Escreva o parecer e salve. Depois clique em <strong>Imprimir</strong>, salve em PDF, assine e anexe o documento assinado no campo de documentos abaixo.
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 space-y-3">
                      <textarea
                        value={parecerTecnicoDraft}
                        onChange={e => setParecerTecnicoDraft(toUpperText(e.target.value))}
                        rows={6}
                        placeholder="Escreva o parecer técnico do gestor da parceria"
                        className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={handleSalvarParecerTecnico}
                          disabled={!parecerTecnicoDraft.trim() || savingParecerTecnico}
                          className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {savingParecerTecnico ? 'Salvando...' : 'Salvar parecer'}
                        </button>
                        <button
                          onClick={handleImprimirParecerGestor}
                          disabled={!plan?.parecerTecnico}
                          title={!plan?.parecerTecnico ? 'Salve o parecer antes de imprimir' : undefined}
                          className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Imprimir parecer (PDF)
                        </button>
                      </div>
                      {plan?.parecerTecnicoInseridoEm && (
                        <p className="text-xs text-gray-500">
                          Última versão salva em {new Date(plan.parecerTecnicoInseridoEm).toLocaleString('pt-BR')}
                          {plan.parecerTecnicoInseridoPor ? ` por ${plan.parecerTecnicoInseridoPor}` : ''}.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {false && !isWorkflowFinished && !(isOperador && isWorkflowStage) && (
                <>
                {/* Botão flutuante para abrir a gaveta de documentos da etapa */}
                {!docsDrawerOpen && (
                  <button
                    type="button"
                    onClick={() => setDocsDrawerOpen(true)}
                    className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-blue-700"
                  >
                    <DocumentTextIcon className="h-5 w-5" aria-hidden="true" />
                    Documentos da etapa
                    {anexosEtapaAtual.length > 0 && (
                      <span className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-white/25 px-1 text-xs font-semibold">{anexosEtapaAtual.length}</span>
                    )}
                    {hasPendingRequiredDoc && (
                      <span className="ml-1 h-2.5 w-2.5 rounded-full bg-amber-300" title="Documento obrigatório pendente" />
                    )}
                  </button>
                )}

                {/* Box lateral compacto (sem fundo escurecido) */}
                {docsDrawerOpen && (
                <div
                  id="documentos-etapa-atual"
                  aria-label="Documentos da etapa atual"
                  className="fixed right-4 top-24 z-40 flex max-h-[calc(100vh-7rem)] w-80 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
                >
                <div className="flex w-full min-w-0 items-start gap-2 border-b border-gray-200 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Documento da etapa</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Anexe o documento obrigatório desta fase.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDocsDrawerOpen(false)}
                    className="shrink-0 rounded-md border border-gray-200 p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                    aria-label="Fechar painel de documentos"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="w-full min-w-0 flex-1 space-y-4 overflow-y-auto p-4">
                  {requiredAnexo ? (
                    <div className="w-full min-w-0 space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{requiredAnexo.label}</p>
                        <p className="mt-1 text-sm text-gray-500">
                          {hasRequiredAnexo ? 'Documento já anexado para esta etapa.' : 'Envie o documento obrigatório para liberar a aprovação desta etapa.'}
                        </p>
                      </div>
                      {canUploadRequiredAnexo && !hasRequiredAnexo ? (
                      <div className="w-full">
                        <div className="space-y-4">
                          {requiredAnexo.tipo === 'ATA_COMISSAO' && (
                            <p className="text-xs text-amber-700 font-medium">Inserir resolucao/ATA assinada</p>
                          )}
                          <label className="flex min-h-[88px] w-full cursor-pointer items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-4 hover:bg-gray-100">
                            <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                            <span className="text-sm text-gray-500 break-all">{selectedAnexoFile ? selectedAnexoFile.name : 'Clique para selecionar o arquivo'}</span>
                            <input type="file" className="hidden" accept={requiredAnexo.tipo === 'ATA_COMISSAO' ? 'application/pdf' : undefined} onChange={e => setSelectedAnexoFile(e.target.files?.[0] || null)} />
                          </label>
                          <textarea
                            value={anexoDescricao}
                            onChange={e => setAnexoDescricao(toUpperText(e.target.value))}
                            rows={4}
                            placeholder="Descrição do documento"
                            className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            onClick={handleUploadAnexoWorkflow}
                            disabled={!selectedAnexoFile || uploadingAnexo}
                            className="inline-flex min-h-[46px] w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {uploadingAnexo ? 'Enviando documento...' : `Anexar ${requiredAnexo.label}`}
                          </button>
                        </div>
                      </div>
                      ) : (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                          {hasRequiredAnexo
                            ? 'Documento obrigatório já anexado para esta etapa.'
                            : requiredAnexo.tipo === 'ATA_COMISSAO'
                              ? 'Apenas o presidente da comissão pode inserir a resolução/ATA assinada.'
                              : 'Este documento deve ser anexado pelo perfil responsável pela etapa.'}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border-t border-gray-200 pt-5 text-sm text-gray-500">
                      Esta etapa não exige anexo obrigatório.
                    </div>
                  )}

                  {requiredAnexoSecundario && canUploadRequiredAnexoSecundario && (
                    <div className="w-full min-w-0 space-y-4 border-t border-gray-200 pt-5">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{requiredAnexoSecundario.label}</p>
                        <p className="mt-1 text-sm text-gray-500">
                          {hasRequiredAnexoSecundario ? 'Documento já anexado para esta etapa.' : 'Envie o documento obrigatório para liberar a aprovação desta etapa.'}
                        </p>
                      </div>
                      <div className="w-full">
                        <div className="space-y-4">
                          <label className="flex min-h-[88px] w-full cursor-pointer items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-4 hover:bg-gray-100">
                            <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                            <span className="text-sm text-gray-500 break-all">{selectedAnexoSecundarioFile ? selectedAnexoSecundarioFile.name : 'Clique para selecionar o arquivo'}</span>
                            <input type="file" className="hidden" onChange={e => setSelectedAnexoSecundarioFile(e.target.files?.[0] || null)} />
                          </label>
                          <textarea
                            value={anexoSecundarioDescricao}
                            onChange={e => setAnexoSecundarioDescricao(toUpperText(e.target.value))}
                            rows={4}
                            placeholder="Descrição do documento"
                            className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            onClick={handleUploadAnexoSecundario}
                            disabled={!selectedAnexoSecundarioFile || uploadingAnexoSecundario}
                            className="inline-flex min-h-[46px] w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {uploadingAnexoSecundario ? 'Enviando documento...' : `Anexar ${requiredAnexoSecundario.label}`}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {needsRetroAnexo && (
                    <div className="w-full min-w-0 space-y-4 border-t border-amber-200 pt-5">
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        Este plano ainda não possui o documento obrigatório da etapa de análise inicial (Convênios). Faça o upload abaixo.
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Plano de trabalho assinado pela instituição e Prefeito</p>
                        <p className="mt-1 text-xs text-gray-500">Etapa: Convênios — análise inicial</p>
                      </div>
                      <div className="space-y-4">
                        <label className="flex min-h-[88px] w-full cursor-pointer items-center gap-3 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-4 hover:bg-amber-100">
                          <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
                          <span className="text-sm text-amber-700 break-all">{selectedRetroAnexoFile ? selectedRetroAnexoFile.name : 'Clique para selecionar o arquivo'}</span>
                          <input type="file" className="hidden" accept="application/pdf" onChange={e => setSelectedRetroAnexoFile(e.target.files?.[0] || null)} />
                        </label>
                        <button
                          onClick={handleUploadRetroAnexo}
                          disabled={!selectedRetroAnexoFile || uploadingRetroAnexo}
                          className="inline-flex min-h-[46px] w-full items-center justify-center rounded-md border border-transparent bg-amber-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {uploadingRetroAnexo ? 'Enviando documento...' : 'Anexar plano assinado (análise inicial)'}
                        </button>
                      </div>
                    </div>
                  )}

                  {needsRetroParecerAnexo && (
                    <div className="w-full min-w-0 space-y-4 border-t border-amber-200 pt-5">
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        Este plano ainda não possui o parecer de análise da etapa de minuta (Convênios). Faça o upload abaixo.
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Parecer de Análise - Departamento de Convênios</p>
                        <p className="mt-1 text-xs text-gray-500">Etapa: Convênios — minuta</p>
                      </div>
                      <div className="space-y-4">
                        <label className="flex min-h-[88px] w-full cursor-pointer items-center gap-3 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-4 hover:bg-amber-100">
                          <ArrowUpTrayIcon className="h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
                          <span className="text-sm text-amber-700 break-all">{selectedRetroParecerAnexoFile ? selectedRetroParecerAnexoFile.name : 'Clique para selecionar o arquivo'}</span>
                          <input type="file" className="hidden" accept="application/pdf" onChange={e => setSelectedRetroParecerAnexoFile(e.target.files?.[0] || null)} />
                        </label>
                        <button
                          onClick={handleUploadRetroParecerAnexo}
                          disabled={!selectedRetroParecerAnexoFile || uploadingRetroParecerAnexo}
                          className="inline-flex min-h-[46px] w-full items-center justify-center rounded-md border border-transparent bg-amber-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {uploadingRetroParecerAnexo ? 'Enviando documento...' : 'Anexar parecer de análise (minuta)'}
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              </div>
                )}
                </>
                )}
                      </div>
                    </div>
                      {false && currentWorkflowIndex !== -1 && workflowTimeline
                        .filter((_, index) => index > currentWorkflowIndex)
                        .map((step, futureOffset) => {
                          const index = currentWorkflowIndex + futureOffset + 1;
                          const stageAnexos = anexos.filter(anexo => anexo.etapa === step.key);
                          return (
                            <div key={step.key} className="relative grid grid-cols-[2rem_minmax(0,1fr)] gap-3 pb-3 last:pb-0">
                              {index < workflowTimeline.length - 1 && (
                                <span className="absolute left-4 top-8 h-[calc(100%-1.5rem)] w-px bg-gray-200" />
                              )}
                              <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-500">
                                {index + 1}
                              </span>
                              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-500">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold">{step.label}</p>
                                    <p className="text-xs opacity-80">Próxima etapa</p>
                                  </div>
                                  </div>
                                {step.key === 'PROCURADORIA_JURIDICA' && canAssignProcuradorJuridico && (
                                  <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Procurador responsável</p>
                                    <p className="mt-1 text-sm text-gray-600">
                                      A seleção será liberada aqui dentro do plano quando ele chegar à etapa PGM.
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                  </div>
            </div>
            </div>
            </div>
            </DetailSection>

        </div>
      )}

        {/* -- Pending items modal ------------------------------------------- */}
        {showPendingModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-slate-950/40" onClick={() => setShowPendingModal(false)} />
            <div className="relative z-10 mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <p className="text-lg font-semibold text-slate-900">Itens pendentes para envio</p>
              <p className="mt-1 text-sm text-slate-500">Preencha os itens abaixo antes de enviar para análise.</p>
              <ul className="mt-4 space-y-2">
                {pendingChecklistItems.map(item => (
                  <li key={item.key} className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                        <ExclamationCircleIcon className="w-3 h-3" />
                      </span>
                      <span className="text-sm text-slate-700">{item.label}</span>
                    </div>
                    {item.onNavigate && (
                      <button
                        type="button"
                        onClick={() => { setShowPendingModal(false); item.onNavigate?.(); }}
                        className="flex-shrink-0 text-xs text-blue-600 hover:underline whitespace-nowrap"
                      >
                        Ir para →
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex justify-end">
                <button onClick={() => setShowPendingModal(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmSending && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-slate-950/40" />
            <div className="relative z-10 mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <p className="text-lg font-semibold text-slate-900">Confirmar envio</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Deseja realmente enviar este plano para aprovação? Esta ação bloqueia parte da edição.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setConfirmSending(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700">
                  Cancelar
                </button>
                <button onClick={handleSendForApproval} className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600">
                  Enviar para análise
                </button>
              </div>
            </div>
          </div>
        )}

        {workflowAction && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-slate-950/40" />
            <div className="relative z-10 mx-4 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
              <p className="text-lg font-semibold text-slate-900">
                {workflowAction === 'aprovar' && (etapaAtual === 'ORCAMENTO' ? 'Reenviar para comissão' : 'Aprovar etapa')}
                {workflowAction === 'reprovar' && 'Reprovar plano'}
                {workflowAction === 'devolver' && 'Devolver para correção'}
                {workflowAction === 'confirmarReprovacao' && 'Confirmar devolução da emenda'}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Etapa atual: <strong>{etapaLabel}</strong>
              </p>

              {workflowAction === 'confirmarReprovacao' && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {plan?.emendaId
                    ? 'Ao confirmar, a emenda vinculada a este plano será devolvida e o processo encerrará como reprovado.'
                    : 'Ao confirmar, o processo encerrará definitivamente como reprovado e não haverá mais ações pendentes.'}
                </div>
              )}

              {(workflowAction === 'reprovar' || workflowAction === 'devolver') && (
                <div className="mt-4">
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Motivo</label>
                  <textarea
                    value={workflowMotivo}
                    onChange={e => setWorkflowMotivo(toUpperText(e.target.value))}
                    rows={4}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Descreva o motivo da decisão"
                  />
                </div>
              )}

              {(etapaAtual === 'GESTOR_PARCERIA' || etapaAtual === 'PROCURADORIA_JURIDICA') && (
                <div className="mt-4">
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                    {etapaAtual === 'PROCURADORIA_JURIDICA' ? 'Parecer jurídico' : 'Parecer técnico'}
                  </label>
                  <textarea
                    value={workflowParecer}
                    onChange={e => setWorkflowParecer(toUpperText(e.target.value))}
                    rows={5}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={etapaAtual === 'PROCURADORIA_JURIDICA'
                      ? 'Opcional: registre o parecer jurídico para esta decisão'
                      : 'Registre o parecer técnico obrigatório para a decisão do gestor'}
                  />
                  {etapaAtual === 'GESTOR_PARCERIA' && gestorParecerAnexo && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                      <PaperClipIcon className="h-3.5 w-3.5 shrink-0" />
                      <span>PDF: {gestorParecerAnexo.nomeOriginal}</span>
                    </div>
                  )}
                  {etapaAtual === 'GESTOR_PARCERIA' && workflowAction === 'aprovar' && !gestorParecerAnexo && (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      Anexe o parecer técnico em PDF (obrigatório) antes de aprovar esta etapa.
                    </div>
                  )}
                </div>
              )}

              {requiredAnexo && !hasRequiredAnexo && workflowAction === 'aprovar' && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Antes de aprovar, anexe o documento obrigatório da etapa: <strong>{requiredAnexo.label}</strong>.
                </div>
              )}
              {requiredAnexoSecundario && !hasRequiredAnexoSecundario && workflowAction === 'aprovar' && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Antes de aprovar, anexe o documento obrigatório da etapa: <strong>{requiredAnexoSecundario.label}</strong>.
                </div>
              )}
              {workflowAction === 'aprovar' && etapaAtual === 'COMISSAO_SELECAO' && parecerResumo?.membros?.length ? (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Pareceres da comissão — {parecerResumo.pareceresConcluidos}/{parecerResumo.totalMembros || 0} concluídos
                  </p>
                  <div className="space-y-1.5">
                    {parecerResumo.membros.map(m => (
                      <div key={m.membroId} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-slate-700">{m.nomeCompleto || 'Membro não identificado'}{m.isPresidente ? ' (Presidente)' : ''}</span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                          m.statusParecer === 'APROVADO' ? 'bg-emerald-100 text-emerald-700' :
                          m.statusParecer === 'REPROVADO' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {m.statusParecer === 'APROVADO' ? 'Aprovado' : m.statusParecer === 'REPROVADO' ? 'Reprovado' : 'Pendente'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="mt-5 flex justify-end gap-2">
                <button onClick={closeWorkflowModal} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700">
                  Cancelar
                </button>
                <button
                  onClick={handleWorkflowAction}
                  disabled={workflowLoading || (workflowAction === 'aprovar' && etapaAtual === 'GESTOR_PARCERIA' && !gestorParecerAnexo)}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {workflowLoading ? 'Salvando...' : 'Confirmar ação'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAdminFaseModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-slate-950/40" onClick={() => { setShowAdminFaseModal(false); setAdminFaseStatus(''); setAdminFaseEtapa(''); setAdminFaseMotivo(''); }} />
            <div className="relative z-10 mx-4 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
              <p className="text-lg font-semibold text-slate-900">Editar fase do plano (Admin)</p>
              <p className="mt-1 text-sm text-slate-500">
                Fase atual: <strong>{plan?.etapaAtual ? (ETAPA_LABEL[plan.etapaAtual] || plan.etapaAtual) : '—'}</strong>
                {' · '}Status: <strong>{plan?.status || '—'}</strong>
              </p>
              <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                Ao retroceder uma fase, os dados jurídicos das fases desfeitas serão removidos automaticamente: procurador jurídico, análise jurídica e aprovação do Prefeito. O gestor da parceria e a comissão de seleção (com seus documentos) são preservados.
              </div>

              {(() => {
                const ETAPAS_ORDEM = ['ENVIO', 'ORCAMENTO', 'CONVENIOS_ANALISE', 'GESTOR_PARCERIA', 'COMISSAO_SELECAO', 'CONVENIOS_MINUTA', 'PROCURADORIA_JURIDICA', 'GESTOR_JURIDICO_REVISAO', 'CONVENIOS_FORMALIZACAO', 'CONCLUIDO'];
                const etapaAtualIdx = plan?.etapaAtual ? ETAPAS_ORDEM.indexOf(plan.etapaAtual) : ETAPAS_ORDEM.length;
                const ETAPAS_POR_STATUS: Record<string, { value: string; label: string }[]> = {
                  RASCUNHO: [],
                  AGUARDANDO_ASSINATURA: [{ value: 'ENVIO', label: 'Envio (aguardando assinatura da instituição)' }],
                  EM_ANALISE: [
                    { value: 'ORCAMENTO', label: 'Orçamento' },
                    { value: 'CONVENIOS_ANALISE', label: 'Convênios — análise inicial' },
                    { value: 'GESTOR_PARCERIA', label: 'Gestor da parceria' },
                    { value: 'COMISSAO_SELECAO', label: 'Comissão de seleção' },
                    { value: 'CONVENIOS_MINUTA', label: 'Convênios — minuta' },
                    { value: 'PROCURADORIA_JURIDICA', label: 'Procuradoria jurídica (PGM)' },
                    { value: 'GESTOR_JURIDICO_REVISAO', label: 'Revisão jurídica' },
                    { value: 'CONVENIOS_FORMALIZACAO', label: 'Convênios — formalização' },
                  ],
                  DEVOLVIDO_CORRECAO: [
                    { value: 'ORCAMENTO', label: 'Orçamento' },
                    { value: 'CONVENIOS_ANALISE', label: 'Convênios — análise inicial' },
                    { value: 'GESTOR_PARCERIA', label: 'Gestor da parceria' },
                    { value: 'COMISSAO_SELECAO', label: 'Comissão de seleção' },
                    { value: 'CONVENIOS_MINUTA', label: 'Convênios — minuta' },
                    { value: 'PROCURADORIA_JURIDICA', label: 'Procuradoria jurídica (PGM)' },
                    { value: 'GESTOR_JURIDICO_REVISAO', label: 'Revisão jurídica' },
                  ],
                  REPROVADO: [
                    { value: 'ORCAMENTO', label: 'Orçamento' },
                    { value: 'CONVENIOS_ANALISE', label: 'Convênios — análise inicial' },
                    { value: 'GESTOR_PARCERIA', label: 'Gestor da parceria' },
                    { value: 'COMISSAO_SELECAO', label: 'Comissão de seleção' },
                    { value: 'PROCURADORIA_JURIDICA', label: 'Procuradoria jurídica (PGM)' },
                  ],
                  APROVADO: [{ value: 'CONCLUIDO', label: 'Concluído' }],
                  CONCLUIDO: [{ value: 'CONCLUIDO', label: 'Concluído' }],
                };
                const etapasDisponiveis = (ETAPAS_POR_STATUS[adminFaseStatus] ?? [])
                  .filter(e => ETAPAS_ORDEM.indexOf(e.value) < etapaAtualIdx);
                return (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Novo status</label>
                      <select
                        value={adminFaseStatus}
                        onChange={e => { setAdminFaseStatus(e.target.value); setAdminFaseEtapa(''); }}
                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      >
                        <option value="">Selecione...</option>
                        <option value="RASCUNHO">Rascunho</option>
                        <option value="AGUARDANDO_ASSINATURA">Aguardando assinatura</option>
                        <option value="EM_ANALISE">Em análise</option>
                        <option value="DEVOLVIDO_CORRECAO">Devolvido para correção</option>
                        <option value="REPROVADO">Reprovado</option>
                        <option value="APROVADO">Aprovado</option>
                        <option value="CONCLUIDO">Concluído</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Nova etapa</label>
                      <select
                        value={adminFaseEtapa}
                        onChange={e => setAdminFaseEtapa(e.target.value)}
                        disabled={!adminFaseStatus || etapasDisponiveis.length === 0}
                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-slate-100 disabled:text-slate-400"
                      >
                        <option value="">{etapasDisponiveis.length === 0 ? '(não aplicável)' : 'Selecione...'}</option>
                        {etapasDisponiveis.map(e => (
                          <option key={e.value} value={e.value}>{e.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })()}

              <div className="mt-4">
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Motivo (obrigatório)</label>
                <textarea
                  value={adminFaseMotivo}
                  onChange={e => setAdminFaseMotivo(toUpperText(e.target.value))}
                  rows={3}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
                  placeholder="Descreva o motivo da alteração manual..."
                />
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  onClick={() => { setShowAdminFaseModal(false); setAdminFaseStatus(''); setAdminFaseEtapa(''); setAdminFaseMotivo(''); }}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAdminEditarFase}
                  disabled={adminFaseLoading || !adminFaseStatus || !adminFaseMotivo.trim()}
                  className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                >
                  {adminFaseLoading ? 'Salvando...' : 'Confirmar alteração'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Plan info – EDIT sub-mode with tabs */}
        {isEditingHeader && (
          <form onSubmit={handleSavePlan} className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700 mb-1">Editar Dados do Plano</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <GuidanceCard title="Orientação de preenchimento">
                    Revise os dados gerais com foco em coerência: o objeto da parceria deve estar alinhado com a realidade apresentada, com a forma de execução e com as metas e itens que serão cadastrados depois.
                  </GuidanceCard>
                </div>
                {/* Título */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Título <span className="text-red-500">*</span></label>
                  <input type="text" value={planoForm.titulo}
                    onChange={e => setPlanoForm(f => ({ ...f, titulo: toUpperText(e.target.value) }))}
                    maxLength={200}
                    placeholder="Ex: Fortalecimento do atendimento social da instituição"
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${planErrors.titulo ? 'border-red-400' : 'border-gray-300'}`} />
                  {planErrors.titulo && <p className="text-xs text-red-500 mt-1">{planErrors.titulo}</p>}
                  <p className="text-xs text-gray-500 mt-1">Dê preferência a um título que deixe clara a finalidade do plano.</p>
                </div>

                {/* Emenda */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Emenda Vinculada</label>
                  <select value={planoForm.emendaId}
                    onChange={e => {
                      const emId = e.target.value;
                      setPlanoForm(f => ({ ...f, emendaId: emId }));
                      if (!emId) return;
                      const em = emendas.find((x: any) => x.id === emId);
                      if (!em) return;
                      setPlanoForm(f => ({ ...f, descricao: em.description ? String(em.description) : f.descricao, valor: em.value != null ? formatCurrency(String(em.value)) : f.valor }));
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— Nenhuma —</option>
                    {emendas.map((em: any) => (
                      <option key={em.id} value={em.id}>
                        {em.officialCode || em.id}{em.value != null ? ` — ${currencyFmt.format(Number(em.value))}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Modalidade — read-only per PT-01 */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Modalidade</label>
                  <input
                    type="text"
                    value={planoForm.modalidade === 'TERMO_COLABORACAO' ? 'Termo de colaboração' : 'Termo de fomento'}
                    readOnly
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-100 cursor-not-allowed text-gray-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Definida pelo órgão concedente. Não editável.</p>
                </div>

                {/* Descrição */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Descrição</label>
                  <textarea value={planoForm.descricao}
                    onChange={e => setPlanoForm(f => ({ ...f, descricao: toUpperText(e.target.value) }))}
                    rows={2}
                    placeholder="Resumo do objetivo do plano e do público beneficiado"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <p className="text-xs text-gray-500 mt-1">Use este campo como uma visão rápida do plano para análise inicial.</p>
                </div>

                {/* RF03 - Objeto da parceria */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Objeto da parceria <span className="text-red-500">*</span></label>
                  <textarea value={planoForm.objetoParceria}
                    onChange={e => setPlanoForm(f => ({ ...f, objetoParceria: toUpperText(e.target.value) }))}
                    rows={2}
                    placeholder="Informe o que será executado e qual resultado a parceria pretende alcançar"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <p className="text-xs text-gray-500 mt-1">Escreva de forma objetiva e conecte esse texto às metas do plano.</p>
                </div>

                {/* RF03 - Descrição da realidade */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Descrição da realidade <span className="text-red-500">*</span></label>
                  <textarea value={planoForm.descricaoRealidade}
                    onChange={e => setPlanoForm(f => ({ ...f, descricaoRealidade: toUpperText(e.target.value) }))}
                    rows={2}
                    placeholder="Explique o contexto, a necessidade e a demanda que justificam a parceria"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <p className="text-xs text-gray-500 mt-1">Apresente o cenário local e justifique por que o recurso será aplicado nessa finalidade.</p>
                </div>

                {/* RF03 - Forma de execução */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Forma de execução <span className="text-red-500">*</span></label>
                  <textarea value={planoForm.formaExecucao}
                    onChange={e => setPlanoForm(f => ({ ...f, formaExecucao: toUpperText(e.target.value) }))}
                    rows={2}
                    placeholder="Descreva como as atividades serão executadas e monitoradas"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <p className="text-xs text-gray-500 mt-1">Explique a execução prática: atividades, responsáveis, frequência e forma de acompanhamento.</p>
                </div>

                {/* Valor */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Valor do Plano</label>
                  <input type="text" value={planoForm.valor}
                    onChange={e => setPlanoForm(f => ({ ...f, valor: e.target.value }))}
                    onBlur={handleValueBlur} placeholder="R$ 0,00"
                    readOnly={Boolean(planoForm.emendaId && emendas.find(em => em.id === planoForm.emendaId && em.value != null))}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${planoForm.emendaId && emendas.find(em => em.id === planoForm.emendaId && em.value != null) ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'}`} />
                </div>

                {/* Data de início */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Data de início <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={periodoInicioInputRef}
                    type="month"
                    value={planoForm.periodoInicio}
                    disabled={!canEdit}
                    onChange={e => {
                      const val = e.target.value;
                      setPlanoForm(f => ({ ...f, periodoInicio: val }));
                      if (planoForm.periodoFim && val > planoForm.periodoFim) {
                        setPlanErrors(errs => ({ ...errs, periodoFim: 'A data de término deve ser posterior à data de início' }));
                      } else {
                        setPlanErrors(errs => { const { periodoInicio: _a, periodoFim: _b, ...rest } = errs; return rest; });
                      }
                    }}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''} ${planErrors.periodoInicio ? 'border-red-400' : 'border-gray-300'}`}
                  />
                  {planErrors.periodoInicio && <p className="text-xs text-red-500 mt-1">{planErrors.periodoInicio}</p>}
                  <p className="text-xs text-gray-500 mt-1">Obrigatório para envio à análise.</p>
                </div>

                {/* Data de término */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Data de término <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="month"
                    value={planoForm.periodoFim}
                    disabled={!canEdit}
                    onChange={e => {
                      const val = e.target.value;
                      setPlanoForm(f => ({ ...f, periodoFim: val }));
                      if (planoForm.periodoInicio && val < planoForm.periodoInicio) {
                        setPlanErrors(errs => ({ ...errs, periodoFim: 'A data de término deve ser posterior à data de início' }));
                      } else {
                        setPlanErrors(errs => { const { periodoFim: _b, ...rest } = errs; return rest; });
                      }
                    }}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''} ${planErrors.periodoFim ? 'border-red-400' : 'border-gray-300'}`}
                  />
                  {planErrors.periodoFim && <p className="text-xs text-red-500 mt-1">{planErrors.periodoFim}</p>}
                  <p className="text-xs text-gray-500 mt-1">Deve ser posterior à data de início.</p>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 sm:col-span-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Status do plano</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">{STATUS_LABEL[plan?.status || planoForm.status] || plan?.status || planoForm.status}</p>
                  <p className="mt-1 text-xs text-slate-500">O status é controlado pelas ações do workflow, não por edição manual.</p>
                </div>
              </div>

            {isEmendaDiretaAtual() && (
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Dados Bancários <span className="font-normal text-gray-400">(opcional na Emenda Direta)</span>
              </label>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Banco</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={bankFilter || planoForm.banco || ''}
                      onChange={e => { setBankFilter(e.target.value); setShowBankDropdown(true); }}
                      onFocus={() => setShowBankDropdown(true)}
                      onKeyDown={(e) => {
                        const list = filteredBanks;
                        if (!list.length) return;
                        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedBankIndex(i => Math.min(i + 1, list.length - 1)); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedBankIndex(i => Math.max(i - 1, 0)); }
                        else if (e.key === 'Enter') { e.preventDefault(); const sel = list[highlightedBankIndex >= 0 ? highlightedBankIndex : 0]; if (sel) { setPlanoForm(f => ({ ...f, banco: sel.name })); setBankCode(sel.code); setShowBankDropdown(false); setBankFilter(''); } }
                      }}
                    onBlur={() => setTimeout(() => { setShowBankDropdown(false); setBankFilter(''); setHighlightedBankIndex(-1); }, 120)}
                    className="w-full border rounded px-3 py-2 text-sm pr-8"
                    placeholder="Digite nome ou código do banco" />
                    {showBankDropdown && (
                      <div className="absolute z-40 left-0 right-0 bg-white border rounded mt-1 max-h-48 overflow-auto shadow">
                        {filteredBanks.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">Nenhum banco disponível</div>
                        ) : (
                          filteredBanks.map((b, idx) => (
                            <button key={b.code} type="button" onMouseDown={() => { setPlanoForm(f => ({ ...f, banco: b.name })); setBankCode(b.code); setBankFilter(''); setShowBankDropdown(false); }} onMouseEnter={() => setHighlightedBankIndex(idx)} className={`w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex items-center justify-between ${idx === highlightedBankIndex ? 'bg-blue-50' : ''}`}>
                              <span>{b.code} — {b.name}</span>
                              {b.code === bankCode && <span className="text-blue-600 text-xs">Selecionado</span>}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Agência</label>
                  <input type="text" value={planoForm.agencia || ''} onChange={e => setPlanoForm(f => ({ ...f, agencia: toUpperText(e.target.value) }))} className="w-full border rounded px-3 py-2 text-sm" placeholder="Agência" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Conta Bancária (20 dígitos)</label>
                  <input ref={contaInputRef} type="text" value={planoForm.contaBancaria || ''} onChange={e => handleContaChange(e.target.value)} onFocus={handleContaFocus} onBlur={handleContaBlur} maxLength={24} className="w-full border rounded px-3 py-2 text-sm" placeholder="0000 0000 0000 0000 0000" />
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-600">
                    Comprovante de conta zerada{' '}
                    {isEmendaDiretaAtual()
                      ? <span className="font-normal text-gray-400">(opcional na Emenda Direta)</span>
                      : <span className="text-red-500">*</span>}
                  </label>
                  {anexos.some(a => a.tipoDocumento === 'COMPROVANTE_CONTA_ZERADA') && (
                    <span className="text-xs text-emerald-600 font-medium">? Já enviado</span>
                  )}
                </div>
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 hover:bg-slate-50">
                  <svg className="h-4 w-4 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                  <span className="text-sm text-slate-600">{comprovanteFile ? comprovanteFile.name : 'Clique para selecionar o arquivo'}</span>
                  <input type="file" className="hidden" onChange={e => setComprovanteFile(e.target.files?.[0] || null)} />
                </label>
                <button
                  type="button"
                  onClick={handleUploadComprovante}
                  disabled={!comprovanteFile || uploadingComprovante}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingComprovante ? 'Enviando...' : 'Enviar comprovante'}
                </button>
              </div>

            {planErrors.general && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{planErrors.general}</p>}

            {validationMessages.length > 0 && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {validationMessages.join(' | ')}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={savingPlan}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {savingPlan ? 'Salvando...' : 'Salvar Alterações'}
              </button>
              <button type="button" onClick={() => { setIsEditingHeader(false); setPlanErrors({}); }}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
            </div>
          </form>
        )}

      {activeSection === 'estrutura' && (
        <DetailSection
          eyebrow="Conteúdo"
          title="Estrutura do plano"
          description="Metas, cronograma, aplicação e desembolso ficam organizados em uma etapa própria do plano."
        >
          <GuidanceCard title="Como avançar nesta etapa">
            Comece por <strong>Metas</strong> e detalhe pelo menos um item em cada uma delas. O <strong>Cronograma de Execução</strong>, o <strong>Plano de Aplicação</strong> e o <strong>Cronograma de Desembolso</strong> são montados automaticamente com base nesses cadastros.
          </GuidanceCard>

          {needsContrapartida && (() => {
            const contrapartidaAnexo = anexos.find(a => a.tipoDocumento === 'CONTRAPARTIDA');
            const referenciaLabel = planEmendaValor > 0 ? 'Valor da emenda' : 'Valor do plano';
            return (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3">
                {/* Header */}
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Atenção: Contrapartida necessária</p>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-amber-700">
                      <div><span className="block text-amber-500">{referenciaLabel}</span><span className="font-semibold">{currencyFmt.format(contrapartidaReferencia)}</span></div>
                      <div><span className="block text-amber-500">Soma das metas</span><span className="font-semibold">{currencyFmt.format(contrapartidaBase)}</span></div>
                      <div><span className="block text-amber-500">Contrapartida</span><span className="font-semibold text-amber-900">{currencyFmt.format(diferencaContrapartida)}</span></div>
                    </div>
                    <p className="mt-2 text-xs text-amber-700">
                      A soma das metas supera o {planEmendaValor > 0 ? 'valor da emenda' : 'valor do plano'} em <strong>{currencyFmt.format(diferencaContrapartida)}</strong>. A instituição deverá arcar com esse excedente como contrapartida. Anexe o comprovante para prosseguir.{' '}
                      Consulte o <strong>Convênios</strong> em caso de dúvidas sobre como realizar a contrapartida.
                    </p>
                  </div>
                </div>

                {/* Status: already uploaded */}
                {contrapartidaAnexo ? (
                  <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <span className="text-sm font-semibold text-emerald-800">Comprovante de contrapartida enviado</span>
                    </div>
                    <p className="text-xs text-emerald-700">{contrapartidaAnexo.nomeOriginal}</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setAnexoViewer(contrapartidaAnexo)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        Visualizar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteContrapartida(contrapartidaAnexo.id)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        Remover e reenviar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-amber-200 bg-white p-3 space-y-2">
                    <label className="block text-xs font-medium text-gray-700">Comprovante de contrapartida <span className="text-red-500">*</span></label>
                    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-3 hover:bg-amber-100">
                      <svg className="h-5 w-5 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      <span className="text-sm text-amber-800">
                        {contrapartidaFile ? contrapartidaFile.name : 'Clique para selecionar o arquivo'}
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={e => setContrapartidaFile(e.target.files?.[0] || null)}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={handleUploadContrapartida}
                      disabled={!contrapartidaFile || uploadingContrapartida}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploadingContrapartida ? 'Enviando...' : 'Enviar comprovante'}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="border-b border-gray-100 bg-slate-50/70 px-2 py-2">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-5">
            {TAB_OPTIONS.map(({ key, label }) => {
              const finalLabel =
                key === 'metas' ? `${label} (${metas.length})` :
                label;

              return (
                <button
                  key={key}
                  onClick={() => handleSelectTab(key)}
                  className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === key
                      ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm'
                      : 'border-transparent bg-white text-gray-600 hover:border-slate-200 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {finalLabel}
                </button>
              );
            })}
          </div>
        </div>

        {/* -- TAB: INDICADORES ----------------------------------------------- */}
        {activeTab === 'indicadores' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-800">Indicadores Quantitativos do Plano</h2>
              {canEdit && !showNewIndicador && (
                <button
                  type="button"
                  onClick={() => { setShowNewIndicador(true); setIndicadorError(null); }}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  + Adicionar indicador
                </button>
              )}
            </div>

            <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              Os indicadores representam o resultado esperado do plano como um todo — por exemplo, <strong>500 pessoas atendidas</strong> ou <strong>200 kg de alimentos distribuídos</strong>. Eles não estão vinculados a metas individuais.
            </div>

            {indicadorError && <p className="text-xs text-red-500 mb-3">{indicadorError}</p>}

            {/* Formulário de novo indicador */}
            {showNewIndicador && (
              <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4 space-y-3">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Novo indicador</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Unidade <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newIndicador.unidade}
                      onChange={e => setNewIndicador(p => ({ ...p, unidade: toUpperText(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Ex: pessoas, quilos, atendimentos"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Quantidade <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="0.0001"
                      step="any"
                      value={newIndicador.quantidade}
                      onChange={e => setNewIndicador(p => ({ ...p, quantidade: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Ex: 500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Descrição (opcional)</label>
                  <input
                    type="text"
                    value={newIndicador.descricao}
                    onChange={e => setNewIndicador(p => ({ ...p, descricao: toUpperText(e.target.value) }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Contexto adicional sobre o indicador"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCreateIndicador}
                    disabled={savingIndicador}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingIndicador ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowNewIndicador(false); setIndicadorError(null); setNewIndicador({ unidade: '', quantidade: '', descricao: '' }); }}
                    className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Lista de indicadores */}
            {indicadores.length === 0 && !showNewIndicador ? (
              <div className="text-center py-10 text-gray-400">
                <div className="text-4xl mb-2">??</div>
                <p className="text-sm">Nenhum indicador cadastrado.</p>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setShowNewIndicador(true)}
                    className="mt-3 text-sm text-blue-600 hover:underline font-medium"
                  >
                    Adicionar o primeiro indicador
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {indicadores.map((ind, idx) => (
                  <div key={ind.id} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                    {editingIndicadorId === ind.id ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Unidade</label>
                            <input
                              type="text"
                              value={editIndicadorForm.unidade}
                              onChange={e => setEditIndicadorForm(p => ({ ...p, unidade: toUpperText(e.target.value) }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                              autoFocus
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Quantidade</label>
                            <input
                              type="number"
                              min="0.0001"
                              step="any"
                              value={editIndicadorForm.quantidade}
                              onChange={e => setEditIndicadorForm(p => ({ ...p, quantidade: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
                          <input
                            type="text"
                            value={editIndicadorForm.descricao}
                            onChange={e => setEditIndicadorForm(p => ({ ...p, descricao: toUpperText(e.target.value) }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={handleUpdateIndicador} disabled={savingIndicador}
                            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
                            {savingIndicador ? '...' : 'Salvar'}
                          </button>
                          <button type="button" onClick={() => { setEditingIndicadorId(null); setIndicadorError(null); }}
                            className="px-3 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-gray-50">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-800">
                              {new Intl.NumberFormat('pt-BR').format(ind.quantidade)}
                            </span>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              {ind.unidade}
                            </span>
                          </div>
                          {ind.descricao && (
                            <p className="text-xs text-gray-500 mt-0.5">{ind.descricao}</p>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => { setEditingIndicadorId(ind.id); setEditIndicadorForm({ unidade: ind.unidade, quantidade: String(ind.quantidade), descricao: ind.descricao || '' }); setIndicadorError(null); }}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-colors"
                              title="Editar"
                            ><PencilIcon className="w-4 h-4" /></button>
                            <button
                              type="button"
                              onClick={() => handleDeleteIndicador(ind.id)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Excluir"
                            ><TrashIcon className="w-4 h-4" /></button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* -- TAB: CRONOGRAMA DE EXECUÇÃO ------------------------------------- */}
        {activeTab === 'cronograma' && (() => {
          const metasComExecucao = new Set(cronogramaExecucaoPlanejado.flatMap((row) => row.metas)).size;

          return (
            <div id="cronograma-section" className="p-6 space-y-4">
              <h2 className="text-base font-semibold text-gray-800">Cronograma de Execução</h2>

              <GuidanceCard title="Como preencher o cronograma de execução">
                Os meses são gerados automaticamente a partir das datas de início e fim dos itens vinculados às metas. Preencha o valor de cada mês e garanta que a soma total seja exatamente igual ao valor planejado do plano.
              </GuidanceCard>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Meses planejados</p>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{cronogramaExecucaoPlanejado.length}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Metas com execução</p>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{metasComExecucao}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Valor total planejado</p>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{currencyFmt.format(targetCronogramaValue)}</p>
                </div>
              </div>

              {cronogramaExecucaoMesesLoaded && targetCronogramaValue > 0 && (
                <div className={`rounded-xl border px-4 py-3 flex items-center justify-between ${Math.abs(cronogramaDiff) < 0.01 ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                  <div className="text-sm">
                    <span className="text-gray-600">Soma dos meses: </span>
                    <span className="font-semibold text-gray-900">{currencyFmt.format(totalCronograma)}</span>
                    <span className="text-gray-400 mx-2">/</span>
                    <span className="text-gray-600">Valor do plano: </span>
                    <span className="font-semibold text-gray-900">{currencyFmt.format(targetCronogramaValue)}</span>
                  </div>
                  <div className={`text-sm font-semibold ${Math.abs(cronogramaDiff) < 0.01 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {Math.abs(cronogramaDiff) < 0.01
                      ? 'Valores conferem ?'
                      : cronogramaDiff > 0
                        ? `Excede em ${currencyFmt.format(cronogramaDiff)}`
                        : `Faltam ${currencyFmt.format(-cronogramaDiff)}`}
                  </div>
                </div>
              )}

              {!cronogramaExecucaoMesesLoaded ? (
                <div className="text-center py-8 text-gray-400 text-sm">Carregando...</div>
              ) : cronogramaExecucaoPlanejado.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center text-gray-400 text-sm">
                  Nenhum mês foi gerado ainda. Cadastre itens com data de início e fim para montar o cronograma automaticamente.
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr className="border-b border-slate-200">
                          <th className="w-[15rem] px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-700">Mês</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-700">Metas</th>
                          <th className="w-[15rem] px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-700">Total do mês</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronogramaExecucaoPlanejado.map((row) => (
                          <tr key={row.mes} className="border-b border-slate-100 last:border-b-0 odd:bg-white even:bg-slate-50/50 hover:bg-blue-50/40">
                            <td className="whitespace-nowrap px-4 py-2.5 align-middle font-semibold text-slate-900">
                              <span className="text-sm font-bold text-slate-950">{formatMonthLabel(row.mes)}</span>
                            </td>
                            <td className="px-4 py-2.5 align-middle">
                              <div className="flex flex-wrap gap-1.5">
                                {row.metas.map((meta) => (
                                  <span key={`${row.mes}-${meta}`} className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800 ring-1 ring-blue-200">
                                    {meta}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 align-middle">
                              <div className="ml-auto flex max-w-[220px] items-center justify-end gap-2">
                                {savingCronogramaMes === row.mes && (
                                  <span className="text-xs font-medium text-blue-600">Salvando...</span>
                                )}
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={cronogramaExecucaoDrafts[row.mes] ?? ''}
                                  onChange={(e) => handleCronogramaExecucaoDraftChange(row.mes, e.target.value)}
                                  onBlur={() => handleCronogramaExecucaoDraftBlur(row.mes)}
                                  readOnly={!canEdit}
                                  placeholder="R$ 0,00"
                                  className={`h-9 w-40 rounded-lg border border-slate-300 px-3 text-right text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${!canEdit ? 'bg-slate-50 text-slate-600' : 'bg-white'}`}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {cronogramaExecucaoError && (
                <p className="text-xs text-red-600">{cronogramaExecucaoError}</p>
              )}
            </div>
          );
        })()}

        {/* -- TAB: METAS ---------------------------------------------------- */}
        {activeTab === 'metas' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-800">Metas do Plano</h2>
              {canEdit ? (
                <button onClick={() => { setShowNewMeta(v => !v); setMetaError(null); setNewMeta(emptyMeta()); }}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                  + Nova Meta
                </button>
              ) : (
                <div className="text-xs text-gray-400 italic">Criação de metas bloqueada após aprovação.</div>
              )}
            </div>

            <div className="mb-4 grid gap-3 lg:grid-cols-2">
              <GuidanceCard title="O que informar em cada meta">
                Cadastre resultados relevantes do plano, não apenas tarefas soltas. A descrição deve mostrar o objetivo daquela meta e cada meta precisa ter pelo menos um item associado.
              </GuidanceCard>
              <GuidanceCard title="Como preencher os itens" tone="amber">
                Em cada item, detalhe o que será adquirido ou executado, informe unidade, quantidade, período e pesquisa de preços. O valor total é calculado automaticamente a partir da quantidade e do valor unitário.
              </GuidanceCard>
            </div>

            {showNewMeta && (
              <form onSubmit={handleCreateMeta} className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm font-semibold text-blue-700 mb-3">Nova Meta</p>
                <p className="mb-3 text-xs leading-5 text-blue-800">Descreva um resultado a ser alcançado pela parceria. Se preferir, deixe o título em branco e o sistema seguirá a numeração automática.</p>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Título</label>
                    <input value={newMeta.titulo} onChange={e => setNewMeta(p => ({ ...p, titulo: toUpperText(e.target.value) }))}
                      className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Deixe em branco para usar Meta 1, Meta 2..." autoFocus />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Descrição</label>
                    <textarea value={newMeta.descricao} onChange={e => setNewMeta(p => ({ ...p, descricao: toUpperText(e.target.value) }))}
                      rows={5}
                      className="w-full resize-y border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Descreva o resultado esperado, o público alcançado e o vínculo com o objeto da parceria" />
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                    O valor da meta será calculado automaticamente pela soma dos itens cadastrados.
                  </div>
                </div>
                {metaError && <p className="text-red-600 text-xs mt-2">{metaError}</p>}
                <div className="flex gap-2 mt-3">
                  <button type="submit" disabled={savingMeta}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                    {savingMeta ? 'Salvando...' : 'Salvar Meta'}
                  </button>
                  <button type="button" onClick={() => setShowNewMeta(false)}
                    className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cancelar
                  </button>
                </div>
              </form>
            )}

            {metas.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <div className="text-4xl mb-2">??</div>
                <p className="text-sm">Nenhuma meta cadastrada ainda.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {metas.map((m, idx) => (
                  <div key={m.id} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>

                      {editingMetaId === m.id ? (
                        <div className="flex-1 grid grid-cols-1 gap-3">
                          <input value={editMetaForm.titulo} onChange={e => setEditMetaForm(p => ({ ...p, titulo: toUpperText(e.target.value) }))}
                            className="border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="Título" autoFocus />
                          <textarea value={editMetaForm.descricao} onChange={e => setEditMetaForm(p => ({ ...p, descricao: toUpperText(e.target.value) }))}
                            rows={5}
                            className="resize-y border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="Descrição" />
                          <div className="max-w-xs rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                            Valor calculado automaticamente pelos itens.
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-800 text-sm">{m.titulo}</span>
                            {getMetaTotal(m) > 0 && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                {currencyFmt.format(getMetaTotal(m))}
                              </span>
                            )}
                            {(itemsByMeta[m.id!]?.length ?? 0) > 0 && (
                              <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs">
                                {itemsByMeta[m.id!].length} {itemsByMeta[m.id!].length === 1 ? 'item' : 'itens'}
                              </span>
                            )}
                            {(orcamentosByMeta[m.id!]?.length ?? 0) > 0 && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                (orcamentosByMeta[m.id!]?.length ?? 0) >= 3
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {orcamentosByMeta[m.id!].length}/3 orç.
                              </span>
                            )}
                          </div>
                          {m.descricao && <p className="text-xs text-gray-500 mt-0.5 truncate">{m.descricao}</p>}
                        </div>
                      )}

                      <div className="flex items-center gap-1 shrink-0">
                        {editingMetaId === m.id ? (
                          <div className="flex items-center gap-2">
                            <button onClick={handleSaveMeta} disabled={savingMeta}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
                              {savingMeta ? '...' : 'Salvar'}
                            </button>
                            <button onClick={() => setEditingMetaId(null)}
                              className="px-3 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-white">
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleMeta(m.id!)}
                              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                                expandedMetas.has(m.id!)
                                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                              }`}
                              title={expandedMetas.has(m.id!) ? 'Ocultar itens' : 'Ver itens'}
                            >
                              <span>{expandedMetas.has(m.id!) ? 'Ocultar itens' : 'Ver itens'}</span>
                              <span className={`rounded-full px-2 py-0.5 text-[11px] ${
                                expandedMetas.has(m.id!) ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {(itemsByMeta[m.id!]?.length ?? 0)}
                              </span>
                            </button>
                            {canEdit && (
                              <>
                                <div className="flex flex-col gap-0.5">
                                  <button onClick={() => handleMoveMeta(m.id!, 'up')} disabled={idx === 0 || isReorderingMeta}
                                    className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Mover para cima"><ChevronUpIcon className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => handleMoveMeta(m.id!, 'down')} disabled={idx === metas.length - 1 || isReorderingMeta}
                                    className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Mover para baixo"><ChevronDownIcon className="w-3.5 h-3.5" /></button>
                                </div>
                                <button onClick={() => { setEditingMetaId(m.id!); setEditMetaForm({ titulo: m.titulo || '', descricao: m.descricao || '' }); setMetaError(null); }}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-colors" title="Editar"><PencilIcon className="w-4 h-4" /></button>
                                <button onClick={() => handleDeleteMeta(m.id!)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Excluir"><TrashIcon className="w-4 h-4" /></button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {expandedMetas.has(m.id!) && (
                      <div className="border-t border-gray-200 bg-white px-4 pb-4 pt-3">
                        {itemError && <p className="text-red-600 text-xs mb-2">{itemError}</p>}
                        {(itemsByMeta[m.id!] || []).length > 0 && (
                          <div className="space-y-1.5 mb-3">
                            {(itemsByMeta[m.id!] || []).map((item, iIdx) => (
                              <div key={item.id} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                                <span className="text-xs text-gray-400 w-5 text-center shrink-0">{iIdx + 1}</span>
                                {editingItemId === item.id ? (
                                  <div className="flex-1 grid grid-cols-1 gap-3">
                                    <div>
                                      <label className="mb-1 block text-xs font-medium text-gray-600">Descrição</label>
                                      <textarea value={editItemForm.descricao} onChange={e => setEditItemForm(p => ({ ...p, descricao: toUpperText(e.target.value) }))}
                                        rows={5} autoFocus
                                        className="w-full resize-y border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" placeholder="Descrição" />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div>
                                      <label className="mb-1 block text-xs font-medium text-gray-600">Tipo</label>
                                      <select value={editItemForm.tipo || ''} onChange={e => setEditItemForm(p => ({ ...p, tipo: e.target.value }))}
                                          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
                                          <option value="">— Selecione —</option>
                                          <option value="CUSTEIO">Custeio</option>
                                          <option value="INVESTIMENTO">Investimento</option>
                                        </select>
                                      <p className="mt-1 text-[11px] text-gray-500">Use <strong>Custeio</strong> para despesas de manutenção e execução. Use <strong>Investimento</strong> para aquisição de bens permanentes ou ampliação de estrutura.</p>
                                    </div>
                                    <div>
                                      <label className="mb-1 block text-xs font-medium text-gray-600">Valor unitário</label>
                                      <input type="text" inputMode="decimal" value={editItemForm.valorUnitario || ''} onChange={e => handleEditItemMoneyChange('valorUnitario', e.target.value)} onBlur={() => handleEditItemMoneyBlur('valorUnitario')}
                                          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" placeholder="R$ 0,00" />
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div>
                                      <label className="mb-1 block text-xs font-medium text-gray-600">Data de início</label>
                                      <input type="date" value={editItemForm.periodoInicio || ''} onChange={e => setEditItemForm(p => ({ ...p, periodoInicio: e.target.value }))}
                                          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                      <p className="mt-1 text-[11px] text-gray-500">Informe quando a execução deste item começa. A data deve estar dentro do período geral do plano.</p>
                                    </div>
                                    <div>
                                      <label className="mb-1 block text-xs font-medium text-gray-600">Data de fim</label>
                                      <input type="date" value={editItemForm.periodoFim || ''} onChange={e => setEditItemForm(p => ({ ...p, periodoFim: e.target.value }))}
                                          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                      <p className="mt-1 text-[11px] text-gray-500">Informe quando a execução termina. Essas datas alimentam o cronograma de execução demonstrativo, sem alterar o valor de pagamento das parcelas.</p>
                                    </div>
                                  </div>
                                </div>
                                ) : (
                                  <div className="flex-1 min-w-0 space-y-1.5">
                                    {/* Linha 1: badges de tipo e valor */}
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700">
                                        Item {iIdx + 1}
                                      </span>
                                      {item.tipo && (
                                        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
                                          {item.tipo === 'CUSTEIO' ? 'Custeio' : item.tipo === 'INVESTIMENTO' ? 'Investimento' : item.tipo}
                                        </span>
                                      )}
                                      {item.valor != null && (
                                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                          {currencyFmt.format(item.valor)}
                                        </span>
                                      )}
                                    </div>
                                    {/* Descrição do item */}
                                    {item.descricao && (
                                      <p className="text-sm text-slate-800 whitespace-pre-wrap leading-snug">{item.descricao}</p>
                                    )}
                                    {/* Linha 4: detalhes (unidade, qtd, valor unitário, período) */}
                                    <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                                      {item.unidade && (
                                        <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                                          Unidade: {item.unidade}
                                        </span>
                                      )}
                                      {item.quantidade != null && (
                                        <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                                          Qtd: {item.quantidade}
                                        </span>
                                      )}
                                      {item.valorUnitario != null && (
                                        <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                                          Valor unit.: {currencyFmt.format(item.valorUnitario)}
                                        </span>
                                      )}
                                      {(item.periodoInicio || item.periodoFim) && (
                                        <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                                          Período: {formatPeriodo(item.periodoInicio, item.periodoFim)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <div className="flex gap-1 shrink-0">
                                  {editingItemId === item.id ? (
                                    canEdit ? (
                                      <>
                                        <button onClick={() => handleSaveItem(m.id!)} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs">Salvar</button>
                                        <button onClick={() => setEditingItemId(null)} className="px-2 py-1 border rounded text-xs">Cancelar</button>
                                      </>
                                    ) : (
                                      <div className="text-xs text-gray-400 italic">Edição bloqueada</div>
                                    )
                                  ) : (
                                    canEdit ? (
                                      <>
                                        <button onClick={() => {
                                          setEditingItemId(item.id!);
                                          setEditItemForm({
                                            descricao: item.descricao || '',
                                            valor: item.valor != null ? formatCurrency(String(item.valor)) : '',
                                            tipo: item.tipo || '',
                                            periodoInicio: item.periodoInicio ? String(item.periodoInicio).substring(0, 10) : '',
                                            periodoFim: item.periodoFim ? String(item.periodoFim).substring(0, 10) : '',
                                            valorUnitario: item.valorUnitario != null ? formatCurrency(String(item.valorUnitario)) : '',
                                          });
                                          setItemError(null);
                                        }}
                                          className="inline-flex items-center rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100" title="Editar item">Editar</button>
                                        <button onClick={() => handleDeleteItem(m.id!, item.id!)}
                                          className="inline-flex items-center rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-100" title="Excluir item">Excluir</button>
                                      </>
                                    ) : (
                                      <div className="text-xs text-gray-400 italic">Ações bloqueadas</div>
                                    )
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {showNewItemFor === m.id ? (
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs font-semibold text-blue-700 mb-2">Novo Item</p>
                            <p className="mb-3 text-xs leading-5 text-blue-800">Detalhe o bem, serviço ou despesa necessária para cumprir esta meta. Informe tipo, período e valores de referência dos fornecedores.</p>
                            <div className="grid grid-cols-1 gap-3">
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">Descrição</label>
                                <textarea value={newItemByMeta[m.id!]?.descricao || ''}
                                  onChange={e => setNewItemByMeta(prev => ({ ...prev, [m.id!]: { ...emptyItem(), ...prev[m.id!], descricao: toUpperText(e.target.value) } }))}
                                  rows={5} autoFocus
                                  className="w-full resize-y border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                  placeholder="Especifique claramente o item, o uso previsto e como ele contribui para a meta" />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-gray-600">Tipo</label>
                                  <select value={newItemByMeta[m.id!]?.tipo || ''}
                                    onChange={e => setNewItemByMeta(prev => ({ ...prev, [m.id!]: { ...emptyItem(), ...prev[m.id!], tipo: e.target.value } }))}
                                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                                    <option value="">— Selecione —</option>
                                    <option value="CUSTEIO">Custeio</option>
                                    <option value="INVESTIMENTO">Investimento</option>
                                  </select>
                                  <p className="mt-1 text-[11px] text-gray-500">Use <strong>Custeio</strong> para despesas correntes e de manutenção. Use <strong>Investimento</strong> para bens permanentes ou melhorias estruturais.</p>
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-gray-600">Valor unitário</label>
                                  <input type="text" inputMode="decimal" value={newItemByMeta[m.id!]?.valorUnitario || ''}
                                    onChange={e => handleItemMoneyChange(m.id!, 'valorUnitario', e.target.value)}
                                    onBlur={() => handleItemMoneyBlur(m.id!, 'valorUnitario')}
                                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    placeholder="R$ 0,00" />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-gray-600">Data de início</label>
                                  <input type="date" value={newItemByMeta[m.id!]?.periodoInicio || ''}
                                    onChange={e => setNewItemByMeta(prev => ({ ...prev, [m.id!]: { ...emptyItem(), ...prev[m.id!], periodoInicio: e.target.value } }))}
                                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                  <p className="mt-1 text-[11px] text-gray-500">Informe quando a execução deste item começa. A data precisa estar dentro do período do plano.</p>
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-gray-600">Data de fim</label>
                                  <input type="date" value={newItemByMeta[m.id!]?.periodoFim || ''}
                                    onChange={e => setNewItemByMeta(prev => ({ ...prev, [m.id!]: { ...emptyItem(), ...prev[m.id!], periodoFim: e.target.value } }))}
                                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                  <p className="mt-1 text-[11px] text-gray-500">Informe quando a execução termina. Essas datas alimentam o cronograma de execução demonstrativo, sem alterar o valor de pagamento das parcelas.</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-2">
                              {canEdit ? (
                                <>
                                  <button onClick={() => handleCreateItem(m.id!)} disabled={savingItemMeta === m.id}
                                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
                                    {savingItemMeta === m.id ? 'Salvando...' : 'Adicionar'}
                                  </button>
                                  <button onClick={() => setShowNewItemFor(null)}
                                    className="px-3 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-white">
                                    Cancelar
                                  </button>
                                </>
                              ) : (
                                <div className="text-xs text-gray-400 italic">Adição de itens bloqueada após aprovação.</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          canEdit ? (
                            <button onClick={() => { setShowNewItemFor(m.id!); setItemError(null); }}
                              className="mt-1 text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                              + Adicionar item
                            </button>
                          ) : (
                            <div className="text-xs text-gray-400 italic">Adicionar itens bloqueado</div>
                          )
                        )}
                        {/* -- Orçamentos por meta ------------------------------------ */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                              Orçamentos (cotações/propostas)
                            </span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              (orcamentosByMeta[m.id!]?.length ?? 0) >= 3
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {orcamentosByMeta[m.id!]?.length ?? 0}/3
                            </span>
                          </div>

                          {/* Lista de orçamentos */}
                          {(orcamentosByMeta[m.id!] || []).length > 0 && (
                            <div className="space-y-1.5 mb-2">
                              {(orcamentosByMeta[m.id!] || []).map((orc, oIdx) => (
                                <div key={orc.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                  <span className="w-5 text-center text-xs text-gray-400 shrink-0">{oIdx + 1}</span>
                                  <div className="flex-1 min-w-0">
                                    {orc.valor != null && (
                                      <span className="text-sm font-semibold text-gray-800">
                                        {currencyFmt.format(orc.valor)}
                                      </span>
                                    )}
                                    {orc.nomeOriginal && (
                                      <AuthenticatedFileButton
                                        url={orcamentoMetaService.viewUrl(orc.id)}
                                        fileName={orc.nomeOriginal}
                                        className="ml-2 max-w-full truncate text-left text-xs text-blue-600 hover:underline"
                                      >
                                        {orc.nomeOriginal}
                                      </AuthenticatedFileButton>
                                    )}
                                    {orc.tamanhoBytes != null && (
                                      <span className="ml-1 text-xs text-gray-400">
                                        ({(orc.tamanhoBytes / 1024).toFixed(0)} KB)
                                      </span>
                                    )}
                                  </div>
                                  {canEdit && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteOrcamento(m.id!, orc.id)}
                                      className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                      aria-label="Remover orcamento"
                                      title="Remover orçamento"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* PT-10: Bloqueio ao atingir 3 */}
                          {(orcamentosByMeta[m.id!]?.length ?? 0) >= 3 ? (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                              Número máximo de orçamentos atingido. A quantidade está <strong>sujeita a análise</strong> pelo gestor.
                            </div>
                          ) : canEdit && (
                            showNewOrcamentoFor === m.id ? (
                              <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2 mt-1">
                                {orcamentoError && showNewOrcamentoFor === m.id && (
                                  <p className="text-xs text-red-500">{orcamentoError}</p>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  <div>
                                    <label className="text-xs font-medium text-gray-600 block mb-1">Valor (R$)</label>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={orcamentoValorByMeta[m.id!] || ''}
                                      onChange={e => setOrcamentoValorByMeta(p => ({ ...p, [m.id!]: sanitizeMoneyDraft(e.target.value) }))}
                                      onBlur={() => {
                                        setOrcamentoValorByMeta(p => ({ ...p, [m.id!]: formatMoneyOnBlur(p[m.id!]) }));
                                      }}
                                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                      placeholder="R$ 0,00"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-gray-600 block mb-1">Arquivo (cotação/proposta)</label>
                                    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 hover:bg-gray-100">
                                      <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                      <span className="text-xs text-gray-600 truncate">{orcamentoFileByMeta[m.id!] ? orcamentoFileByMeta[m.id!]!.name : 'Clique para selecionar (PDF, JPG, PNG)'}</span>
                                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => handleOrcamentoFileChange(m.id!, e.target.files?.[0] ?? null)} />
                                    </label>
                                  </div>
                                </div>
                                {orcamentoFileByMeta[m.id!] && orcamentoPreviewByMeta[m.id!] && (
                                  <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <img src={orcamentoPreviewByMeta[m.id!]!} alt="preview" className="w-10 h-10 object-cover rounded border" />
                                  </div>
                                )}
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleAddOrcamento(m.id!)}
                                    disabled={savingOrcamentoMeta === m.id}
                                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    {savingOrcamentoMeta === m.id ? 'Salvando...' : 'Salvar orçamento'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setShowNewOrcamentoFor(null); setOrcamentoError(null); }}
                                    className="px-3 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-gray-50"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => { setShowNewOrcamentoFor(m.id!); setOrcamentoError(null); }}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 mt-1"
                              >
                                + Adicionar orçamento
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* -- TAB: APLICAÇÃO ---------------------------------------------------- */}
        {activeTab === 'aplicacao' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-800">Plano de Aplicação de Recursos</h2>
              <div className="flex items-center gap-2">
                <button onClick={exportAplicacaoXlsx} className="px-3 py-1.5 bg-gray-800 text-white rounded text-sm">Exportar XLSX</button>
              </div>
            </div>
            <div className="mb-4">
              <GuidanceCard title="Como conferir esta aba">
                Esta visão consolida os itens por meta para facilitar a análise financeira. Use-a para revisar especificação, quantidade, valor unitário, total do item e pesquisa de preços antes do envio para aprovação.
              </GuidanceCard>
            </div>
            {aplicacaoLoading ? (
              <div className="text-center py-8 text-gray-400">Carregando...</div>
            ) : aplicacao.length === 0 ? (
              <div className="text-center py-8 text-gray-400">Nenhum dado de aplicação encontrado.</div>
            ) : (
              <div className="space-y-4">
                {aplicacao.map((m:any) => (
                  <div key={m.metaId} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <div className="p-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">{m.metaTitulo || `Meta ${aplicacao.findIndex((meta: any) => meta.metaId === m.metaId) + 1}`}</div>
                      </div>
                      <div className="text-sm font-bold text-gray-700">Subtotal: {currencyFmt.format(m.totalMeta || 0)}</div>
                    </div>
                    <div className="p-3 overflow-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                            <th className="pb-2">Item</th>
                            <th className="pb-2">Descrição</th>
                            <th className="pb-2">Unidade</th>
                            <th className="pb-2">Quantidade</th>
                            <th className="pb-2">Valor Unitário</th>
                            <th className="pb-2">Valor Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(m.items||[]).map((it:any) => (
                            <tr key={it.itemId || it.id} className="border-b last:border-b-0">
                              <td className="py-2 pr-3">{it.itemTitulo}</td>
                              <td className="py-2 pr-3">{it.descricao}</td>
                              <td className="py-2 pr-3">{it.unidade}</td>
                              <td className="py-2 pr-3">{formatNumber(it.quantidade)}</td>
                              <td className="py-2 pr-3">{it.valorUnitario ? currencyFmt.format(it.valorUnitario) : '—'}</td>
                              <td className="py-2 pr-3">{it.valorTotal ? currencyFmt.format(it.valorTotal) : (it.valor ? currencyFmt.format(it.valor) : '—')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(m.orcamentos || []).length > 0 && (
                        <div className="mt-3 border-t border-gray-100 pt-3">
                          <p className="mb-2 text-xs font-semibold text-gray-500">
                            Pesquisa de fornecimento ({(m.orcamentos || []).length} orçamento{(m.orcamentos || []).length !== 1 ? 's' : ''})
                          </p>
                          <div className="space-y-1">
                            {(m.orcamentos || []).map((orc: any, oIdx: number) => (
                              <div key={orc.id} className="flex items-center gap-3 text-sm">
                                <span className="w-6 shrink-0 text-right text-xs text-gray-400">#{oIdx + 1}</span>
                                <span className="font-medium text-gray-700">{currencyFmt.format(orc.valor)}</span>
                                {orc.nomeOriginal && (
                                  <AuthenticatedFileButton
                                    url={`/api/orcamentos-meta/${orc.id}/view`}
                                    fileName={orc.nomeOriginal}
                                    className="truncate text-left text-xs text-blue-600 hover:underline"
                                  >
                                    {orc.nomeOriginal}
                                  </AuthenticatedFileButton>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="text-right text-sm font-bold">Total Geral: {currencyFmt.format(aplicacao.reduce((s,a)=>s + (Number(a.totalMeta||0)),0))}</div>
              </div>
            )}
          </div>
        )}

        {/* -- TAB: DESEMBOLSO ---------------------------------------------------- */}
        {activeTab === 'desembolso' && (() => {
          const planEmendaValor = toMoneyNumber(plan?.emendaValor ?? plan?.emenda?.value ?? linkedEmenda?.value ?? 0);
          const isSingleForced = planEmendaValor > 0 && planEmendaValor <= 100000;
          const parcelasSum = parcelas.reduce((s, p) => s + toMoneyNumber(p.valor), 0);
          const parcelaDiff = parcelasSum - planEmendaValor;
          const currentYear = new Date().getFullYear();
          const hasLateMonth = parcelas.some(p => p.mes > `${currentYear}-12`);
          const atLimit = parcelas.length >= 6;
          return (
            <div className="p-6 space-y-4">
              <h2 className="text-base font-semibold text-gray-800">Cronograma de Desembolso</h2>

              <GuidanceCard title="Como preencher o cronograma" tone="emerald">
                Informe as parcelas que serão usadas no pagamento do termo de fomento. Metas, itens, indicadores e orçamentos são demonstrativos e não alteram esse valor. A soma das parcelas deve ser exatamente igual ao valor da emenda vinculada, com no máximo 6 parcelas por plano.
              </GuidanceCard>

              {/* PT-11 single-forced banner */}
              {isSingleForced && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  O valor da emenda é de <strong>{currencyFmt.format(planEmendaValor)}</strong>, portanto o desembolso será realizado em <strong>parcela única</strong>.
                </div>
              )}

              {/* PT-13 late-month warning */}
              {hasLateMonth && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-1">
                  <p className="font-semibold">Atenção: parcelas além de dezembro/{currentYear}</p>
                  <p>O restante do recurso será repassado em parcela única no mês de dezembro. O controle do recurso é de responsabilidade da instituição.</p>
                </div>
              )}

              {/* PT-12 totalizer */}
              {planEmendaValor > 0 && (
                <div className={`rounded-xl border px-4 py-3 flex items-center justify-between ${Math.abs(parcelaDiff) < 0.01 ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                  <div className="text-sm">
                    <span className="text-gray-600">Soma das parcelas: </span>
                    <span className="font-semibold text-gray-900">{currencyFmt.format(parcelasSum)}</span>
                    <span className="text-gray-400 mx-2">/</span>
                    <span className="text-gray-600">Valor da emenda: </span>
                    <span className="font-semibold text-gray-900">{currencyFmt.format(planEmendaValor)}</span>
                  </div>
                  <div className={`text-sm font-semibold ${Math.abs(parcelaDiff) < 0.01 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {Math.abs(parcelaDiff) < 0.01
                      ? 'Valores conferem ?'
                      : parcelaDiff > 0
                        ? `Excede em ${currencyFmt.format(parcelaDiff)}`
                        : `Faltam ${currencyFmt.format(-parcelaDiff)}`}
                  </div>
                </div>
              )}

              {/* Parcelas list */}
              {!parcelasLoaded ? (
                <div className="text-center py-8 text-gray-400 text-sm">Carregando...</div>
              ) : parcelas.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center text-gray-400 text-sm">
                  Nenhuma parcela cadastrada. {!isSingleForced && 'Adicione a primeira parcela abaixo.'}
                </div>
              ) : (
                <div className="space-y-2">
                  {parcelas.map(p => (
                    <div key={p.id} className="rounded-xl border border-gray-200 bg-white">
                      {editingParcelaId === p.id ? (
                        <div className="p-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Mês (AAAA-MM)</label>
                              <input
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                value={editParcelaForm.mes}
                                onChange={e => setEditParcelaForm(f => ({ ...f, mes: e.target.value }))}
                                placeholder="2026-06"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Valor (R$)</label>
                              <input
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                value={editParcelaForm.valor}
                                onChange={e => setEditParcelaForm(f => ({ ...f, valor: sanitizeMoneyDraft(e.target.value) }))}
                                onBlur={() => setEditParcelaForm(f => ({ ...f, valor: formatMoneyOnBlur(f.valor) }))}
                                placeholder="R$ 0,00"
                                type="text"
                                inputMode="decimal"
                              />
                            </div>
                          </div>
                          {parcelaError && <p className="text-xs text-red-600">{parcelaError}</p>}
                          <div className="flex gap-2">
                            <button onClick={() => handleUpdateParcela(p.id)} disabled={savingParcela}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
                              {savingParcela ? 'Salvando…' : 'Salvar'}
                            </button>
                            <button onClick={() => { setEditingParcelaId(null); setParcelaError(null); }}
                              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm">
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className="text-sm font-medium text-gray-900">{p.mes}</span>
                            <span className="text-sm text-gray-700">{currencyFmt.format(Number(p.valor))}</span>
                            {p.mes > `${currentYear}-12` && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Além de dez/{currentYear}</span>
                            )}
                          </div>
                          {canEdit && (
                            <div className="flex gap-2">
                              <button onClick={() => { setEditingParcelaId(p.id); setEditParcelaForm({ mes: p.mes, valor: p.valor != null ? formatCurrency(String(p.valor)) : '' }); setParcelaError(null); }}
                                className="text-xs text-blue-600 hover:underline">Editar</button>
                              <button onClick={() => handleDeleteParcela(p.id)}
                                className="text-xs text-red-500 hover:underline">Remover</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add new parcela */}
              {canEdit && !isSingleForced && (
                <div>
                  {!atLimit && !showNewParcela && (
                    <button onClick={() => { setShowNewParcela(true); setParcelaError(null); }}
                      className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
                      <span className="text-lg leading-none">+</span> Adicionar parcela
                    </button>
                  )}
                  {atLimit && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                      Limite de 6 parcelas atingido.
                    </p>
                  )}
                  {showNewParcela && !atLimit && (
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                      <p className="text-sm font-medium text-gray-700">Nova parcela</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Mês (AAAA-MM)</label>
                          <input
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={newParcela.mes}
                            onChange={e => setNewParcela(f => ({ ...f, mes: e.target.value }))}
                            placeholder="2026-06"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Valor (R$)</label>
                          <input
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            value={newParcela.valor}
                            onChange={e => setNewParcela(f => ({ ...f, valor: sanitizeMoneyDraft(e.target.value) }))}
                            onBlur={() => setNewParcela(f => ({ ...f, valor: formatMoneyOnBlur(f.valor) }))}
                            placeholder="R$ 0,00"
                            type="text"
                            inputMode="decimal"
                          />
                        </div>
                      </div>
                      {parcelaError && <p className="text-xs text-red-600">{parcelaError}</p>}
                      <div className="flex gap-2">
                        <button onClick={handleAddParcela} disabled={savingParcela}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
                          {savingParcela ? 'Salvando…' : 'Salvar'}
                        </button>
                        <button onClick={() => { setShowNewParcela(false); setNewParcela({ mes: '', valor: '' }); setParcelaError(null); }}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* PT-11 single forced: show add form only if no parcela yet */}
              {canEdit && isSingleForced && parcelas.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Parcela única</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Mês (AAAA-MM)</label>
                      <input
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        value={newParcela.mes}
                        onChange={e => setNewParcela(f => ({ ...f, mes: e.target.value }))}
                        placeholder="2026-06"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Valor (R$)</label>
                      <input
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100"
                        value={planEmendaValor > 0 ? formatCurrency(String(planEmendaValor)) : 'R$ 0,00'}
                        readOnly
                      />
                    </div>
                  </div>
                  {parcelaError && <p className="text-xs text-red-600">{parcelaError}</p>}
                  <button
                    onClick={async () => {
                      if (!id) return;
                      setParcelaError(null);
                      if (!newParcela.mes.match(/^\d{4}-\d{2}$/)) return setParcelaError('Mês inválido. Use o formato AAAA-MM.');
                      setSavingParcela(true);
                      try {
                        const created = await parcelaDesembolsoService.create({ planoTrabalhoId: id, mes: newParcela.mes, valor: planEmendaValor });
                        setParcelas([created]);
                        setNewParcela({ mes: '', valor: '' });
                      } catch (err: any) {
                        setParcelaError(err?.response?.data?.error || 'Erro ao salvar parcela.');
                      } finally { setSavingParcela(false); }
                    }}
                    disabled={savingParcela}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
                    {savingParcela ? 'Salvando…' : 'Salvar parcela única'}
                  </button>
                </div>
              )}
            </div>
          );
        })()}
          </div>
        </DetailSection>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {conveniosIniciarRetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Iniciar Retificação</h2>
            <p className="text-sm text-gray-600">
              Descreva o que será ajustado. O plano entrará em modo retificação e poderá ser editado diretamente.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo <span className="text-red-500">*</span>
              </label>
              <textarea
                value={conveniosIniciarRetMotivo}
                onChange={(e) => setConveniosIniciarRetMotivo(toUpperText(e.target.value))}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm placeholder:text-gray-400"
                placeholder="Descreva os ajustes que serão realizados."
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setConveniosIniciarRetOpen(false); setConveniosIniciarRetMotivo(''); }}
                disabled={conveniosIniciarRetLoading}
                className="px-4 py-2 rounded border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleIniciarRetificacaoConvenios}
                disabled={conveniosIniciarRetLoading || !conveniosIniciarRetMotivo.trim()}
                className="px-4 py-2 rounded bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {conveniosIniciarRetLoading ? 'Iniciando…' : 'Iniciar Retificação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRetificacaoConveniosModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Solicitar Retificação</h2>
            <p className="text-sm text-gray-600">
              Selecione a etapa a partir da qual o plano deve ser retificado e descreva o que precisa ser ajustado.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etapa de retificação <span className="text-red-500">*</span>
              </label>
              <select
                value={retificacaoConveniosEtapa}
                onChange={(e) => setRetificacaoConveniosEtapa(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              >
                <option value="">Selecione a etapa...</option>
                <option value="CONVENIOS_ANALISE">{ETAPA_LABEL.CONVENIOS_ANALISE}</option>
                <option value="GESTOR_PARCERIA">{ETAPA_LABEL.GESTOR_PARCERIA}</option>
                <option value="COMISSAO_SELECAO">{ETAPA_LABEL.COMISSAO_SELECAO}</option>
                <option value="CONVENIOS_MINUTA">{ETAPA_LABEL.CONVENIOS_MINUTA}</option>
                <option value="PROCURADORIA_JURIDICA">{ETAPA_LABEL.PROCURADORIA_JURIDICA}</option>
                <option value="CONVENIOS_FORMALIZACAO">{ETAPA_LABEL.CONVENIOS_FORMALIZACAO}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Devolver a (ao reenviar)
              </label>
              <select
                value={retificacaoConveniosRetorno}
                onChange={(e) => setRetificacaoConveniosRetorno(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              >
                <option value="">Retornar à própria etapa (padrão)</option>
                <option value="CONVENIOS_ANALISE">{ETAPA_LABEL.CONVENIOS_ANALISE}</option>
                <option value="GESTOR_PARCERIA">{ETAPA_LABEL.GESTOR_PARCERIA}</option>
                <option value="COMISSAO_SELECAO">{ETAPA_LABEL.COMISSAO_SELECAO}</option>
                <option value="CONVENIOS_MINUTA">{ETAPA_LABEL.CONVENIOS_MINUTA}</option>
                <option value="PROCURADORIA_JURIDICA">{ETAPA_LABEL.PROCURADORIA_JURIDICA}</option>
                <option value="CONVENIOS_FORMALIZACAO">{ETAPA_LABEL.CONVENIOS_FORMALIZACAO}</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Use quando o apontamento é de outro setor (ex.: PGM): ao concluir e reenviar, o plano volta para a etapa escolhida em vez da atual.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                O que precisa ser retificado <span className="text-red-500">*</span>
              </label>
              <textarea
                value={retificacaoConveniosMotivo}
                onChange={(e) => setRetificacaoConveniosMotivo(toUpperText(e.target.value))}
                rows={4}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm placeholder:text-gray-400"
                placeholder="Descreva os ajustes necessários nos documentos e/ou no plano de trabalho."
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowRetificacaoConveniosModal(false); setRetificacaoConveniosMotivo(''); setRetificacaoConveniosEtapa(''); setRetificacaoConveniosRetorno(''); }}
                disabled={solicitandoRetificacaoConvenios}
                className="px-4 py-2 rounded border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSolicitarRetificacaoConvenios}
                disabled={solicitandoRetificacaoConvenios || !retificacaoConveniosMotivo.trim() || !retificacaoConveniosEtapa}
                className="px-4 py-2 rounded bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {solicitandoRetificacaoConvenios ? 'Solicitando…' : 'Confirmar Retificação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {timelineDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-slate-950/50" onClick={() => setTimelineDrawerOpen(false)} />
          <aside className="relative z-10 flex h-full w-full max-w-xl flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${workflowAccentClass}`}>Linha do tempo</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">Histórico do plano de trabalho</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{planCode}</p>
              </div>
              <button
                type="button"
                onClick={() => setTimelineDrawerOpen(false)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Fechar
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {timelineHistoricoPlano.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                  Nenhum histórico registrado para este plano.
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute bottom-0 left-[11px] top-0 w-px bg-gradient-to-b from-sky-300 via-slate-200 to-slate-100 dark:from-sky-700 dark:via-slate-700 dark:to-slate-800" />
                  <div className="space-y-5">
                    {timelineHistoricoPlano.map(item => {
                      const tone = planoHistoricoTone(item.acao);
                      const responsavel = item.usuarioNome || item.usuario || 'Sistema';
                      const isInicioEtapa = ['CREATE', 'ENVIAR_PARA_APROVACAO', 'APROVAR'].includes(item.acao);
                      return (
                        <div key={item.id} className="relative pl-9">
                          <div className={`absolute left-0 top-5 h-6 w-6 rounded-full border-4 border-white shadow-lg dark:border-slate-950 ${tone.dot}`} />
                          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="space-y-2">
                                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${tone.badge}`}>
                                  {planoHistoricoLabel(item.acao)}
                                </span>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                  {item.decisao || (item.etapa ? ETAPA_LABEL[item.etapa] || item.etapa : '') || 'Atualização do workflow'}
                                </p>
                              </div>
                              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{formatTimelineDateTime(item.dataHora)}</p>
                            </div>
                            {item.motivo && (
                              <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-600 dark:bg-slate-950 dark:text-slate-300">{item.motivo}</p>
                            )}
                            {item.parecerTexto && (
                              <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
                                <strong>Parecer:</strong> {item.parecerTexto}
                              </div>
                            )}
                            {item.acao === 'JUNTADA_DOCUMENTO' && (item.documentoTipo || item.documentoNome) && (
                              <div className="mt-2 flex items-center gap-2 rounded-xl border border-violet-100 bg-violet-50 px-3 py-2 text-sm text-violet-800 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-200">
                                <PaperClipIcon className="h-4 w-4 flex-shrink-0 text-violet-500" />
                                <span>{item.documentoNome || item.documentoTipo}</span>
                              </div>
                            )}
                            <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                              {isInicioEtapa ? `Iniciado por ${responsavel}` : `Ação por ${responsavel}`}
                              {item.acao !== 'JUNTADA_DOCUMENTO' && item.documentoTipo ? ` · Documento: ${item.documentoTipo}` : ''}
                              {item.acao !== 'JUNTADA_DOCUMENTO' && item.documentoNome ? ` · ${item.documentoNome}` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}

      {emendaPanelOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-slate-950/50" onClick={() => setEmendaPanelOpen(false)} />
          <aside className="relative z-10 flex h-full w-full max-w-xl flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${workflowAccentClass}`}>Consulta rápida</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">Emenda vinculada</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{planCode}</p>
              </div>
              <button
                type="button"
                onClick={() => setEmendaPanelOpen(false)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Fechar
              </button>
            </div>
            <dl className="flex-1 space-y-4 overflow-y-auto px-6 py-5 text-sm">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Código</dt>
                <dd className="font-semibold text-slate-800 dark:text-slate-100">{planCode}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Valor</dt>
                <dd className="font-semibold text-blue-700 dark:text-blue-300">
                  {planEmendaValor > 0 ? currencyFmt.format(planEmendaValor) : '—'}
                </dd>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Destinação original', value: plan?.emendaDestinacao },
                  { label: 'Exercício', value: plan?.emendaExercicio },
                  { label: 'Apresentada em', value: plan?.emendaData ? new Date(`${plan.emendaData}T00:00:00`).toLocaleDateString('pt-BR') : null },
                  { label: 'Esfera', value: plan?.emendaEsfera },
                  { label: 'Parlamentar', value: plan?.emendaParlamentar },
                  { label: 'Secretaria de destino', value: plan?.emendaSecretariaDestino },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</dt>
                    <dd className={value ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400'}>{value || 'Não informado'}</dd>
                  </div>
                ))}
              </div>
              {[
                { label: 'Texto original da emenda', value: plan?.emendaDescricao },
                { label: 'Objeto detalhado', value: plan?.emendaObjetoDetalhado },
                { label: 'Justificativa', value: plan?.emendaJustificativa },
              ].map(({ label, value }) => (
                <div key={label}>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</dt>
                  <dd className={`whitespace-pre-line ${value ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400'}`}>
                    {value || 'Não informado'}
                  </dd>
                </div>
              ))}
            </dl>
          </aside>
        </div>
      )}

      {showJuntadaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Juntada de Documento</h2>
            <p className="text-sm text-gray-600">
              Junte um documento ao processo com justificativa. O registro aparecerá na timeline do plano.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etapa do processo <span className="text-red-500">*</span>
              </label>
              <select
                value={juntadaEtapa}
                disabled={juntadaFaseLocked}
                onChange={(e) => {
                  const etapa = e.target.value;
                  setJuntadaEtapa(etapa);
                  const tipoMap: Record<string, string> = {
                    CONVENIOS_FORMALIZACAO: 'TERMO_FOMENTO',
                    CONVENIOS_MINUTA: 'MINUTA_TERMO_FOMENTO_ASSINADA',
                    PROCURADORIA_JURIDICA: 'PARECER_TECNICO',
                    GESTOR_PARCERIA: 'PARECER_TECNICO',
                    GESTOR_JURIDICO_REVISAO: 'PARECER_TECNICO',
                    COMISSAO_SELECAO: 'RESOLUCAO',
                  };
                  if (tipoMap[etapa]) setJuntadaTipo(tipoMap[etapa]);
                }}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-violet-500 focus:ring-violet-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">Selecione a etapa...</option>
                {workflowTimeline.map(step => (
                  <option key={step.key} value={step.key}>{step.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de documento <span className="text-red-500">*</span>
              </label>
              <select
                value={juntadaTipo}
                disabled={juntadaFaseLocked && !!juntadaTipo}
                onChange={(e) => setJuntadaTipo(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-violet-500 focus:ring-violet-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">Selecione o tipo...</option>
                <option value="OFICIO">Ofício</option>
                <option value="PARECER_TECNICO">Parecer técnico</option>
                <option value="PARECER_JURIDICO">Parecer jurídico</option>
                <option value="PARECER_GESTOR_PARCERIA">Parecer do Gestor da Parceria</option>
                <option value="PARECER_ANALISE_CONVENIOS">Parecer de Análise - Departamento de Convênios</option>
                <option value="RESOLUCAO">Resolução</option>
                <option value="ATA_COMISSAO">Ata da comissão</option>
                <option value="TERMO_ADITIVO">Termo aditivo</option>
                <option value="PLANO_TRABALHO_ATUALIZADO">Plano de trabalho atualizado</option>
                <option value="PLANO_TRABALHO_ASSINADO_PREFEITO">Plano de trabalho assinado pelo Prefeito</option>
                <option value="MINUTA_TERMO_FOMENTO_ASSINADA">Minuta do termo de fomento</option>
                <option value="TERMO_FOMENTO">Termo de fomento assinado</option>
                <option value="COMPROVANTE_CONTA_BANCARIA">Comprovante de conta bancária</option>
                <option value="RELATORIO">Relatório</option>
                <option value="DOCUMENTO_COMPLEMENTAR">Documento complementar</option>
                <option value="OUTROS">Outros</option>
              </select>
            </div>
            {juntadaAnexoExistente && (
              <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 dark:border-violet-800 dark:bg-violet-950/30">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">Arquivo atual</p>
                <button
                  type="button"
                  onClick={() => setAnexoViewer(juntadaAnexoExistente)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:underline dark:text-violet-300"
                >
                  <PaperClipIcon className="h-4 w-4 shrink-0" />
                  <span className="truncate max-w-[22rem]">{juntadaAnexoExistente.nomeOriginal}</span>
                </button>
                {(juntadaAnexoExistente.uploaderNome || juntadaAnexoExistente.usuarioUpload) && (
                  <p className="mt-0.5 text-xs text-violet-500">
                    por {juntadaAnexoExistente.uploaderNome || juntadaAnexoExistente.usuarioUpload}
                    {juntadaAnexoExistente.dataUpload && <> · {new Date(juntadaAnexoExistente.dataUpload).toLocaleDateString('pt-BR')}</>}
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {juntadaAnexoExistente ? 'Substituir arquivo' : 'Arquivo'} <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                onChange={(e) => setJuntadaFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-violet-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-violet-700 hover:file:bg-violet-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Justificativa <span className="text-red-500">*</span>
              </label>
              <textarea
                value={juntadaJustificativa}
                onChange={(e) => setJuntadaJustificativa(toUpperText(e.target.value))}
                rows={3}
                placeholder="Descreva o motivo da juntada deste documento..."
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-violet-500 focus:ring-violet-500 sm:text-sm placeholder:text-gray-400"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setShowJuntadaModal(false); setJuntadaTipo(''); setJuntadaEtapa(''); setJuntadaFile(null); setJuntadaJustificativa(''); setJuntadaFaseLocked(false); setJuntadaAnexoExistente(null); }}
                disabled={enviandoJuntada}
                className="px-4 py-2 rounded border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleJuntarDocumento}
                disabled={enviandoJuntada || !juntadaTipo || !juntadaEtapa || !juntadaFile || !juntadaJustificativa.trim()}
                className="px-4 py-2 rounded bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enviandoJuntada ? 'Adicionando…' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {parecerViewerUrl && (
        <FileViewerModal
          url={parecerViewerUrl}
          nome={parecerAnexoInfo?.nomeOriginal || 'Parecer jurídico'}
          mimeType={parecerAnexoInfo?.mimeType}
          subtitle="Parecer jurídico da PGM"
          onClose={fecharParecerViewer}
        />
      )}

      {anexoViewer?.viewUrl && (
        <FileViewerModal
          url={`/api${anexoViewer.viewUrl}`}
          nome={anexoViewer.nomeOriginal}
          mimeType={anexoViewer.mimeType}
          subtitle="Documento do plano de trabalho"
          onClose={() => setAnexoViewer(null)}
        />
      )}

      {/* Modal de diligência por plano de trabalho */}
      {diligenciaPlanoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40" onClick={fecharDiligenciaPlano} />
          <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Diligência</p>
                <h2 className="text-base font-bold text-slate-900">{plan?.titulo || 'Plano de trabalho'}</h2>
              </div>
              <button type="button" onClick={fecharDiligenciaPlano} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {diligenciaPlanoEnviado ? (
              <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                  <CheckIcon className="h-6 w-6 text-emerald-600" />
                </div>
                <p className="text-sm font-semibold text-slate-900">Diligência enviada com sucesso</p>
                <p className="text-xs text-slate-500">A instituição foi notificada por e-mail.</p>
                <button type="button" onClick={fecharDiligenciaPlano} className="mt-2 rounded-xl bg-slate-900 px-5 py-2 text-sm font-medium text-white">
                  Fechar
                </button>
              </div>
            ) : (
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Mensagem para a instituição
                  </label>
                  <textarea
                    rows={4}
                    value={diligenciaPlanoMensagem}
                    onChange={e => setDiligenciaPlanoMensagem(toUpperText(e.target.value))}
                    placeholder="Descreva o que a instituição precisa providenciar em relação a este plano de trabalho..."
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-300"
                  />
                </div>
                {diligenciaPlanoErro && (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {diligenciaPlanoErro}
                  </p>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={fecharDiligenciaPlano} className="rounded-xl border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={enviarDiligenciaPlano}
                    disabled={!diligenciaPlanoMensagem.trim() || diligenciaPlanoLoading}
                    className="rounded-xl bg-amber-600 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    {diligenciaPlanoLoading ? 'Enviando...' : 'Enviar diligência'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanoFullPage;
